/**
 * Shell stylesheet (Task 14.3, Req 21.3, 21.5, 21.6) — AVOS Brand Guidelines v1.0.
 *
 * A framework-free CSS string derived from {@link COLOR_TOKENS}, injected into
 * the document `<head>` by `renderAppDocument`. It renders the AVOS identity as
 * a shell — emerald chrome, ivory canvas, white cards on linen wells, one gold
 * element — and establishes the machine-checkable accessibility + responsive
 * scaffolding:
 *
 *   - Req 21.3 (responsive @ 360px, no horizontal scroll): `box-sizing:
 *     border-box` everywhere, `max-width: 100%` on media, wrapping flex.
 *   - Req 21.5 (contrast): every foreground/background TEXT pairing comes from
 *     the documented palette whose pairs clear WCAG AA (see `tokens.ts`).
 *   - Req 21.6 (keyboard operability, visible focus, no focus trap): a
 *     `:focus-visible` outline on every control, and a skip-link visible on
 *     focus. No focus-trapping patterns.
 *
 * TYPOGRAPHY (SaaS v2): ONE font family — Proxima Nova (local, licensed),
 * falling back to Inter / system UI. Weights 400/500/600/700. Tabular figures
 * on tables/ledgers/figure columns. No serif display face, no monospace.
 *
 * THE GOLD RULE (brand §07): gold is reserved for the single highest-priority
 * element on a view. It is applied through the `.is-gold` / `--gold` accent
 * treatment, never as general chrome.
 */
import { COLOR_TOKENS } from "./tokens.js";

/** CSS custom-property block mirroring the design tokens. */
function tokenVariables(): string {
  return [
    // Generic role tokens (retained names, mapped to brand hues).
    `--color-surface: ${COLOR_TOKENS.surface};`,
    `--color-surface-muted: ${COLOR_TOKENS.surfaceMuted};`,
    `--color-text: ${COLOR_TOKENS.text};`,
    `--color-text-muted: ${COLOR_TOKENS.textMuted};`,
    `--color-primary: ${COLOR_TOKENS.primary};`,
    `--color-primary-text: ${COLOR_TOKENS.primaryText};`,
    `--color-danger: ${COLOR_TOKENS.danger};`,
    `--color-border: ${COLOR_TOKENS.border};`,
    `--color-focus: ${COLOR_TOKENS.focus};`,
    // Brand hues (explicit).
    `--color-emerald: ${COLOR_TOKENS.deepEmerald};`,
    `--color-forest: ${COLOR_TOKENS.forest};`,
    `--color-gold: ${COLOR_TOKENS.gold};`,
    `--color-ivory: ${COLOR_TOKENS.ivory};`,
    `--color-sage: ${COLOR_TOKENS.sage};`,
    `--color-ink: ${COLOR_TOKENS.ink};`,
    `--color-slate: ${COLOR_TOKENS.slate};`,
    `--color-linen: ${COLOR_TOKENS.linen};`,
    `--color-rule: ${COLOR_TOKENS.rule};`,
    `--color-clay: ${COLOR_TOKENS.clay};`,
    `--color-on-emerald: ${COLOR_TOKENS.onEmerald};`,
    `--color-on-emerald-muted: ${COLOR_TOKENS.onEmeraldMuted};`,
    // Card surface (white) + the SaaS neutral/blue scale and semantic tokens.
    // These are ADDITIVE — existing `--color-*` role vars above already carry
    // the remapped SaaS values; these give components the full ramp + chips.
    `--color-card: #FFFFFF;`,
    `--color-page-bg: #F6F7F9;`,
    `--color-surface-subtle: #F8F9FB;`,
    `--color-surface-sidebar: #F8F9FB;`,
    `--color-surface-hover: #F6F7F9;`,
    `--color-surface-selected: #F3F4FF;`,
    `--color-text-primary: #101828;`,
    `--color-text-secondary: #344054;`,
    `--color-border-strong: #D0D5DD;`,
    // Primary blue ramp.
    `--color-primary-50: #F3F4FF;`,
    `--color-primary-100: #E7E9FF;`,
    `--color-primary-200: #CDD3FF;`,
    `--color-primary-300: #A7B2F5;`,
    `--color-primary-400: #7183DD;`,
    `--color-primary-500: #4056C5;`,
    `--color-primary-600: #1D35A9;`,
    `--color-primary-700: #172B8A;`,
    `--color-primary-800: #13246F;`,
    `--color-primary-900: #101E5C;`,
    `--color-primary-action: #1D35A9;`,
    `--color-primary-hover: #172B8A;`,
    `--color-primary-pressed: #13246F;`,
    `--color-primary-soft: #F3F4FF;`,
    `--color-primary-border: #CDD3FF;`,
    // Gray ramp.
    `--color-gray-25: #FCFCFD;`,
    `--color-gray-50: #F9FAFB;`,
    `--color-gray-100: #F2F4F7;`,
    `--color-gray-200: #EAECF0;`,
    `--color-gray-300: #D0D5DD;`,
    `--color-gray-400: #98A2B3;`,
    `--color-gray-500: #667085;`,
    `--color-gray-600: #475467;`,
    `--color-gray-700: #344054;`,
    `--color-gray-800: #1D2939;`,
    `--color-gray-900: #101828;`,
    // Semantic status (chip bg / text / border triplets).
    `--color-info-bg: #F3F4FF;`, `--color-info-text: #172B8A;`, `--color-info-border: #CDD3FF;`,
    `--color-primary-selected: #EEF0FF;`,
    `--color-success-bg: #ECFDF3;`, `--color-success-text: #027A48;`, `--color-success-border: #ABEFC6;`,
    `--color-warning-bg: #FFFAEB;`, `--color-warning-text: #B54708;`, `--color-warning-border: #FEDF89;`,
    `--color-error-bg: #FEF3F2;`, `--color-error-text: #B42318;`, `--color-error-border: #FECDCA;`,
    `--color-neutral-bg: #F2F4F7;`, `--color-neutral-text: #475467;`, `--color-neutral-border: #EAECF0;`,
    // Focus ring (blue, 3px soft).
    `--focus-ring: 0 0 0 3px rgba(29, 53, 169, 0.16);`,
    `--placeholder: #98A2B3;`,
  ].join("\n    ");
}

/** Base scaffolding: tokens, typography, focus, skip-link, auth-shell header. */
const BASE_CSS = `
  /* SaaS theme v2: ONE font family across the product — Proxima Nova (licensed
     .ttf assets served locally from /fonts), falling back to Inter / the system
     UI stack. No Google Fonts request, no serif display face. */
  @font-face {
    font-family: "Proxima Nova"; font-style: normal; font-weight: 400;
    font-display: swap; src: url("/fonts/ProximaNova-Regular.ttf") format("truetype");
  }
  @font-face {
    font-family: "Proxima Nova"; font-style: normal; font-weight: 500;
    font-display: swap; src: url("/fonts/ProximaNova-Semibold.ttf") format("truetype");
  }
  @font-face {
    font-family: "Proxima Nova"; font-style: normal; font-weight: 600;
    font-display: swap; src: url("/fonts/ProximaNova-Semibold.ttf") format("truetype");
  }
  @font-face {
    font-family: "Proxima Nova"; font-style: normal; font-weight: 700;
    font-display: swap; src: url("/fonts/ProximaNova-Bold.ttf") format("truetype");
  }
  @font-face {
    font-family: "Proxima Nova"; font-style: normal; font-weight: 300;
    font-display: swap; src: url("/fonts/ProximaNova-Light.ttf") format("truetype");
  }

  :root {
    ${tokenVariables()}
    --focus-ring-width: 3px;
    --layout-max-width: 78rem;
    /* Border-radius system (SaaS v2). */
    --radius: 12px;      /* cards */
    --radius-sm: 8px;    /* inputs, buttons, menus */
    --radius-xs: 6px;    /* nav items, small controls */
    /* ONE font family across the whole product. */
    --font-body: "Proxima Nova", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    --font-display: var(--font-body);
    /* Restrained SaaS shadow scale. */
    --shadow-xs: 0 1px 2px rgba(16, 24, 40, 0.04);
    --shadow-sm: 0 1px 2px rgba(16, 24, 40, 0.06);
    --shadow-md: 0 4px 12px rgba(16, 24, 40, 0.08);
    --shadow-lg: 0 20px 40px rgba(16, 24, 40, 0.12);
    --shadow-card: var(--shadow-xs);
    --sidebar-width: 16.5rem;
  }

  /* Req 21.3: predictable box model so padding/borders never overflow the
     viewport and produce horizontal scroll at the 360px reference width. */
  *, *::before, *::after { box-sizing: border-box; }

  html { -webkit-text-size-adjust: 100%; }

  /* Establish the height chain so viewport-sized shells have a stable base. */
  html, body { height: 100%; }

  body {
    margin: 0;
    /* Req 21.3: never allow content to force a horizontal scrollbar. */
    overflow-x: hidden;
    background: var(--color-surface);
    color: var(--color-text);
    font-family: var(--font-body);
    font-size: 14px;
    line-height: 1.5;
    color: var(--color-text-primary);
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }

  /* Req 21.3: replaced/media elements stay within their container width. */
  img, svg, video, canvas, iframe { max-width: 100%; height: auto; }

  /* Req 21.3: long words/URLs wrap instead of overflowing at 360px. */
  p, li, a, h1, h2, h3, span, label, button {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  /* Display voice: screen titles + headline figures (same family, weight 600). */
  .avos-display, .console-page-title, .recon-headline {
    font-family: var(--font-display);
    font-weight: 600;
    font-style: normal;
    letter-spacing: -0.01em;
    color: var(--color-text-primary);
    line-height: 1.2;
  }

  /* Tracked micro-labels (§06): the Midhurst device that replaces monospace —
     Medium weight, tracked capitals. */
  .avos-eyebrow {
    font-family: var(--font-body);
    font-weight: 500;
    font-size: 0.72rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  /* Tabular figures everywhere numbers must align (§06). */
  .avos-tnum, .console-table td, .recon-num, .booking-num,
  .console-card__value, .recon-banner__value { font-variant-numeric: tabular-nums; }

  /* Fluid layout container (auth shell). */
  [data-app-shell="avos"] {
    width: 100%;
    max-width: 100%;
    min-height: 100vh;
    min-height: 100dvh;
    margin: 0 auto;
    padding: 0;
  }

  [data-app-shell="avos"] > header[role="banner"],
  [data-app-shell="avos"] > main {
    width: 100%;
    max-width: var(--layout-max-width);
    margin-inline: auto;
    padding: 0.9rem 1.25rem;
  }

  header[role="banner"] {
    background: var(--color-card);
    border-bottom: 1px solid var(--color-border);
    color: var(--color-text-primary);
    display: flex;
    /* Req 21.3: header contents wrap on narrow viewports. */
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 1rem;
  }

  .brand {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-family: var(--font-display);
    font-weight: 600;
    letter-spacing: 0.01em;
    color: var(--color-text-primary);
  }
  .brand__name { font-family: var(--font-display); font-weight: 600; letter-spacing: 0.01em; }

  /* Req 21.6: a single, consistent, visible keyboard focus indicator on every
     interactive control. Uses :focus-visible so it shows for keyboard users
     without adding a persistent ring for pointer clicks. */
  a:focus-visible,
  button:focus-visible,
  input:focus-visible,
  select:focus-visible,
  textarea:focus-visible,
  [tabindex]:focus-visible {
    outline: 2px solid var(--color-focus);
    outline-offset: 2px;
    border-radius: 3px;
  }

  /* Req 21.6: the skip-link is the first focusable control; hidden off-screen
     until focused, then visible. Does NOT trap focus. */
  .skip-link {
    position: absolute;
    left: -9999px;
    top: 0;
    background: var(--color-primary-action);
    color: var(--color-primary-text);
    padding: 0.5rem 0.75rem;
    z-index: 1000;
    text-decoration: none;
  }
  .skip-link:focus {
    left: 0.5rem;
    top: 0.5rem;
  }

  /* The main region receives focus after the skip-link; never traps focus. */
  main:focus { outline: none; }

  [role="alert"] {
    color: var(--color-danger);
    border: 1px solid var(--color-danger);
    background: var(--color-card);
    padding: 0.5rem 0.75rem;
    border-radius: var(--radius-sm);
  }

  @media (prefers-reduced-motion: reduce) {
    * { animation: none !important; transition: none !important; }
  }
`;

/** Operator console shell: emerald chrome top bar + full-height collapsible
 *  sidebar, ivory canvas, white cards. */
const CONSOLE_SHELL_CSS = `
  .console-sr-only {
    position: absolute;
    width: 1px; height: 1px;
    padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0 0 0 0);
    white-space: nowrap; border: 0;
  }

  :root { --topbar-height: 3.75rem; --sidebar-rail: 4.25rem; }

  /* Full-height layout: the shell is a horizontal row of [sidebar | column],
     each 100vh. The sidebar spans the entire window height; the column holds
     the top bar + the scrollable main. */
  [data-app-shell="avos-console"] {
    width: 100%;
    max-width: 100%;
    height: 100vh;
    height: 100dvh;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    background: var(--color-surface);
    color: var(--color-text);
    overflow: hidden;
  }

  /* The right-hand column (top bar + main). */
  .console-column {
    flex: 1 1 auto;
    min-width: 0;
    height: 100vh;
    height: 100dvh;
    display: flex;
    flex-direction: column;
  }

  /* ---- Top bar (white SaaS chrome, spans the column) ------------------- */
  .console-topbar {
    flex: 0 0 var(--topbar-height);
    height: var(--topbar-height);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0 1rem;
    background: var(--color-card);
    color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border);
    z-index: 30;
  }
  .console-topbar__left { display: flex; align-items: center; gap: 0.6rem; min-width: 0; }
  .console-topbar__right { display: flex; align-items: center; gap: 0.4rem; }

  /* Icon buttons in the top bar (toggle, bell). */
  .console-iconbtn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.35rem;
    height: 2.35rem;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--color-gray-500);
    border: 1px solid transparent;
    cursor: pointer;
    position: relative;
    transition: background 0.12s ease, color 0.12s ease;
  }
  .console-iconbtn:hover { background: var(--color-gray-100); color: var(--color-gray-700); }
  .console-iconbtn:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
  .console-iconbtn__badge {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 999px;
    background: var(--color-primary-action);
    border: 1.5px solid var(--color-card);
  }

  /* Profile trigger button. */
  .console-profilebtn {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.3rem 0.55rem 0.3rem 0.35rem;
    border-radius: 999px;
    background: transparent;
    border: 1px solid var(--color-border);
    color: var(--color-text-primary);
    cursor: pointer;
    transition: background 0.12s ease;
  }
  .console-profilebtn:hover { background: var(--color-surface-subtle); }
  .console-profilebtn:focus-visible { outline: none; box-shadow: var(--focus-ring); }
  .console-profilebtn__meta { display: flex; flex-direction: column; line-height: 1.15; text-align: left; }
  .console-profilebtn__name { font-size: 0.82rem; font-weight: 600; color: var(--color-gray-700); }
  .console-profilebtn__role { font-size: 0.68rem; color: var(--color-gray-500); }
  .console-profilebtn__chevron { display: inline-flex; color: var(--color-gray-500); }

  /* Avatar with initials (soft primary). */
  .console-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.9rem;
    height: 1.9rem;
    border-radius: 999px;
    background: var(--color-primary-soft);
    color: var(--color-primary-700);
    border: 1px solid var(--color-primary-border);
    font-family: var(--font-body);
    font-weight: 700;
    font-size: 0.72rem;
    letter-spacing: 0.02em;
    flex: 0 0 auto;
  }
  .console-avatar--lg { width: 2.6rem; height: 2.6rem; font-size: 0.9rem; }

  /* ---- Dropdowns (notifications, profile) ------------------------------ */
  .console-dropdown { position: relative; }
  .console-dropdown__panel {
    position: absolute;
    top: calc(100% + 0.55rem);
    right: 0;
    z-index: 60;
    min-width: 15rem;
    background: var(--color-card);
    color: var(--color-text);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    box-shadow: var(--shadow-md);
    overflow: hidden;
    animation: console-pop 0.12s ease;
  }
  @keyframes console-pop {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .console-menu--notif { width: 21rem; max-width: calc(100vw - 1.5rem); }
  .console-menu--profile { width: 16rem; }
  .console-menu__header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.75rem 0.9rem; border-bottom: 1px solid var(--color-rule);
  }
  .console-menu__title { font-weight: 600; color: var(--color-text-primary); font-size: 0.9rem; }
  .console-menu__count {
    font-size: 0.7rem; font-weight: 700; color: var(--color-primary-700);
    background: var(--color-primary-soft); border: 1px solid var(--color-primary-border);
    border-radius: 999px; padding: 0.05rem 0.5rem;
  }
  .console-notif__list { list-style: none; margin: 0; padding: 0.25rem; max-height: 20rem; overflow-y: auto; }
  .console-notif {
    display: flex; gap: 0.6rem; padding: 0.6rem 0.65rem; border-radius: var(--radius-sm);
  }
  .console-notif:hover { background: var(--color-surface-subtle); }
  .console-notif__dot {
    flex: 0 0 auto; width: 0.55rem; height: 0.55rem; border-radius: 999px; margin-top: 0.35rem;
  }
  .console-notif__dot--attention { background: var(--color-warning-text); }
  .console-notif__dot--ok { background: var(--color-success-text); }
  .console-notif__dot--critical { background: var(--color-error-text); }
  .console-notif__dot--pending { background: var(--color-gray-400); }
  .console-notif__body { display: flex; flex-direction: column; gap: 0.12rem; min-width: 0; }
  .console-notif__title { font-weight: 600; font-size: 0.85rem; color: var(--color-text); }
  .console-notif__detail { font-size: 0.8rem; color: var(--color-text-muted); }
  .console-notif__when { font-size: 0.7rem; color: var(--color-gray-500); margin-top: 0.1rem; }
  /* A full-row clickable notification (deep-links to the booking drawer).
     Resets the button chrome so it reads exactly like the former static row. */
  .console-notif__link {
    display: flex; gap: 0.6rem; width: 100%; text-align: left;
    background: none; border: none; padding: 0; font: inherit; color: inherit; cursor: pointer;
  }
  .console-notif--read { opacity: 0.6; }
  .console-notif__empty {
    margin: 0; padding: 1.1rem 0.9rem; text-align: center;
    font-size: 0.83rem; color: var(--color-text-muted);
  }
  .console-menu__footer-action {
    display: block; width: 100%; text-align: center; padding: 0.65rem;
    background: var(--color-surface-subtle); border: none; border-top: 1px solid var(--color-border);
    color: var(--color-primary-700); font: inherit; font-weight: 600; font-size: 0.82rem; cursor: pointer;
  }
  .console-menu__footer-action:hover { background: var(--color-gray-100); }

  .console-menu__profile { display: flex; gap: 0.7rem; padding: 0.9rem; align-items: center; }
  .console-menu__profile-meta { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
  .console-menu__profile-name { font-weight: 600; color: var(--color-text); font-size: 0.9rem; }
  .console-menu__profile-email { font-size: 0.78rem; color: var(--color-text-muted); overflow-wrap: anywhere; }
  .console-menu__profile-role { font-size: 0.72rem; color: var(--color-gray-500); margin-top: 0.15rem; }
  .console-menu__sep { height: 1px; background: var(--color-border); margin: 0.25rem 0; }
  .console-menu__item {
    display: flex; align-items: center; gap: 0.55rem; width: 100%;
    padding: 0.6rem 0.9rem; background: transparent; border: none; text-align: left;
    color: var(--color-text); text-decoration: none; font: inherit; font-size: 0.86rem; cursor: pointer;
  }
  .console-menu__item:hover { background: var(--color-surface-subtle); }
  .console-menu__item--active { background: var(--color-primary-soft); color: var(--color-primary-700); }
  .console-menu__item-icon { display: inline-flex; color: var(--color-gray-500); }
  .console-menu__item--danger { color: #D92D20; }
  .console-menu__item--danger:hover { background: var(--color-error-bg); }
  .console-menu__item--danger .console-menu__item-icon { color: #D92D20; }
  /* The Sign out control is a real <button> styled identically to the menu
     anchors (full-width, left-aligned). */
  .console-menu__item--button {
    width: 100%;
    background: transparent;
    border: none;
    font: inherit;
    cursor: pointer;
  }
  .console-menu__item--button:disabled { opacity: 0.6; cursor: default; }
  .console-menu__item--button[aria-busy="true"] { cursor: progress; }
  .console-menu__signout-error {
    margin: 0.1rem 0.9rem 0.4rem;
    color: #D92D20;
    font-size: 0.78rem;
  }

  /* ---- Sidebar (white SaaS chrome, icons + labels, FULL WINDOW HEIGHT) ---- */
  .console-sidebar {
    flex: 0 0 var(--sidebar-width);
    width: var(--sidebar-width);
    height: 100vh;
    height: 100dvh;
    background: var(--color-surface-sidebar);
    color: var(--color-gray-600);
    padding: 0 0.7rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    overflow-y: auto;
    overflow-x: hidden;
    border-right: 1px solid var(--color-border);
    transition: width 0.16s ease;
    z-index: 30;
  }

  /* Brand lockup at the top of the sidebar (aligned to the top-bar height). */
  .console-sidebar__brand {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    height: var(--topbar-height);
    min-height: var(--topbar-height);
    padding: 0 0.4rem;
    margin-bottom: 0.5rem;
    text-decoration: none;
    border-bottom: 1px solid var(--color-border);
  }
  .console-sidebar__brand-word { display: flex; flex-direction: column; line-height: 1; }
  .console-sidebar__brand-name {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.15rem;
    letter-spacing: 0.2em;
    color: var(--color-text-primary);
  }
  .console-sidebar__brand-endorse {
    font-family: var(--font-body);
    font-weight: 500;
    font-size: 0.46rem;
    letter-spacing: 0.13em;
    text-transform: uppercase;
    color: var(--color-gray-500);
    margin-top: 0.28rem;
    white-space: nowrap;
  }

  .console-sidebar__list { list-style: none; margin: 0; padding: 0.35rem 0 0; display: flex; flex-direction: column; gap: 0.05rem; }
  /* Grouped IA: each group is a standalone item (Dashboard) or a labelled
     cluster. Sub-lists reset list styling; groups add a little breathing room. */
  .console-sidebar__group { list-style: none; }
  .console-sidebar__sublist { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.05rem; }
  /* Muted, non-clickable, uppercase section label with extra top spacing so the
     eye can scan the sidebar by workflow. */
  .console-sidebar__section {
    margin: 0.9rem 0 0.15rem;
    padding: 0 0.75rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-gray-400);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    user-select: none;
    pointer-events: none;
  }
  .console-sidebar__link {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    height: 2.5rem;
    padding: 0 0.75rem;
    color: var(--color-gray-600);
    text-decoration: none;
    border-radius: var(--radius-xs);
    font-size: 0.9rem;
    font-weight: 500;
    white-space: nowrap;
    position: relative;
    transition: color 0.12s ease, background 0.12s ease;
  }
  .console-sidebar__link:hover {
    text-decoration: none;
    color: var(--color-gray-700);
    background: #F1F3F5;
  }
  .console-sidebar__link:hover .console-sidebar__icon { color: var(--color-gray-700); }
  .console-sidebar__icon { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; width: 1.4rem; color: var(--color-gray-500); }
  .console-sidebar__label { overflow: hidden; text-overflow: ellipsis; }
  .console-sidebar__link--active {
    color: var(--color-forest);
    font-weight: 600;
    background: #F1F2FF;
  }
  .console-sidebar__link--active::before {
    content: "";
    position: absolute;
    left: 0; top: 0.35rem; bottom: 0.35rem;
    width: 2px; border-radius: 0 2px 2px 0;
    background: var(--color-primary-action);
  }
  .console-sidebar__link--active .console-sidebar__icon { color: var(--color-primary-action); }

  /* Collapsed icon-rail (desktop): hide labels + wordmark, centre everything. */
  .console-shell--collapsed .console-sidebar { width: var(--sidebar-rail); flex-basis: var(--sidebar-rail); }
  .console-shell--collapsed .console-sidebar__link { justify-content: center; padding: 0.6rem; gap: 0; }
  .console-shell--collapsed .console-sidebar__label { display: none; }
  /* In the icon rail the section labels would be noise: hide them but keep a
     small gap between groups so the clusters still read as distinct. */
  .console-shell--collapsed .console-sidebar__section { display: none; }
  .console-shell--collapsed .console-sidebar__group + .console-sidebar__group { margin-top: 0.4rem; }
  .console-shell--collapsed .console-sidebar__brand { justify-content: center; padding: 0; gap: 0; }
  .console-shell--collapsed .console-sidebar__brand-word { display: none; }

  /* Scrim behind the mobile drawer (covers the whole window under the drawer). */
  .console-scrim {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(16, 24, 40, 0.35);
    border: none;
    z-index: 45;
  }

  /* ---- Main canvas (scrolls independently, fills the column) ----------- */
  .console-main {
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
    overflow-y: auto;
    background: var(--color-card);
  }
  .console-main__inner {
    /* Fill the full width of the main column; only a fixed 42px left/right
       gutter (no centered max-width cap). */
    max-width: none;
    margin: 0;
    padding: 1.75rem 42px 3rem;
  }
  .console-page-eyebrow {
    font-family: var(--font-body);
    font-weight: 500;
    font-size: 0.72rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--color-text-muted);
    margin: 0 0 0.5rem;
  }
  .console-page-title {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: clamp(1.7rem, 3vw, 2.4rem);
    color: #171B26;
    margin: 0;
    line-height: 1.1;
  }
  .console-lede { color: var(--color-text-muted); margin-top: 0.5rem; max-width: 46rem; }

  /* ---- Metric cards ---------------------------------------------------- */
  .console-card-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
    gap: 1rem;
    margin-top: 1.5rem;
  }
  .console-card {
    border: 1px solid var(--color-border);
    border-radius: 10px;
    padding: 1.1rem 1.2rem;
    background: var(--color-card);
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
  }
  /* A "well": the subtle recessed surface some cards sit in. */
  .console-card--muted { background: var(--color-surface-subtle); box-shadow: none; }
  .console-card__label {
    margin: 0;
    color: var(--color-text-muted);
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }
  .console-card__value {
    margin: 0.55rem 0 0;
    font-family: var(--font-body);
    font-weight: 700;
    font-size: 2rem;
    color: var(--color-forest);
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }
  .console-card__hint { margin: 0.4rem 0 0; color: var(--color-text-muted); font-size: 0.8rem; }
  /* Emphasis KPI card: a quiet left accent only — NOT a full blue outline,
     and the value stays near-black like every other KPI (blue is interaction,
     not an information-hierarchy colour). */
  /* Emphasis KPI card: equal neutral treatment (no indigo accent). */
  .console-card--gold {
    border-color: var(--color-border);
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  }
  .console-card--gold .console-card__label { color: var(--color-text-muted); }
  .console-card--gold .console-card__value { color: var(--color-forest); }

  /* ---- Panels ---------------------------------------------------------- */
  .console-panel {
    margin-top: 1.75rem;
    border: 1px solid var(--color-rule);
    border-radius: var(--radius);
    padding: 1.35rem 1.5rem 1.5rem;
    background: var(--color-card);
    box-shadow: var(--shadow-card);
  }
  .console-panel__title {
    margin: 0 0 0.35rem;
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.3rem;
    color: var(--color-forest);
  }
  .console-subsection { margin-top: 1.35rem; }
  .console-drilldown {
    margin-top: 1.1rem;
    padding-top: 1.1rem;
    border-top: 1px solid var(--color-rule);
  }
  .console-loading, .console-empty {
    color: var(--color-text-muted);
    padding: 0.75rem 0;
  }

  /* ---- Tables (tabular figures; hairline rules) ------------------------ */
  .console-table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.75rem 0;
    font-variant-numeric: tabular-nums;
  }
  .console-table th, .console-table td {
    text-align: left;
    padding: 0.65rem 0.7rem;
    border-bottom: 1px solid var(--color-rule);
    vertical-align: top;
  }
  .console-table thead th {
    background: var(--color-surface-subtle);
    color: var(--color-gray-500);
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    border-bottom: 1px solid var(--color-border);
  }
  .console-table td { color: var(--color-text-primary); font-size: 0.9rem; }
  .console-table tbody tr:last-child td { border-bottom: none; }
  .console-table tbody tr:hover td { background: var(--color-surface-subtle); }
  .console-table tr[aria-selected="true"] { background: var(--color-primary-soft); }

  .console-badge {
    display: inline-block;
    padding: 0.12rem 0.5rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    border: 1px solid var(--color-rule);
    color: var(--color-text);
    background: var(--color-card);
  }
  .console-badge--ok { border-color: var(--color-success-border); color: var(--color-success-text); background: var(--color-success-bg); }
  .console-badge--off { border-color: var(--color-error-border); color: var(--color-error-text); background: var(--color-error-bg); }

  /* ---- Forms ----------------------------------------------------------- */
  .console-form {
    margin-top: 1.25rem;
    padding: 1.1rem 1.2rem;
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    background: var(--color-surface-subtle);
  }
  .console-form__title {
    margin: 0 0 0.75rem;
    color: var(--color-text-primary);
    font-size: 0.95rem;
    font-weight: 600;
  }
  .console-form__row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.85rem;
    margin-bottom: 0.75rem;
  }
  .console-field {
    display: flex;
    flex-direction: column;
    flex: 1 1 12rem;
    min-width: 0;
    gap: 0.3rem;
  }
  .console-field label {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--color-text-muted);
  }
  .console-field input, .console-field select, .console-field textarea {
    padding: 0.5rem 0.6rem;
    min-height: 2.5rem;
    border: 1px solid var(--color-border-strong);
    border-radius: var(--radius-sm);
    background: var(--color-card);
    color: var(--color-text-primary);
    font: inherit;
    font-size: 0.9rem;
  }
  .console-field textarea { min-height: 4rem; }
  .console-field input::placeholder, .console-field textarea::placeholder { color: var(--placeholder); }
  .console-field input:hover:not(:disabled), .console-field select:hover:not(:disabled), .console-field textarea:hover:not(:disabled) {
    border-color: var(--color-gray-400);
  }
  .console-field input:focus, .console-field select:focus, .console-field textarea:focus {
    outline: none;
    border-color: var(--color-primary-action);
    box-shadow: var(--focus-ring);
  }
  .console-field input[readonly], .console-field textarea[readonly] {
    background: var(--color-surface-subtle);
  }
  /* ---- Pass A: form-primitive polish (shared, tasteful global) -------- */
  /* Replace the native select arrow with a consistent custom caret. */
  .console-field select {
    -webkit-appearance: none; -moz-appearance: none; appearance: none;
    padding-right: 2rem;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none' stroke='%23667085' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 4.5L6 7.5L9 4.5'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.7rem center;
  }
  /* Remove native number spinners that clash with the rest of the form. */
  .console-field input[type="number"] { -moz-appearance: textfield; appearance: textfield; }
  .console-field input[type="number"]::-webkit-inner-spin-button,
  .console-field input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none; margin: 0;
  }
  /* Deliberate disabled state (not browser-default grey). */
  .console-field input:disabled, .console-field select:disabled, .console-field textarea:disabled {
    background: var(--color-gray-100); color: var(--placeholder);
    border-color: var(--color-border);
    cursor: not-allowed; opacity: 1;
  }
  /* Validation red only on explicit error. */
  .console-field input[aria-invalid="true"],
  .console-field select[aria-invalid="true"],
  .console-field textarea[aria-invalid="true"] {
    border-color: #F04438; box-shadow: 0 0 0 3px rgba(240, 68, 56, 0.12);
  }
  /* Consistent accent for checkboxes/radios across the console forms. */
  .console-field input[type="checkbox"], .console-field input[type="radio"] {
    accent-color: var(--color-primary-action); width: 1.05rem; height: 1.05rem;
    min-height: 0; flex: 0 0 auto;
  }
  .console-field--checkbox { justify-content: flex-end; }
  .console-field--checkbox label {
    flex-direction: row;
    display: flex;
    align-items: center;
    gap: 0.45rem;
    font-weight: 500;
    color: var(--color-text);
  }

  /* Buttons: blue primary (§ primary actions), neutral secondary ghost. */
  .console-btn {
    background: var(--color-primary-action);
    color: #FFFFFF;
    border: 1px solid var(--color-primary-action);
    border-radius: var(--radius-sm);
    padding: 0.55rem 1.1rem;
    font: inherit;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease;
  }
  .console-btn:hover:not(:disabled) { background: var(--color-primary-hover); border-color: var(--color-primary-hover); }
  .console-btn:active:not(:disabled) { background: var(--color-primary-pressed); border-color: var(--color-primary-pressed); }
  .console-btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }
  .console-btn:disabled {
    background: var(--color-gray-300); border-color: var(--color-gray-300);
    color: #FFFFFF; cursor: not-allowed; opacity: 1;
  }
  /* Secondary (neutral outline) button. */
  .console-btn--ghost {
    background: #FFFFFF;
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border-strong);
    font-weight: 600;
  }
  .console-btn--ghost:hover:not(:disabled) { background: var(--color-gray-50); border-color: var(--color-gray-400); }
  .console-btn--ghost:active:not(:disabled) { background: var(--color-gray-100); }
  .console-btn--ghost:focus-visible { outline: none; box-shadow: var(--focus-ring); }
  /* True transparent ghost used by icon-style buttons. */
  .console-btn--plain {
    background: transparent;
    color: var(--color-gray-600);
    border: 1px solid transparent;
    font-weight: 600;
  }
  .console-btn--plain:hover:not(:disabled) { background: var(--color-gray-100); }
  /* Danger button. */
  .console-btn--danger {
    background: #D92D20;
    color: #FFFFFF;
    border: 1px solid #D92D20;
    font-weight: 600;
  }
  .console-btn--danger:hover:not(:disabled) { background: #B42318; border-color: #B42318; }
  .console-btn--danger:focus-visible { outline: none; box-shadow: var(--focus-ring); }

  /* ---- Shared status pill (semantic mapping §05) ----------------------- */
  /* sage=matched/healthy, gold=needs attention/variance, clay=unmatched/failed,
     slate=pending/in-progress. Every pill also carries a text label. */
  .avos-status {
    display: inline-block;
    padding: 0.12rem 0.55rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    border: 1px solid var(--color-info-border);
    color: var(--color-info-text);
    background: var(--color-info-bg);
  }
  .avos-status--ok { border-color: var(--color-success-border); color: var(--color-success-text); background: var(--color-success-bg); }
  .avos-status--attention { border-color: var(--color-warning-border); color: var(--color-warning-text); background: var(--color-warning-bg); }
  .avos-status--critical { border-color: var(--color-error-border); color: var(--color-error-text); background: var(--color-error-bg); }
  .avos-status--pending { border-color: var(--color-neutral-border); color: var(--color-neutral-text); background: var(--color-neutral-bg); }

  /* ---- Responsive: off-canvas drawer under 900px ----------------------- */
  @media (max-width: 900px) {
    /* On mobile the profile name/role text is noise; keep the avatar + the
       right-cluster icons. */
    .console-profilebtn__meta { display: none; }

    /* The sidebar becomes a full-height fixed drawer that slides in from the
       left, over the column. Desktop collapse never applies here. */
    .console-sidebar {
      position: fixed;
      inset: 0 auto 0 0;
      width: min(82vw, var(--sidebar-width)) !important;
      flex-basis: auto;
      transform: translateX(-105%);
      transition: transform 0.18s ease;
      z-index: 50;
      box-shadow: 4px 0 24px rgba(16, 24, 40, 0.18);
    }
    .console-sidebar--open { transform: translateX(0); }
    /* Even when the desktop "collapsed" flag is set, the mobile drawer shows
       full labels + wordmark. */
    .console-shell--collapsed .console-sidebar { width: min(82vw, var(--sidebar-width)) !important; }
    .console-shell--collapsed .console-sidebar__label { display: inline; }
    /* Restore the workflow section labels in the mobile drawer too. */
    .console-shell--collapsed .console-sidebar__section { display: block; }
    .console-shell--collapsed .console-sidebar__link { justify-content: flex-start; padding: 0.5rem 0.75rem; gap: 0.75rem; }
    .console-shell--collapsed .console-sidebar__brand { justify-content: flex-start; padding: 0 0.4rem; gap: 0.6rem; }
    .console-shell--collapsed .console-sidebar__brand-word { display: flex; }

    .console-scrim { display: block; }
    .console-main__inner { padding: 1.25rem 1.1rem 2.5rem; }
  }
`;

/** Module-specific CSS (recon, booking, channels, analytics, crm, agents),
 *  all on the brand semantic mapping: sage=healthy, gold=attention/variance,
 *  clay=critical/failed, slate=pending. Every state also carries a text label. */
const MODULES_CSS = `
  /* ===================== Reconciliation (M4/M5) ======================== */
  .recon-banner {
    margin-top: 1.5rem;
    border: 1px solid var(--color-rule);
    border-radius: var(--radius);
    background: var(--color-card);
    box-shadow: var(--shadow-card);
    padding: 1.35rem 1.5rem;
  }
  .recon-banner__title {
    margin: 0;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--color-text-muted);
  }
  .recon-banner__figures { display: flex; flex-wrap: wrap; gap: 1.5rem 3rem; margin-top: 0.75rem; }
  .recon-banner__figure { min-width: 0; }
  .recon-banner__label { margin: 0; font-size: 0.7rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--color-text-muted); }
  .recon-banner__value {
    margin: 0.3rem 0 0;
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 2rem;
    color: var(--color-forest);
    font-variant-numeric: tabular-nums;
  }
  /* The one gold element: the recovered/attention figure. */
  .recon-banner__value--accent { color: var(--color-primary-700); }

  .recon-chips { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 1.1rem; }
  .recon-chip {
    display: inline-flex; align-items: baseline; gap: 0.35rem;
    padding: 0.25rem 0.65rem; border-radius: 999px;
    border: 1px solid var(--color-rule); background: var(--color-card);
    font-size: 0.78rem; color: var(--color-text);
  }
  .recon-chip__count { font-weight: 700; font-variant-numeric: tabular-nums; }
  .recon-chip__label { color: var(--color-text-muted); }

  .recon-actionbar { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center; margin-top: 1.5rem; }
  .recon-caption { color: var(--color-text-muted); font-size: 0.82rem; margin: 0.4rem 0 0; }

  /* State badges (§05 semantic mapping). */
  .recon-state {
    display: inline-block; padding: 0.12rem 0.55rem; border-radius: 999px;
    font-size: 0.72rem; font-weight: 600; border: 1px solid var(--color-rule);
    color: var(--color-text); background: var(--color-card);
  }
  .recon-state--matched { border-color: var(--color-success-border); color: var(--color-success-text); }
  .recon-state--variance { border-color: var(--color-warning-border); color: var(--color-warning-text); }
  .recon-state--disputed { border-color: var(--color-warning-border); color: var(--color-warning-text); }
  .recon-state--unmatched { border-color: var(--color-clay); color: var(--color-clay); }
  .recon-state--pending { border-color: var(--color-rule); color: var(--color-slate); }

  .recon-delta { font-variant-numeric: tabular-nums; }
  .recon-delta--zero { color: var(--color-text-muted); }
  .recon-delta--nonzero { color: var(--color-clay); font-weight: 700; }
  .recon-delta--nonzero .recon-delta__flag {
    display: inline-block; margin-left: 0.35rem; padding: 0 0.35rem;
    border: 1px solid var(--color-clay); border-radius: 4px; font-size: 0.68rem;
  }
  .recon-num { text-align: right; font-variant-numeric: tabular-nums; }
  .recon-evidence { margin: 0.2rem 0 0; font-size: 0.78rem; color: var(--color-text-muted); }
  .recon-class {
    display: inline-block; font-size: 0.68rem; font-weight: 700; padding: 0 0.35rem;
    border-radius: 4px; border: 1px solid var(--color-rule); color: var(--color-text);
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .recon-class--anomalous { border-color: var(--color-clay); color: var(--color-clay); }
  .recon-class--expected { border-color: var(--color-success-border); color: var(--color-success-text); }
  .recon-row--flagged { background: var(--color-warning-bg); }
  .recon-details {
    margin-top: 1.5rem; border: 1px solid var(--color-rule); border-radius: var(--radius);
    background: var(--color-card); padding: 0.5rem 1.25rem 1.25rem; box-shadow: var(--shadow-card);
  }
  .recon-details > summary { cursor: pointer; font-weight: 600; color: var(--color-forest); padding: 0.75rem 0; }
  .recon-ledgers { display: grid; grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr)); gap: 1.5rem; }
  .recon-locked { color: var(--color-text-muted); font-size: 0.82rem; }
  .recon-headline {
    font-family: var(--font-display); font-weight: 600; color: var(--color-forest);
    font-size: clamp(1.6rem, 3vw, 2.2rem); margin: 0.35rem 0 0;
  }

  /* ===================== Direct Booking (M2) =========================== */
  .booking-flow { background: transparent; }
  .booking-step {
    border: 1px solid var(--color-rule); border-radius: var(--radius);
    padding: 0.9rem 1.15rem 1.15rem; margin: 0 0 1rem; background: var(--color-surface-subtle);
  }
  .booking-step > legend {
    display: flex; align-items: center; gap: 0.55rem; font-weight: 600;
    color: var(--color-forest); padding: 0 0.4rem;
  }
  .booking-step__num {
    display: inline-flex; align-items: center; justify-content: center;
    width: 1.6rem; height: 1.6rem; border-radius: 999px;
    background: var(--color-primary-action); color: var(--color-primary-text);
    font-size: 0.85rem; font-weight: 700;
  }
  .console-field__label-static { font-size: 0.8rem; color: var(--color-text-muted); font-weight: 600; }
  .booking-baserate { margin: 0.15rem 0 0; font-weight: 700; color: var(--color-forest); font-variant-numeric: tabular-nums; }
  .booking-newguest { margin-top: 0.5rem; padding-top: 0.75rem; border-top: 1px solid var(--color-rule); }
  .booking-outcome {
    border: 1px solid var(--color-primary-200); border-radius: var(--radius);
    padding: 1rem 1.15rem; background: var(--color-card); box-shadow: var(--shadow-card);
  }
  .booking-outcome__line { margin: 0 0 0.35rem; color: var(--color-text); }
  .booking-outcome__label { color: var(--color-text-muted); font-size: 0.82rem; }
  .booking-outcome__value { font-weight: 700; font-variant-numeric: tabular-nums; }
  .booking-outcome__actions { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 0.85rem; }
  .booking-code { font-weight: 700; font-variant-numeric: tabular-nums; color: var(--color-primary-700); }
  .booking-num { text-align: right; font-variant-numeric: tabular-nums; }
  .booking-rowactions { display: flex; flex-wrap: wrap; gap: 0.5rem; }
  .booking-state {
    display: inline-block; padding: 0.12rem 0.55rem; border-radius: 999px;
    font-size: 0.72rem; font-weight: 600; border: 1px solid var(--color-rule);
    color: var(--color-text); background: var(--color-card);
  }
  .booking-state--held { border-color: var(--color-rule); color: var(--color-slate); }
  .booking-state--confirmed { border-color: var(--color-success-border); color: var(--color-success-text); }
  .booking-state--cancelled { border-color: var(--color-rule); color: var(--color-text-muted); }
  .booking-state--overbooked { border-color: var(--color-clay); color: var(--color-clay); }

  /* ===================== Direct Booking composer ====================== */
  .console-page-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; }
  .console-btn--sm { padding: 0.28rem 0.6rem; font-size: 0.78rem; }

  /* Full-viewport backdrop: opaque enough that the page never bleeds through. */
  .composer-modal {
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(16, 24, 40, 0.55);
    -webkit-backdrop-filter: blur(2px); backdrop-filter: blur(2px);
    display: flex; align-items: center; justify-content: center;
    padding: 24px; overflow: hidden;
  }
  /* One SOLID modal container. Header + body; only the body's columns scroll. */
  .composer-modal__panel {
    background: var(--color-card); color: var(--color-text);
    border: 1px solid var(--color-border); border-radius: 14px;
    box-shadow: 0 24px 60px rgba(16, 24, 40, 0.28), 0 4px 14px rgba(16, 24, 40, 0.16);
    width: min(1180px, calc(100vw - 64px));
    max-height: calc(100vh - 48px);
    display: flex; flex-direction: column; overflow: hidden;
  }
  .composer-modal__panel--sm { width: min(520px, calc(100vw - 48px)); }
  /* Header: white SaaS chrome, fixed within the modal (never scrolls). */
  .composer-modal__head {
    flex-shrink: 0; min-height: 58px;
    display: flex; justify-content: space-between; align-items: center;
    padding: 0.85rem 1.4rem; border-bottom: 1px solid var(--color-border);
    background: var(--color-card); color: var(--color-text-primary);
    border-radius: 14px 14px 0 0;
  }
  .composer-modal__head h2 { margin: 0; font-family: var(--font-display); font-size: 1.2rem; color: var(--color-text-primary); }
  .composer-modal__head .console-btn--ghost { color: var(--color-text-secondary); border-color: var(--color-border-strong); }
  .composer-modal__head .console-btn--ghost:hover { background: var(--color-gray-50); }

  /* Body: single column on narrow, two columns on desktop. The body owns the
     remaining height (min-height:0 lets its children scroll, not the panel). */
  .composer-body {
    flex: 1 1 auto; min-height: 0;
    display: grid; grid-template-columns: minmax(0, 1fr);
    overflow: hidden;
  }
  @media (min-width: 900px) {
    .composer-body { grid-template-columns: minmax(0, 2fr) minmax(320px, 1fr); }
  }
  /* Left form: SOLID off-white, internal scroll, comfortable padding. */
  .composer-left {
    min-width: 0; background: var(--color-surface);
    padding: 24px 26px; overflow-y: auto; -webkit-overflow-scrolling: touch;
  }
  /* Right summary: SOLID white, belongs to the same modal, internal scroll. */
  .composer-summary {
    min-width: 0; background: var(--color-card);
    border-left: 1px solid var(--color-rule);
    padding: 24px; overflow-y: auto; -webkit-overflow-scrolling: touch;
  }
  @media (max-width: 899.98px) {
    .composer-summary { border-left: none; border-top: 1px solid var(--color-rule); }
  }

  /* Section cards: solid white on the ivory left column, clear spacing. */
  .composer-section {
    background: var(--color-card); border: 1px solid var(--color-rule);
    border-radius: 12px; padding: 18px 20px; margin-bottom: 16px;
  }
  .composer-section:last-child { margin-bottom: 0; }
  .composer-section__title {
    margin: 0 0 0.85rem; font-family: var(--font-display); font-size: 1.02rem;
    color: var(--color-forest);
  }
  .composer-readonly { margin: 0; padding: 0.5rem 0; font-weight: 600; font-variant-numeric: tabular-nums; }

  .composer-avail { margin: 0.5rem 0 0; font-size: 0.82rem; font-weight: 600; }
  .composer-avail--yes { color: var(--color-success-text); }
  .composer-avail--no { color: var(--color-clay); }
  .composer-rate__note { margin: 0.15rem 0 0; font-size: 0.72rem; color: var(--color-text-muted); }
  .composer-rate__breakdown { margin-top: 0.3rem; font-size: 0.78rem; }
  .composer-rate__breakdown > summary { cursor: pointer; color: var(--color-forest); font-weight: 600; }
  .composer-breakdown-table { width: 100%; border-collapse: collapse; margin-top: 0.4rem; font-size: 0.75rem; }
  .composer-breakdown-table th, .composer-breakdown-table td { text-align: left; padding: 0.2rem 0.4rem; border-bottom: 1px solid var(--color-rule); font-variant-numeric: tabular-nums; }

  .composer-combobox { position: relative; }
  .composer-listbox {
    list-style: none; margin: 0.25rem 0 0; padding: 0.25rem; position: absolute;
    z-index: 20; left: 0; right: 0; background: var(--color-card);
    border: 1px solid var(--color-rule); border-radius: 10px;
    box-shadow: var(--shadow-card); max-height: 280px; overflow-y: auto;
  }
  .composer-listbox__item { padding: 0.5rem 0.6rem; border-radius: 8px; cursor: pointer; display: flex; flex-direction: column; gap: 0.1rem; }
  .composer-listbox__item.is-active, .composer-listbox__item:hover { background: var(--color-surface-subtle); }
  .composer-listbox__name { font-weight: 600; }
  .composer-listbox__meta, .composer-guest-chip__meta { font-size: 0.78rem; color: var(--color-text-muted); }
  .composer-listbox__empty { padding: 0.5rem 0.6rem; color: var(--color-text-muted); font-size: 0.85rem; }
  .composer-listbox__addnew { padding: 0.35rem 0.3rem 0.1rem; border-top: 1px solid var(--color-rule); margin-top: 0.25rem; }

  .composer-guest-chip { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.55rem 0.7rem; border: 1px solid var(--color-success-border); border-radius: 10px; background: var(--color-surface-subtle); }
  .composer-guest-chip__name { font-weight: 600; }
  .composer-guest-hint { margin: 0.6rem 0 0; font-size: 0.82rem; color: var(--color-success-text); background: var(--color-success-bg); padding: 0.45rem 0.65rem; border-radius: 8px; }

  .composer-subdrawer { margin-top: 0.85rem; padding: 1rem; border: 1px solid var(--color-rule); border-radius: 10px; background: var(--color-surface-subtle); }
  .composer-subdrawer__head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.6rem; }
  .composer-subdrawer__head h3 { margin: 0; font-size: 0.95rem; }
  .composer-subdrawer__actions { margin-top: 0.7rem; }

  .composer-dupe { border: 1px solid var(--color-warning-border); border-radius: 8px; padding: 0.7rem; margin-bottom: 0.8rem; background: var(--color-warning-bg); }
  .composer-dupe__title { margin: 0 0 0.4rem; font-weight: 700; font-size: 0.85rem; }
  .composer-dupe__row { display: flex; justify-content: space-between; align-items: center; gap: 0.6rem; font-size: 0.82rem; padding: 0.2rem 0; }

  .composer-addons { margin-top: 0.7rem; }
  .composer-addons__head { font-size: 0.82rem; color: var(--color-text-muted); margin-bottom: 0.4rem; }
  .composer-addons__row, .composer-pay__row { display: grid; grid-template-columns: 1.6fr 0.6fr 1fr auto; gap: 0.5rem; margin-bottom: 0.45rem; align-items: center; }
  .composer-pay__row { grid-template-columns: 1fr 1fr 1fr auto; }
  .composer-addons__presets { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.4rem; }
  .composer-taxnote, .composer-paylink { font-size: 0.78rem; color: var(--color-text-muted); margin: 0.6rem 0 0; }
  .composer-paylink--disabled { opacity: 0.7; }
  .composer-paystatus { margin: 0 0 0.6rem; font-size: 0.85rem; }

  .composer-summary__title { margin: 0 0 1rem; font-family: var(--font-display); color: var(--color-forest); font-size: 1.15rem; }
  .composer-summary__empty { color: var(--color-text-muted); font-size: 0.88rem; line-height: 1.5; }
  .composer-summary__meta, .composer-summary__price { margin: 0 0 1.1rem; display: grid; gap: 0.4rem; }
  .composer-summary__meta div, .composer-summary__price div { display: flex; justify-content: space-between; gap: 0.75rem; font-size: 0.85rem; }
  .composer-summary__meta dt, .composer-summary__price dt { color: var(--color-text-muted); margin: 0; }
  .composer-summary__meta dd, .composer-summary__price dd { margin: 0; font-weight: 600; text-align: right; font-variant-numeric: tabular-nums; }
  .composer-summary__meta { padding-bottom: 1.1rem; border-bottom: 1px solid var(--color-rule); }
  .composer-summary__price { padding-top: 0.2rem; }
  .composer-summary__total { margin-top: 0.35rem; padding-top: 0.6rem; border-top: 1px solid var(--color-rule); }
  .composer-summary__total dt, .composer-summary__total dd { font-size: 1rem; color: var(--color-forest); font-weight: 700; }
  .composer-summary__balance dd { color: var(--color-clay); }
  .composer-summary__actions { display: flex; flex-direction: column; gap: 0.7rem; margin-top: 1.2rem; }
  .composer-summary__actions .console-btn { width: 100%; }

  .composer-success { text-align: center; padding: 2rem 1.5rem; }
  .composer-success__badge { width: 52px; height: 52px; border-radius: 999px; background: var(--color-success-text); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 1.6rem; margin: 0 auto 0.8rem; }
  .composer-success__ref { font-size: 1rem; }
  .composer-success__actions { display: flex; gap: 0.6rem; justify-content: center; margin-top: 1.2rem; }

  /* ===================== Channel Manager (M3) ========================== */
  .channel-state {
    display: inline-block; padding: 0.12rem 0.55rem; border-radius: 999px;
    font-size: 0.72rem; font-weight: 600; border: 1px solid var(--color-rule);
    color: var(--color-text); background: var(--color-card);
  }
  .channel-state--connected { border-color: var(--color-success-border); color: var(--color-success-text); }
  .channel-state--disconnected { border-color: var(--color-rule); color: var(--color-text-muted); }
  .channel-state--error { border-color: var(--color-clay); color: var(--color-clay); }
  .channel-authority {
    display: inline-block; padding: 0 0.4rem; border-radius: 4px; font-size: 0.68rem;
    font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    border: 1px solid var(--color-rule); color: var(--color-text); background: var(--color-card);
  }
  .channel-authority--ota { border-color: var(--color-warning-border); color: var(--color-warning-text); }
  .channel-authority--avos { border-color: var(--color-success-border); color: var(--color-success-text); }
  .channel-attention { color: var(--color-clay); font-weight: 700; }
  .channel-name { font-weight: 600; margin-right: 0.4rem; color: var(--color-forest); }
  .channel-parity-form {
    margin-top: 1rem; padding: 1.1rem; border: 1px solid var(--color-rule);
    border-radius: var(--radius); background: var(--color-surface-subtle);
  }

  /* ===================== Analytics (M9) ================================ */
  .analytics-bar__track {
    width: 100%; min-width: 4rem; height: 0.55rem; background: var(--color-surface-subtle);
    border: 1px solid var(--color-rule); border-radius: 999px; overflow: hidden;
  }
  .analytics-bar { display: block; height: 100%; background: var(--color-primary-action); border-radius: 999px; }
  .analytics-pct { font-variant-numeric: tabular-nums; color: var(--color-text); }
  .analytics-barcell { min-width: 8rem; }
  .analytics-barcell__value { display: block; font-variant-numeric: tabular-nums; color: var(--color-text); margin-bottom: 0.25rem; }
  .analytics-delta {
    display: inline-flex; align-items: baseline; gap: 0.2rem; font-size: 0.8rem;
    font-weight: 700; font-variant-numeric: tabular-nums;
  }
  .analytics-delta--up { color: var(--color-success-text); }
  .analytics-delta--down { color: var(--color-clay); }
  .analytics-delta--flat { color: var(--color-text-muted); }

  /* ===================== Guest CRM (M6) ================================ */
  .crm-split { display: grid; grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr)); gap: 1.5rem; }
  .crm-row--selected { background: var(--color-surface-subtle); }
  .crm-rowbtn {
    background: none; border: none; padding: 0; margin: 0; font: inherit;
    color: var(--color-forest); text-align: left; cursor: pointer; font-weight: 600;
  }
  .crm-rowbtn:hover { text-decoration: underline; }
  .crm-optout {
    display: inline-block; padding: 0.12rem 0.55rem; border-radius: 999px;
    font-size: 0.72rem; font-weight: 600; border: 1px solid var(--color-rule);
    color: var(--color-text); background: var(--color-card);
  }
  .crm-optout--yes { border-color: var(--color-clay); color: var(--color-clay); }
  .crm-optout--no { border-color: var(--color-success-border); color: var(--color-success-text); }
  .crm-class {
    display: inline-block; padding: 0 0.4rem; border-radius: 4px; font-size: 0.68rem;
    font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    border: 1px solid var(--color-rule); color: var(--color-text); background: var(--color-card);
  }
  .crm-class--definite { border-color: var(--color-clay); color: var(--color-clay); }
  .crm-class--probable { border-color: var(--color-warning-border); color: var(--color-warning-text); }
  .crm-detail { margin-top: 1rem; padding-top: 1rem; border-top: 1px solid var(--color-rule); }
  .crm-detail__field { margin: 0 0 0.5rem; }
  .crm-detail__label { color: var(--color-text-muted); font-size: 0.78rem; font-weight: 600; }
  .crm-detail__value { color: var(--color-text); }
  .crm-conversation {
    display: flex; flex-direction: column; gap: 0.6rem; margin: 0.75rem 0;
    max-height: 26rem; overflow-y: auto; padding: 0.25rem;
  }
  .crm-msg {
    border: 1px solid var(--color-rule); border-radius: var(--radius-sm);
    padding: 0.55rem 0.8rem; background: var(--color-card); max-width: 85%;
  }
  .crm-msg--inbound { align-self: flex-start; background: var(--color-surface-subtle); border-left: 3px solid var(--color-rule); }
  .crm-msg--outbound { align-self: flex-end; border-right: 3px solid var(--color-forest); }
  .crm-msg__meta {
    display: flex; flex-wrap: wrap; align-items: baseline; gap: 0.4rem;
    margin: 0 0 0.3rem; font-size: 0.72rem; color: var(--color-text-muted);
  }
  .crm-msg__body { margin: 0; color: var(--color-text); }
  .crm-msg__time { font-variant-numeric: tabular-nums; }
  .crm-author {
    display: inline-block; padding: 0 0.4rem; border-radius: 4px; font-size: 0.66rem;
    font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    border: 1px solid var(--color-rule); color: var(--color-text); background: var(--color-card);
  }
  .crm-author--guest { border-color: var(--color-rule); color: var(--color-text-muted); }
  .crm-author--operator { border-color: var(--color-success-border); color: var(--color-success-text); }
  .crm-author--agent { border-color: var(--color-forest); color: var(--color-forest); }
  .crm-kind {
    display: inline-block; padding: 0 0.4rem; border-radius: 4px; font-size: 0.66rem;
    font-weight: 600; border: 1px solid var(--color-rule); color: var(--color-text-muted); background: var(--color-card);
  }
  .crm-kind--marketing { border-color: var(--color-warning-border); color: var(--color-warning-text); }
  .crm-reply { margin-top: 0.75rem; }
  .crm-reply textarea {
    width: 100%; min-height: 4rem; padding: 0.5rem 0.6rem; border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm); background: var(--color-card); color: var(--color-text);
    font: inherit; resize: vertical;
  }

  /* ---- Actionable CRM: KPI cards -------------------------------------- */
  .crm-kpis {
    display: grid; gap: 1rem; margin-top: 1.5rem;
    grid-template-columns: repeat(5, minmax(9.5rem, 1fr));
  }
  .crm-kpis--compact { grid-template-columns: repeat(3, minmax(7rem, 1fr)); margin-top: 0.5rem; margin-bottom: 0.5rem; }
  @media (max-width: 1200px) {
    .crm-kpis { grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr)); }
  }

  /* ---- Actionable CRM: tabs ------------------------------------------- */
  .crm-tabs {
    display: flex; flex-wrap: wrap; gap: 0.25rem; margin: 1.75rem 0 0.5rem;
    border-bottom: 1px solid var(--color-rule);
  }
  .crm-tab {
    display: inline-flex; align-items: center; gap: 0.4rem;
    background: none; border: none; border-bottom: 2px solid transparent;
    font: inherit; font-weight: 600; font-size: 0.92rem; cursor: pointer;
    padding: 0.55rem 0.9rem; color: var(--color-text-muted);
    margin-bottom: -1px;
  }
  .crm-tab:hover { color: var(--color-forest); }
  .crm-tab--active { color: var(--color-forest); border-bottom-color: var(--color-primary-action); }
  .crm-tab__badge {
    display: inline-block; min-width: 1.2rem; text-align: center;
    padding: 0 0.35rem; border-radius: 999px; font-size: 0.68rem; font-weight: 700;
    background: var(--color-primary-action); color: #FFFFFF;
  }

  /* ---- Guest tier + consent badges ------------------------------------ */
  .crm-tier {
    display: inline-block; padding: 0.05rem 0.5rem; border-radius: 999px;
    font-size: 0.68rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
    border: 1px solid var(--color-rule); color: var(--color-text); background: var(--color-card);
  }
  .crm-tier--vip { border-color: var(--color-warning-border); color: var(--color-warning-text); background: var(--color-warning-bg); }
  .crm-tier--loyal { border-color: var(--color-success-border); color: var(--color-success-text); }
  .crm-tier--returning { border-color: var(--color-slate); color: var(--color-slate); }
  .crm-tier--new { border-color: var(--color-rule); color: var(--color-text-muted); }

  /* ---- Initials tile -------------------------------------------------- */
  .crm-tile {
    display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto;
    border-radius: 999px; background: var(--color-surface-subtle); border: 1px solid var(--color-rule);
    color: var(--color-forest); font-weight: 700; letter-spacing: 0.02em;
  }
  .crm-tile--sm { width: 2rem; height: 2rem; font-size: 0.72rem; }
  .crm-tile--lg { width: 2.9rem; height: 2.9rem; font-size: 1rem; }

  /* ---- Directory: filters + table ------------------------------------- */
  .crm-directory { margin-top: 0.5rem; }
  .crm-filters {
    display: flex; flex-wrap: wrap; align-items: flex-end; gap: 0.75rem;
    margin-bottom: 1rem;
  }
  .crm-filters .console-field { flex: 0 0 auto; min-width: 9rem; gap: 0.25rem; }
  .crm-filters__search { flex: 1 1 16rem; min-width: 12rem; }
  .crm-filters .console-field label {
    font-size: 0.7rem; font-weight: 600; letter-spacing: 0.04em;
    color: var(--color-text-muted); text-transform: uppercase;
  }
  .crm-filters .console-field input, .crm-filters .console-field select {
    padding: 0.45rem 0.55rem; border: 1px solid var(--color-rule); border-radius: var(--radius-sm);
    background: var(--color-card); color: var(--color-text); font: inherit; font-size: 0.88rem;
  }
  .crm-filters__clear { flex: 0 0 auto; align-self: flex-end; }
  .crm-directory__count { margin: 0 0 0.5rem; color: var(--color-text-muted); font-size: 0.82rem; }
  .crm-table-wrap { overflow-x: auto; }
  .crm-guestcell {
    display: inline-flex; align-items: center; gap: 0.6rem; background: none; border: none;
    padding: 0; margin: 0; font: inherit; cursor: pointer; text-align: left;
  }
  .crm-guestcell__meta { display: flex; flex-direction: column; min-width: 0; }
  .crm-guestcell__name { font-weight: 600; color: var(--color-forest); }
  .crm-guestcell:hover .crm-guestcell__name { text-decoration: underline; }
  .crm-guestcell__tags { font-size: 0.72rem; color: var(--color-text-muted); overflow-wrap: anywhere; }
  .crm-contact { font-size: 0.85rem; }
  .crm-contact__phone { display: block; color: var(--color-text-muted); font-size: 0.8rem; }
  .crm-rowactions { display: flex; align-items: center; gap: 0.35rem; white-space: nowrap; }

  /* ---- Engagement opportunities --------------------------------------- */
  .crm-engagement { margin-top: 1.25rem; }
  .crm-engagement-groups { display: flex; flex-direction: column; }
  .crm-group__count {
    display: inline-block; margin-left: 0.4rem; padding: 0 0.4rem; border-radius: 999px;
    font-size: 0.7rem; font-weight: 700; background: var(--color-surface-subtle);
    border: 1px solid var(--color-rule); color: var(--color-forest); vertical-align: middle;
  }
  .crm-opp-list { list-style: none; margin: 0.5rem 0 0; padding: 0; display: flex; flex-direction: column; gap: 0.6rem; }
  .crm-opp {
    display: flex; align-items: flex-start; gap: 0.85rem;
    padding: 0.85rem 1rem; border: 1px solid var(--color-rule); border-radius: var(--radius);
    background: var(--color-card);
  }
  .crm-opp__main { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 0.2rem; }
  .crm-opp__title { margin: 0; display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
  .crm-opp__reason { margin: 0; font-weight: 600; color: var(--color-forest); font-size: 0.9rem; }
  .crm-opp__suggestion { margin: 0; color: var(--color-text-muted); font-size: 0.85rem; }
  .crm-opp__facts { margin: 0.2rem 0 0; display: flex; flex-wrap: wrap; gap: 0.4rem 0.9rem; font-size: 0.78rem; color: var(--color-text-muted); }
  .crm-opp__facts span { font-variant-numeric: tabular-nums; }
  .crm-opp__actions { display: flex; flex-direction: column; gap: 0.4rem; flex: 0 0 auto; }
  @media (max-width: 720px) {
    .crm-opp { flex-wrap: wrap; }
    .crm-opp__actions { flex-direction: row; width: 100%; }
    .crm-opp__actions .console-btn { flex: 1 1 auto; }
  }

  /* ---- Segments ------------------------------------------------------- */
  .crm-segments {
    display: grid; gap: 1rem; margin-top: 1.5rem;
    grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  }
  .crm-segment { display: flex; flex-direction: column; gap: 0.4rem; }
  .crm-segment__head { display: flex; align-items: baseline; justify-content: space-between; gap: 0.5rem; }
  .crm-segment__count { margin: 0; font-family: var(--font-body); font-weight: 700; font-size: 1.6rem; color: var(--color-forest); font-variant-numeric: tabular-nums; }
  .crm-segment__open { margin-top: auto; align-self: flex-start; }

  /* ---- Overview: Currently staying grid + cards ----------------------- */
  .crm-current-grid {
    display: grid; gap: 1rem; margin-top: 0.5rem;
    grid-template-columns: repeat(auto-fill, minmax(20rem, 1fr));
  }
  .crm-current { display: flex; flex-direction: column; gap: 0.75rem; }
  .crm-current__head { display: flex; align-items: center; gap: 0.75rem; }
  .crm-current__ident { min-width: 0; display: flex; flex-direction: column; gap: 0.2rem; }
  .crm-current__name { margin: 0; font-weight: 700; }
  .crm-current__badges { margin: 0; display: flex; flex-wrap: wrap; gap: 0.35rem; align-items: center; }
  .crm-current__meta {
    margin: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 0.55rem 1rem;
    padding: 0.6rem 0; border-top: 1px solid var(--color-rule); border-bottom: 1px solid var(--color-rule);
  }
  .crm-current__meta > div { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
  .crm-current__meta dt { margin: 0; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-text-muted); }
  .crm-current__meta dd { margin: 0; color: var(--color-text); overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
  .crm-current__contact { display: flex; flex-direction: column; }
  .crm-current__tags { margin: 0; }
  .crm-current__actions { display: flex; gap: 0.5rem; margin-top: auto; }
  .crm-current__actions .console-btn { flex: 1 1 auto; text-align: center; }
  .crm-current__head { position: relative; }
  .crm-current__head .accounts-rowmenu { margin-left: auto; }
  .crm-current__remaining { color: var(--color-text-muted); font-weight: 500; }
  .crm-pref-chip { border-color: var(--color-success-border); color: var(--color-success-text); }
  /* Subtle VIP accent (the one gold cue) — a hairline gold ring, not heavy. */
  .crm-current--vip { border-color: var(--color-primary-200); box-shadow: var(--shadow-card); }
  .crm-current--vip .crm-current__name { color: var(--color-forest); }
  /* Message composer textarea. */
  .crm-composer__textarea {
    width: 100%; min-height: 6rem; padding: 0.5rem 0.6rem; border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm); background: var(--color-card); color: var(--color-text);
    font: inherit; resize: vertical;
  }

  /* ---- Overview: polished empty hero for "no current guests" ---------- */
  .crm-empty-hero {
    margin-top: 0.5rem; padding: 2rem 1.25rem; text-align: center;
    background: var(--color-surface-subtle); border: 1px dashed var(--color-rule);
    border-radius: var(--radius);
  }
  .crm-empty-hero__title { margin: 0; font-family: var(--font-display); font-weight: 600; font-size: 1.2rem; color: var(--color-forest); }
  .crm-empty-hero__sub { margin: 0.4rem auto 0; max-width: 34rem; color: var(--color-text-muted); font-size: 0.9rem; }

  /* ---- New-guest inline form margins ---------------------------------- */
  .crm-newguest { margin-top: 1rem; }
  .crm-newguest__actions { display: flex; gap: 0.6rem; flex-wrap: wrap; }
  .crm-unavailable { color: var(--color-text-muted); font-style: italic; }

  /* ---- Right-side guest profile drawer -------------------------------- */
  .crm-drawer-root { position: fixed; inset: 0; z-index: 80; }
  .crm-drawer-scrim {
    position: absolute; inset: 0; border: none; padding: 0; margin: 0;
    background: rgba(16, 24, 40, 0.4); cursor: pointer;
    animation: console-pop 0.12s ease;
  }
  .crm-drawer {
    position: absolute; top: 0; right: 0; bottom: 0;
    width: min(34rem, 94vw); background: var(--color-surface);
    border-left: 1px solid var(--color-rule); box-shadow: -8px 0 30px rgba(16,24,40,0.18);
    display: flex; flex-direction: column; overflow: hidden;
    animation: crm-slidein 0.16s ease;
  }
  @keyframes crm-slidein { from { transform: translateX(100%); } to { transform: translateX(0); } }
  .crm-drawer__head {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem;
    padding: 1.1rem 1.25rem; border-bottom: 1px solid var(--color-rule); background: var(--color-card);
  }
  .crm-drawer__ident { display: flex; align-items: center; gap: 0.8rem; min-width: 0; }
  .crm-drawer__name { margin: 0; font-family: var(--font-display); font-weight: 600; font-size: 1.3rem; color: var(--color-forest); }
  .crm-drawer__sub { margin: 0.25rem 0 0; display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
  .crm-drawer__ltv { font-size: 0.82rem; color: var(--color-text-muted); font-variant-numeric: tabular-nums; }
  .crm-drawer__close { color: var(--color-forest); background: var(--color-surface-subtle); }
  .crm-drawer__tabs {
    display: flex; flex-wrap: wrap; gap: 0.15rem; padding: 0 1.25rem;
    border-bottom: 1px solid var(--color-rule); background: var(--color-card);
  }
  .crm-drawer__tab {
    background: none; border: none; border-bottom: 2px solid transparent; margin-bottom: -1px;
    font: inherit; font-weight: 600; font-size: 0.82rem; cursor: pointer;
    padding: 0.55rem 0.6rem; color: var(--color-text-muted);
  }
  .crm-drawer__tab:hover { color: var(--color-forest); }
  .crm-drawer__tab--active { color: var(--color-forest); border-bottom-color: var(--color-primary-action); }
  .crm-drawer__body { flex: 1 1 auto; overflow-y: auto; padding: 1.25rem; }
  .crm-drawer__grid { margin: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 0.85rem 1.25rem; }
  .crm-drawer__grid--tight { grid-template-columns: 1fr 1fr; gap: 0.65rem 1.25rem; }
  .crm-drawer__field { margin: 0; display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; }
  .crm-drawer__field dt { margin: 0; font-size: 0.66rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-text-muted); }
  .crm-drawer__field dd { margin: 0; color: var(--color-text); overflow-wrap: anywhere; }
  .crm-drawer__section { margin-top: 1.25rem; }
  .crm-drawer__section:first-child { margin-top: 0; }
  .crm-drawer__subhead { margin: 0 0 0.35rem; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--color-text-muted); }
  .crm-drawer__note { margin: 0 0 0.75rem; padding: 0.6rem 0.75rem; background: var(--color-surface-subtle); border: 1px solid var(--color-rule); border-radius: var(--radius-sm); color: var(--color-text); }
  .crm-chips { display: flex; flex-wrap: wrap; gap: 0.4rem; }
  @media (max-width: 560px) {
    .crm-drawer__grid, .crm-drawer__grid--tight { grid-template-columns: 1fr; }
  }

  /* ===================== Operations command centre (M8) =============== */
  .ops-kpis {
    display: grid; gap: 1rem; margin-top: 1.5rem;
    grid-template-columns: repeat(6, minmax(9rem, 1fr));
  }
  @media (max-width: 1300px) { .ops-kpis { grid-template-columns: repeat(3, minmax(9rem, 1fr)); } }
  @media (max-width: 700px) { .ops-kpis { grid-template-columns: repeat(2, minmax(8rem, 1fr)); } }
  /* Clickable KPI cards read as buttons but keep card styling. */
  .ops-kpi-btn { text-align: left; cursor: pointer; font: inherit; color: inherit; }
  .ops-kpi-btn:hover { border-color: var(--color-forest); }
  .ops-kpi-btn:focus-visible { outline: 2px solid var(--color-focus); outline-offset: 2px; }

  .ops-subtle { color: var(--color-text-muted); }
  .ops-task-title { font-weight: 600; color: var(--color-text); }
  .ops-overdue { color: var(--color-clay); font-weight: 600; }
  .ops-overdue-tag { color: var(--color-clay); font-size: 0.72rem; font-weight: 700; text-transform: uppercase; }
  .ops-risk-text { color: var(--color-clay); font-weight: 600; }
  .ops-checklist-badge, .ops-blocked-note {
    display: block; margin-top: 0.15rem; font-size: 0.72rem; color: var(--color-text-muted);
  }
  .ops-blocked-note { color: var(--color-clay); }
  .ops-field--grow { flex: 1 1 18rem; }

  /* Property readiness cards. */
  .ops-readiness-grid, .ops-turnover-grid, .ops-checklist-grid, .ops-dept-grid, .ops-perf-grid {
    display: grid; gap: 1rem; margin-top: 0.5rem;
    grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  }
  .ops-perf-grid { grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr)); }
  .ops-readycard, .ops-turnover, .ops-checklist-card { display: flex; flex-direction: column; gap: 0.6rem; }
  .ops-readycard--risk, .ops-turnover--urgent { border-color: var(--color-clay); box-shadow: 0 0 0 1px rgba(180, 35, 24, 0.3), var(--shadow-card); }
  .ops-readycard__head, .ops-turnover__head, .ops-checklist-card__head {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 0.5rem;
  }
  .ops-readycard__unit, .ops-turnover__unit { margin: 0; font-weight: 700; color: var(--color-forest); }
  .ops-readycard__prop, .ops-turnover__prop { margin: 0; font-size: 0.82rem; color: var(--color-text-muted); }
  .ops-readycard__meta, .ops-turnover__meta {
    margin: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 0.4rem 1rem;
  }
  .ops-readycard__meta dt, .ops-turnover__meta dt {
    margin: 0; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--color-text-muted);
  }
  .ops-readycard__meta dd, .ops-turnover__meta dd { margin: 0; color: var(--color-text); font-variant-numeric: tabular-nums; }
  .ops-readycard__blocked { margin: 0; font-size: 0.8rem; color: var(--color-clay); }

  /* Turnover stage track. */
  .ops-stage-track { list-style: none; margin: 0.25rem 0; padding: 0; display: flex; flex-wrap: wrap; gap: 0.35rem; }
  .ops-stage { display: flex; align-items: center; gap: 0.3rem; font-size: 0.7rem; color: var(--color-text-muted); }
  .ops-stage__dot { width: 0.6rem; height: 0.6rem; border-radius: 999px; background: var(--color-rule); border: 1px solid var(--color-rule); }
  .ops-stage--done .ops-stage__dot { background: var(--color-success-text); border-color: var(--color-success-text); }
  .ops-stage--done .ops-stage__label { color: var(--color-forest); font-weight: 600; }
  .ops-turnover__actions { margin-top: auto; }

  /* Timeline. */
  .ops-timeline { list-style: none; margin: 0.5rem 0 0; padding: 0; }
  .ops-timeline__item {
    display: grid; grid-template-columns: 6rem 1rem 1fr auto; align-items: center; gap: 0.75rem;
    padding: 0.7rem 0; border-bottom: 1px solid var(--color-rule);
  }
  .ops-timeline__item:last-child { border-bottom: none; }
  .ops-timeline__time { font-size: 0.78rem; font-weight: 600; color: var(--color-text-muted); }
  .ops-timeline__dot { width: 0.6rem; height: 0.6rem; border-radius: 999px; background: var(--color-primary-action); }
  .ops-timeline__title { margin: 0; font-weight: 600; color: var(--color-text); }
  .ops-timeline__meta { margin: 0.1rem 0 0; font-size: 0.8rem; color: var(--color-text-muted); }
  @media (max-width: 700px) {
    .ops-timeline__item { grid-template-columns: 1fr auto; }
    .ops-timeline__dot { display: none; }
    .ops-timeline__time { grid-column: 1; }
    .ops-timeline__status { grid-column: 2; }
    .ops-timeline__body { grid-column: 1 / -1; }
  }

  /* Needs attention list. */
  .ops-attention { list-style: none; margin: 0.5rem 0 0; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
  .ops-attention__item {
    display: flex; align-items: center; gap: 0.7rem; padding: 0.65rem 0.8rem;
    border: 1px solid var(--color-rule); border-radius: var(--radius-sm); background: var(--color-card);
  }
  .ops-attention__item--critical { border-color: var(--color-clay); background: var(--color-error-bg); }
  .ops-attention__item--attention { border-color: var(--color-warning-border); background: var(--color-warning-bg); }
  .ops-attention__dot { flex: 0 0 auto; width: 0.55rem; height: 0.55rem; border-radius: 999px; background: var(--color-clay); }
  .ops-attention__item--attention .ops-attention__dot { background: var(--color-warning-text); }
  .ops-attention__body { flex: 1 1 auto; min-width: 0; }
  .ops-attention__label { margin: 0; font-weight: 600; color: var(--color-text); }
  .ops-attention__detail { margin: 0.1rem 0 0; font-size: 0.82rem; color: var(--color-text-muted); }

  /* Department snapshot cards (clickable). */
  .ops-dept-card { text-align: left; cursor: pointer; font: inherit; color: inherit; display: flex; flex-direction: column; gap: 0.4rem; }
  .ops-dept-card:hover { border-color: var(--color-forest); }
  .ops-dept-card:focus-visible { outline: 2px solid var(--color-focus); outline-offset: 2px; }
  .ops-dept-counts { display: flex; flex-wrap: wrap; gap: 0.3rem 0.75rem; font-size: 0.82rem; color: var(--color-text-muted); }
  .ops-dept-counts__warn { color: var(--color-clay); font-weight: 600; }

  /* Checklist template cards. */
  .ops-checklist-items { list-style: disc; margin: 0.25rem 0 0; padding-left: 1.1rem; font-size: 0.85rem; color: var(--color-text); }
  .ops-checklist-items li { margin: 0.15rem 0; }

  /* Shared property selector in the Operations header. */
  .ops-property-select { min-width: 14rem; }
  .ops-property-select label {
    font-size: 0.7rem; font-weight: 600; letter-spacing: 0.04em;
    text-transform: uppercase; color: var(--color-text-muted);
  }
  .ops-property-select select {
    padding: 0.45rem 0.6rem; border: 1px solid var(--color-rule); border-radius: var(--radius-sm);
    background: var(--color-card); color: var(--color-text); font: inherit; font-size: 0.9rem;
  }
  /* Clickable property-comparison rows. */
  .ops-comp-row { cursor: pointer; }
  .ops-comp-row:hover td { background: var(--color-surface-subtle); }

  /* ===================== Shared: placeholder / misc ==================== */
  .console-placeholder {
    margin-top: 1.5rem; border: 1px dashed var(--color-rule); border-radius: var(--radius);
    padding: 2rem; background: var(--color-surface-subtle); color: var(--color-text-muted);
  }
`;

/** AI Agents (M7) + remaining shared utility classes. */
const AGENTS_CSS = `
  /* Kill-switch banner: engaged => clay (critical), else quiet. */
  .agent-killbanner {
    margin-top: 1.5rem; border: 1px solid var(--color-rule); border-radius: var(--radius);
    padding: 1rem 1.25rem; background: var(--color-card); box-shadow: var(--shadow-card);
    display: flex; align-items: center; gap: 1rem; flex-wrap: wrap;
  }
  .agent-killbanner[data-engaged="true"] { border-color: var(--color-clay); box-shadow: 0 0 0 1px var(--color-clay); }
  .agent-killbanner__body { flex: 1 1 16rem; min-width: 0; }
  .agent-killbanner__title { margin: 0; font-weight: 600; color: var(--color-forest); }
  .agent-killbanner__note { margin: 0.25rem 0 0; color: var(--color-text-muted); font-size: 0.85rem; }
  .agent-killstate { font-weight: 700; }
  .agent-killstate--on { color: var(--color-clay); }
  .agent-killstate--off { color: var(--color-success-text); }

  .agent-num { text-align: right; font-variant-numeric: tabular-nums; }
  .agent-tools { display: inline-flex; flex-wrap: wrap; gap: 0.3rem; }
  .agent-tool {
    display: inline-block; padding: 0.05rem 0.45rem; border-radius: 999px;
    font-size: 0.68rem; font-weight: 500; border: 1px solid var(--color-rule);
    color: var(--color-text-muted); background: var(--color-surface-subtle);
  }

  .agent-dispatch, .agent-screener { }
  .agent-outcome {
    margin-top: 1rem; padding: 0.9rem 1.1rem; border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm); background: var(--color-surface-subtle);
  }
  .agent-outcome__reason { margin: 0.4rem 0 0; color: var(--color-text-muted); font-size: 0.88rem; }
  .agent-sanitized {
    margin: 0.5rem 0 0; padding: 0.75rem; border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm); background: var(--color-surface-subtle); color: var(--color-text);
    font-family: var(--font-body); font-size: 0.85rem; white-space: pre-wrap; overflow-wrap: anywhere;
  }

  /* Dispatch decision badges (§05 mapping): acted=sage, drafted=slate/forest,
     escalated=gold, refused=clay. */
  .agent-decision {
    display: inline-block; padding: 0.12rem 0.6rem; border-radius: 999px;
    font-size: 0.72rem; font-weight: 700; text-transform: capitalize;
    border: 1px solid var(--color-rule); color: var(--color-text); background: var(--color-card);
  }
  .agent-decision--acted { border-color: var(--color-success-border); color: var(--color-success-text); }
  .agent-decision--drafted { border-color: var(--color-forest); color: var(--color-forest); }
  .agent-decision--escalated { border-color: var(--color-warning-border); color: var(--color-warning-text); }
  .agent-decision--refused { border-color: var(--color-clay); color: var(--color-clay); }

  /* Inline single-field form (Admin settings rows, etc.). */
  .console-inline-form { display: flex; align-items: flex-end; gap: 0.6rem; margin: 0.4rem 0; flex-wrap: wrap; }
  .console-inline-form .console-field { flex: 1 1 14rem; }

  /* Screen section headings use the one product font (weight 600). */
  [data-console-screen] > h1 {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: clamp(1.7rem, 3vw, 2.4rem);
    color: var(--color-forest);
    margin: 0 0 0.25rem;
    line-height: 1.1;
  }
`;

/** Dashboard (greeting, insight, charts, check-ins, properties), the New
 *  Booking button + modal, and chart tooltips/legends. */
const DASHBOARD_CSS = `
  /* ---- New booking button (top bar) ------------------------------------ */
  .console-newbooking {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 0.95rem;
    border-radius: var(--radius-sm);
    background: var(--color-primary-action);
    color: #FFFFFF;
    border: 1px solid var(--color-primary-action);
    font: inherit;
    font-weight: 700;
    font-size: 0.85rem;
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease;
    white-space: nowrap;
  }
  .console-newbooking:hover { background: var(--color-primary-hover); border-color: var(--color-primary-hover); }
  .console-newbooking:focus-visible { outline: none; box-shadow: var(--focus-ring); }
  .console-newbooking > span { font-size: 1.05rem; line-height: 0; }

  /* ---- AI insight banner ----------------------------------------------- */
  .dashboard-insight {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 1.25rem;
    padding: 0.9rem 1.15rem;
    border-radius: var(--radius);
    background: var(--color-primary-soft);
    color: var(--color-text-primary);
    border: 1px solid var(--color-primary-border);
    box-shadow: var(--shadow-card);
  }
  .dashboard-insight__mark {
    flex: 0 0 auto;
    display: inline-flex; align-items: center; justify-content: center;
    width: 2rem; height: 2rem; border-radius: 999px;
    background: var(--color-card);
    color: var(--color-primary-700);
    border: 1px solid var(--color-primary-border);
    font-size: 0.66rem; font-weight: 700; letter-spacing: 0.06em;
  }
  .dashboard-insight__body { flex: 1 1 auto; min-width: 0; }
  .dashboard-insight__headline { margin: 0; font-weight: 600; font-size: 0.95rem; color: var(--color-text-primary); }
  .dashboard-insight__detail { margin: 0.2rem 0 0; font-size: 0.85rem; color: var(--color-gray-600); }
  .dashboard-insight__action {
    flex: 0 0 auto;
    padding: 0.5rem 0.9rem; border-radius: var(--radius-sm);
    background: var(--color-primary-action); color: #FFFFFF;
    border: none; font: inherit; font-weight: 700; font-size: 0.82rem; cursor: pointer;
    white-space: nowrap;
    transition: background 0.12s ease;
  }
  .dashboard-insight__action:hover { background: var(--color-primary-hover); }
  /* Tone accents on the left mark. */
  .dashboard-insight--attention .dashboard-insight__mark { color: var(--color-warning-text); border-color: var(--color-warning-border); }
  .dashboard-insight--critical .dashboard-insight__mark { color: var(--color-error-text); border-color: var(--color-error-border); background: var(--color-error-bg); }
  .dashboard-insight--ok .dashboard-insight__mark { color: var(--color-success-text); border-color: var(--color-success-border); background: var(--color-success-bg); }

  .dashboard-kpis { margin-top: 1.25rem; }

  /* ---- Charts row ------------------------------------------------------ */
  .dashboard-charts {
    display: grid;
    grid-template-columns: minmax(0, 2.2fr) minmax(0, 1fr);
    gap: 1.25rem;
    margin-top: 1.25rem;
  }
  .dashboard-charts .console-panel { margin-top: 0; }
  @media (max-width: 1000px) {
    .dashboard-charts { grid-template-columns: 1fr; }
  }
  .chart-frame { width: 100%; }

  /* Recharts tooltip (brand card). */
  .chart-tooltip {
    background: var(--color-gray-900);
    color: #FFFFFF;
    border: 1px solid var(--color-gray-800);
    border-radius: var(--radius-sm);
    padding: 0.5rem 0.7rem;
    box-shadow: 0 8px 24px rgba(16,24,40,0.28);
    font-size: 0.8rem;
  }
  .chart-tooltip__title { margin: 0 0 0.3rem; font-weight: 600; color: #FFFFFF; }
  .chart-tooltip__row { margin: 0.1rem 0; display: flex; align-items: center; gap: 0.4rem; }
  .chart-tooltip__swatch { width: 0.6rem; height: 0.6rem; border-radius: 2px; display: inline-block; }

  /* Donut + legend. */
  .chart-donut { display: flex; align-items: center; gap: 1.25rem; flex-wrap: wrap; }
  .chart-donut__chart { flex: 0 0 auto; }
  .chart-legend { list-style: none; margin: 0; padding: 0; flex: 1 1 8rem; min-width: 0; display: flex; flex-direction: column; gap: 0.5rem; }
  .chart-legend__item { display: flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; }
  .chart-legend__swatch { width: 0.7rem; height: 0.7rem; border-radius: 3px; flex: 0 0 auto; }
  .chart-legend__label { flex: 1 1 auto; color: var(--color-text); }
  .chart-legend__value { font-variant-numeric: tabular-nums; font-weight: 700; color: var(--color-forest); }

  /* ---- Two-column lower row (check-ins + properties) ------------------- */
  .dashboard-columns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
    margin-top: 0.25rem;
  }
  .dashboard-columns .console-panel { margin-top: 1.25rem; }
  @media (max-width: 1000px) { .dashboard-columns { grid-template-columns: 1fr; } }

  /* Upcoming check-ins. */
  .dashboard-checkins { list-style: none; margin: 0.5rem 0 0; padding: 0; display: flex; flex-direction: column; }
  .dashboard-checkin {
    display: flex; align-items: center; gap: 0.85rem;
    padding: 0.7rem 0; border-bottom: 1px solid var(--color-rule);
  }
  .dashboard-checkin:last-child { border-bottom: none; }
  .dashboard-checkin__date {
    flex: 0 0 auto; display: flex; flex-direction: column; align-items: center;
    width: 2.6rem; padding: 0.3rem 0; border-radius: var(--radius-sm);
    background: var(--color-surface-subtle); border: 1px solid var(--color-rule);
    line-height: 1;
  }
  .dashboard-checkin__month { font-size: 0.6rem; font-weight: 700; letter-spacing: 0.08em; color: var(--color-slate); }
  .dashboard-checkin__day { font-size: 1.05rem; font-weight: 700; color: var(--color-forest); margin-top: 0.15rem; font-variant-numeric: tabular-nums; }
  .dashboard-checkin__body { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
  .dashboard-checkin__guest { font-weight: 600; font-size: 0.9rem; color: var(--color-text); }
  .dashboard-checkin__meta { font-size: 0.78rem; color: var(--color-text-muted); }
  .dashboard-checkin__right { flex: 0 0 auto; display: flex; flex-direction: column; align-items: flex-end; gap: 0.15rem; }
  .dashboard-checkin__channel { font-size: 0.72rem; color: var(--color-slate); }
  .dashboard-checkin__amount { font-weight: 700; font-variant-numeric: tabular-nums; color: var(--color-forest); }

  /* Property performance. */
  .dashboard-props { list-style: none; margin: 0.5rem 0 0; padding: 0; display: flex; flex-direction: column; gap: 0.9rem; }
  .dashboard-prop__head { display: flex; justify-content: space-between; align-items: baseline; gap: 0.5rem; }
  .dashboard-prop__name { font-weight: 600; font-size: 0.9rem; color: var(--color-text); }
  .dashboard-prop__rev { font-weight: 700; font-variant-numeric: tabular-nums; color: var(--color-forest); }
  .dashboard-prop__meta { font-size: 0.76rem; color: var(--color-text-muted); }
  .dashboard-prop__track { display: block; height: 0.4rem; margin-top: 0.4rem; background: var(--color-gray-100); border-radius: 999px; overflow: hidden; }
  .dashboard-prop__bar { display: block; height: 100%; background: var(--color-primary-action); border-radius: 999px; }

  /* ---- New Booking modal ----------------------------------------------- */
  .console-modal-scrim {
    position: fixed; inset: 0; z-index: 100;
    background: rgba(16, 24, 40, 0.35);
    display: flex; align-items: flex-start; justify-content: center;
    padding: 3rem 1rem; overflow-y: auto;
  }
  .console-modal {
    width: 100%; max-width: 44rem;
    background: var(--color-card);
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    box-shadow: var(--shadow-lg);
    outline: none;
    animation: console-pop 0.14s ease;
  }
  .console-modal__header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 1rem 1.25rem; border-bottom: 1px solid var(--color-border);
    background: var(--color-card); color: var(--color-text-primary);
    border-radius: var(--radius) var(--radius) 0 0;
  }
  .console-modal__title { margin: 0; font-family: var(--font-display); font-weight: 600; font-size: 1.3rem; color: var(--color-text-primary); }
  .console-modal__close { width: 2rem; height: 2rem; }
  .console-modal__body { padding: 1.25rem; }
  .console-modal__actions { display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1rem; }

  @media (max-width: 900px) {
    .console-newbooking { padding: 0.5rem 0.7rem; }
    .console-modal-scrim { padding: 1rem; }
  }

  /* ---- Bookings register ----------------------------------------------- */
  .bookings-summary { margin-top: 1.25rem; }

  .bookings-filters--panel { background: var(--color-surface-subtle); border: 1px solid var(--color-border); border-radius: 10px; padding: 0.9rem 1rem; }
  .bookings-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.75rem;
    margin-top: 1.25rem;
    padding: 1rem 1.1rem;
    background: var(--color-surface-subtle);
    border: 1px solid var(--color-rule);
    border-radius: var(--radius);
  }
  .bookings-filters .console-field { flex: 0 0 auto; min-width: 9rem; gap: 0.3rem; }
  .bookings-filters__search { flex: 1 1 16rem; min-width: 12rem; }
  .bookings-filters__clear { align-self: flex-end; }

  .bookings-table tbody tr.bookings-row { cursor: pointer; }
  .bookings-table tbody tr.bookings-row:hover td { background: var(--color-surface-subtle); }
  .bookings-table tbody tr.bookings-row:focus-visible {
    outline: 2px solid var(--color-forest); outline-offset: -2px;
  }
    .bookings-guest {
    display: block; font-weight: 600; color: var(--color-primary-700); cursor: pointer;
  }
  .bookings-table tbody tr.bookings-row--selected td { background: var(--color-surface-selected); }
  .bookings-row:hover .bookings-guest,
  .bookings-guest:hover { color: var(--color-primary-800); text-decoration: underline; text-underline-offset: 2px; }
  .bookings-code {
    display: block; font-size: 0.72rem; color: var(--color-slate);
    font-variant-numeric: tabular-nums;
  }
  .bookings-prop { display: block; color: var(--color-text); }
  .bookings-unit { display: block; font-size: 0.76rem; color: var(--color-text-muted); }
  .bookings-dates { display: block; font-variant-numeric: tabular-nums; }
  .bookings-nights { display: block; font-size: 0.74rem; color: var(--color-text-muted); }
  .bookings-source { display: inline-flex; align-items: center; gap: 0.4rem; }

  /* Detail drawer (slides in from the right). */
  .bookings-drawer-scrim {
    position: fixed; inset: 0; z-index: 100;
    background: rgba(16, 24, 40, 0.4);
    display: flex; justify-content: flex-end;
  }
  .bookings-drawer {
    width: 100%; max-width: 26rem; height: 100%;
    background: var(--color-surface);
    box-shadow: -12px 0 40px rgba(16,24,40,0.25);
    display: flex; flex-direction: column;
    animation: bookings-slide 0.16s ease;
    overflow-y: auto;
  }
  @keyframes bookings-slide {
    from { transform: translateX(24px); opacity: 0.6; }
    to { transform: translateX(0); opacity: 1; }
  }
  .bookings-drawer__header {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 1.1rem 1.25rem; background: var(--color-card); color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border);
  }
  .bookings-drawer__eyebrow {
    margin: 0; font-size: 0.66rem; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--color-gray-500);
  }
  .bookings-drawer__title {
    margin: 0.2rem 0 0; font-family: var(--font-display); font-weight: 600;
    font-size: 1.3rem; color: var(--color-text-primary);
  }
  .bookings-drawer__close { color: var(--color-gray-500); width: 2rem; height: 2rem; }
  .bookings-drawer__body { padding: 1.25rem; }
  .bookings-drawer__pills { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 1rem; }
  .bookings-drawer__row {
    display: flex; justify-content: space-between; gap: 1rem;
    padding: 0.55rem 0; border-bottom: 1px solid var(--color-rule);
  }
  .bookings-drawer__row:last-child { border-bottom: none; }
  .bookings-drawer__label { color: var(--color-text-muted); font-size: 0.82rem; }
  .bookings-drawer__value {
    color: var(--color-text); font-weight: 600; text-align: right;
    font-variant-numeric: tabular-nums; overflow-wrap: anywhere;
  }

  /* Header row: title + view toggle. */
  .bookings-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
  .bookings-head__actions { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; margin-top: 0.4rem; }
  .bookings-viewtoggle {
    display: inline-flex; flex: 0 0 auto; margin-top: 0.4rem;
    background: var(--color-gray-100); border: 1px solid var(--color-border);
    border-radius: 999px; padding: 0.2rem;
  }
  .bookings-viewtoggle__btn {
    padding: 0.4rem 1rem; border: none; background: transparent; cursor: pointer;
    font: inherit; font-weight: 600; font-size: 0.82rem; color: var(--color-gray-500);
    border-radius: 999px; transition: background 0.12s ease, color 0.12s ease;
  }
  .bookings-viewtoggle__btn:hover { color: var(--color-gray-700); }
  .bookings-viewtoggle__btn--active { background: var(--color-primary-soft); color: var(--color-primary-700); }

  /* Pagination footer. */
  .bookings-pagination {
    display: flex; align-items: center; justify-content: space-between; gap: 1rem;
    flex-wrap: wrap; margin-top: 1rem; padding-top: 0.9rem; border-top: 1px solid var(--color-rule);
  }
  .bookings-pagination__info { color: var(--color-text-muted); font-size: 0.82rem; font-variant-numeric: tabular-nums; }
  .bookings-pagination__controls { display: flex; align-items: center; gap: 0.75rem; }
  .bookings-pagination__page { font-size: 0.82rem; color: var(--color-text); font-variant-numeric: tabular-nums; }

  /* Calendar nav. */
  .bookings-calnav { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 0.5rem; }
  .bookings-calnav__controls { display: flex; align-items: center; gap: 0.5rem; }
  .bookings-calnav__range { font-size: 0.85rem; font-weight: 600; color: var(--color-forest); margin-right: 0.35rem; font-variant-numeric: tabular-nums; }
  .bookings-calnav .console-btn { padding: 0.4rem 0.75rem; }

  /* ---- Calendar timeline ----------------------------------------------- */
  .bkcal { width: 100%; overflow-x: auto; }
  .bkcal__header, .bkcal__row { display: grid; align-items: stretch; }
  .bkcal__header { position: sticky; top: 0; }
  .bkcal__corner {
    font-size: 0.66rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--color-text-muted); padding: 0.5rem 0.75rem; display: flex; align-items: flex-end;
  }
  .bkcal__day {
    display: flex; flex-direction: column; align-items: center; justify-content: flex-end;
    padding: 0.4rem 0 0.5rem; border-left: 1px solid var(--color-rule);
  }
  .bkcal__daynum { font-size: 0.95rem; font-weight: 600; color: var(--color-text); font-variant-numeric: tabular-nums; }
  .bkcal__weekday { font-size: 0.62rem; letter-spacing: 0.06em; color: var(--color-text-muted); text-transform: uppercase; }
  .bkcal__day--today .bkcal__daynum { color: var(--color-primary-700); }
  .bkcal__day--today .bkcal__weekday { color: var(--color-primary-700); }

  .bkcal__row { border-top: 1px solid var(--color-rule); min-height: 3.1rem; }
  .bkcal__label {
    display: flex; align-items: center; padding: 0.5rem 0.75rem;
    font-family: var(--font-display); font-weight: 600; font-size: 1rem; color: var(--color-forest);
    border-right: 1px solid var(--color-rule);
  }
  .bkcal__track { position: relative; }
  .bkcal__cells { position: absolute; inset: 0; display: grid; }
  .bkcal__cell { border-left: 1px solid var(--color-rule); }
  .bkcal__cell--weekend { background: var(--color-surface-subtle); }
  .bkcal__cell--today { background: var(--color-primary-soft); }

  /* Booking bar (spans its nights). */
  .bkcal__bar {
    position: absolute; top: 0.4rem; bottom: 0.4rem;
    display: flex; align-items: center; gap: 0;
    padding: 0 0.55rem; margin-left: 2px;
    border: none; border-radius: 6px; cursor: pointer;
    color: #F5F4EF; font: inherit; font-size: 0.76rem; text-align: left;
    overflow: hidden; white-space: nowrap;
    box-shadow: 0 1px 2px rgba(16,24,40,0.18);
    transition: filter 0.12s ease, transform 0.12s ease;
  }
  .bkcal__bar:hover { filter: brightness(1.05); transform: translateY(-1px); }
  .bkcal__bar:focus-visible { outline: 2px solid var(--color-ink); outline-offset: 1px; }
  .bkcal__bar-guest { font-weight: 700; overflow: hidden; text-overflow: ellipsis; }
  .bkcal__bar-channel { opacity: 0.85; overflow: hidden; text-overflow: ellipsis; }
  /* Held bookings read as tentative: a dashed outline over the fill. */
  .bkcal__bar--held { box-shadow: inset 0 0 0 1.5px rgba(245,244,239,0.6); opacity: 0.9; }
  /* Bars clipped by the window edge lose the rounded corner on that side. */
  .bkcal__bar--clip-start { border-top-left-radius: 0; border-bottom-left-radius: 0; margin-left: 0; }
  .bkcal__bar--clip-end { border-top-right-radius: 0; border-bottom-right-radius: 0; }

  @media (max-width: 900px) {
    .bookings-filters .console-field { flex: 1 1 100%; min-width: 0; }
    .bkcal { min-width: 640px; }
  }
`;

/**
 * The complete shell stylesheet as a CSS string. Deterministic and pure.
 * Composed from the base scaffolding + console shell + module sections so the
 * whole cascade is one inlined <style> for SSR (no bundler, no CSS fetch).
 */
/** Channels (Channex OTA integration): catalogue + overview cards, readiness
 *  checklist, per-channel detail tabs, activity timeline, and the iframe modal.
 *  Follows the brand mapping (emerald/forest/gold/sage/clay/slate); gold is
 *  reserved for the single primary element per view (the Connect CTA on the
 *  empty state; the Bookings-this-month card in the overview). */
const CHANNELS_CSS = `
  /* ---- The one gold CTA (empty state, §07) ----------------------------- */
  .console-btn--gold {
    background: var(--color-primary-action);
    color: #FFFFFF;
    border-color: var(--color-primary-action);
    font-weight: 700;
  }
  .console-btn--gold:hover:not(:disabled) {
    background: var(--color-primary-hover);
    border-color: var(--color-primary-hover);
  }

  /* ---- Readiness checklist (gate) -------------------------------------- */
  .channel-checklist {
    list-style: none;
    margin: 1rem 0;
    padding: 0;
    display: grid;
    gap: 0.5rem;
  }
  .channel-checklist__item {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0.55rem 0.75rem;
    border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm);
    background: var(--color-card);
  }
  .channel-checklist__item--ok { border-color: var(--color-success-border); }
  .channel-checklist__icon {
    min-width: 1.5rem;
    text-align: center;
    font-weight: 700;
  }
  .channel-checklist__label { color: var(--color-text); }

  /* ---- Empty state ----------------------------------------------------- */
  .channel-empty .console-lede { margin-top: 0.5rem; }

  /* ---- Panel head (title + inline action) ------------------------------ */
  .channel-panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }

  /* ---- Catalogue + overview card grids --------------------------------- */
  .channel-catalogue,
  .channel-grid {
    grid-template-columns: repeat(auto-fill, minmax(19rem, 1fr));
    margin-top: 1rem;
  }
  .channel-card {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }
  .channel-card__head {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .channel-card__ident {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  .channel-card__name {
    margin: 0;
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.15rem;
    color: var(--color-forest);
  }
  .channel-card__desc {
    margin: 0;
    color: var(--color-text-muted);
    font-size: 0.88rem;
  }
  /* Branded glyph circle: forest chrome, one letter. */
  .channel-glyph {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: var(--color-surface-subtle);
    border: 1px solid var(--color-rule);
    color: var(--color-forest);
    font-family: var(--font-display);
    font-size: 1.2rem;
    line-height: 1;
    flex: none;
  }
  .channel-glyph--airbnb { background: #fdeef0; border-color: #f0c3c9; color: #b5405a; }
  .channel-glyph--booking_com { background: #eaf1fb; border-color: #c3d6f0; color: #234a86; }
  .channel-glyph--makemytrip { background: #fff2e6; border-color: #f0d3b0; color: #a85e14; }
  .channel-glyph--agoda { background: #eef0fb; border-color: #cbd0f0; color: #3b3f86; }
  .channel-glyph--expedia { background: #eef3f7; border-color: #c3d2df; color: #2b4a63; }

  .channel-card__stats {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem 1rem;
    margin: 0;
  }
  .channel-card__stat { margin: 0; }
  .channel-card__stat dt {
    margin: 0;
    color: var(--color-text-muted);
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .channel-card__stat dd {
    margin: 0.1rem 0 0;
    color: var(--color-text);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .channel-card__alert {
    margin: 0;
    padding: 0.5rem 0.65rem;
    border-radius: var(--radius-sm);
    font-size: 0.82rem;
  }
  .channel-card__alert--attention {
    background: var(--color-warning-bg);
    color: var(--color-warning-text);
    border: 1px solid var(--color-warning-border);
  }
  .channel-card__alert--critical {
    background: var(--color-error-bg);
    color: var(--color-clay);
    border: 1px solid var(--color-clay);
  }
  .channel-card__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: auto;
  }

  /* ---- Channel detail tabs --------------------------------------------- */
  .channel-detail__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin: 1rem 0 1.25rem;
    border-bottom: 1px solid var(--color-rule);
  }
  .channel-detail__tab {
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 0.55rem 0.85rem;
    font: inherit;
    font-weight: 600;
    font-size: 0.9rem;
    color: var(--color-text-muted);
    cursor: pointer;
  }
  .channel-detail__tab:hover { color: var(--color-forest); }
  .channel-detail__tab--active {
    color: var(--color-forest);
    border-bottom-color: var(--color-forest);
  }
  .channel-detail__tabpanel {
    display: grid;
    gap: 0.75rem;
    max-width: 40rem;
  }
  .channel-detail__pricing-figure dd {
    font-family: var(--font-display);
    font-size: 1.4rem;
    color: var(--color-primary-700);
  }

  /* ---- Activity timeline ----------------------------------------------- */
  .channel-timeline {
    list-style: none;
    margin: 0.5rem 0 0;
    padding: 0;
    display: grid;
    gap: 0.6rem;
  }
  .channel-timeline__item {
    border-left: 2px solid var(--color-rule);
    padding: 0 0 0 0.85rem;
  }
  .channel-timeline__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .channel-timeline__op { font-weight: 600; color: var(--color-text); }
  .channel-timeline__time {
    margin: 0.15rem 0 0;
    color: var(--color-text-muted);
    font-size: 0.8rem;
  }
  .channel-timeline__error {
    margin: 0.3rem 0 0;
    color: var(--color-clay);
    font-size: 0.82rem;
  }

  /* ---- Channex iframe modal -------------------------------------------- */
  .console-modal--iframe { max-width: 60rem; }
  .channex-iframe__note {
    margin: 0.25rem 0 0;
    color: var(--color-text-muted);
    font-size: 0.82rem;
  }
  .channex-iframe__body {
    padding: 0;
    height: min(70vh, 40rem);
  }
  .channex-iframe__frame {
    display: block;
    width: 100%;
    height: 100%;
    border: none;
  }
  .channex-iframe__actions {
    padding: 1rem 1.25rem;
    margin-top: 0;
    border-top: 1px solid var(--color-rule);
  }

  @media (max-width: 900px) {
    .channel-card__stats { grid-template-columns: 1fr; }
  }
`;

/** Reconciliation v2 (M4/M5 reshape): the auto-match money-flow banner, the
 *  variance callout bar, the state-count cards, and the transaction ledger
 *  additions (channel dot, negative commission, delta pill, toolbar). Follows
 *  the brand mapping (sage=matched, gold=attention/variance, slate=pending,
 *  clay=critical/unmatched). Gold is reserved for exactly ONE element here: the
 *  Bank-credit figure on the flow banner. */
const RECON2_CSS = `
  /* ---- Auto-match flow banner ------------------------------------------ */
  .recon-flow {
    margin-top: 1.5rem;
    border: 1px solid var(--color-rule);
    border-radius: var(--radius);
    background: var(--color-card);
    box-shadow: var(--shadow-card);
    padding: 1.35rem 1.5rem;
  }
  .recon-flow__head {
    display: flex; flex-wrap: wrap; align-items: baseline;
    justify-content: space-between; gap: 0.5rem 1rem;
  }
  .recon-flow__eyebrow {
    margin: 0; font-size: 0.7rem; font-weight: 600; letter-spacing: 0.16em;
    text-transform: uppercase; color: var(--color-text-muted);
  }
  .recon-flow__count {
    font-size: 0.78rem; font-weight: 600; color: var(--color-slate);
    font-variant-numeric: tabular-nums;
  }
  .recon-flow__strip {
    display: flex; flex-wrap: wrap; align-items: stretch; gap: 0.4rem;
    margin-top: 1.1rem;
  }
  .recon-flow__step { min-width: 0; flex: 1 1 8rem; }
  .recon-flow__label {
    margin: 0; font-size: 0.68rem; font-weight: 600; letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--color-text-muted);
  }
  .recon-flow__value {
    margin: 0.3rem 0 0; font-family: var(--font-display); font-weight: 600;
    font-size: clamp(1.15rem, 2vw, 1.6rem); color: var(--color-forest);
    line-height: 1.1; font-variant-numeric: tabular-nums;
  }
  /* Commission is a subtraction — clay, signed. */
  .recon-flow__value--minus { color: var(--color-clay); }
  /* THE ONE GOLD ELEMENT (§07): the bank credit — the number that settled. */
  .recon-flow__value--gold { color: var(--color-primary-700); }
  .recon-flow__hint { margin: 0.2rem 0 0; font-size: 0.72rem; color: var(--color-text-muted); }
  .recon-flow__arrow {
    align-self: center; color: var(--color-slate); font-size: 1.1rem;
    flex: 0 0 auto; padding: 0 0.15rem;
  }
  /* Variance callout bar. */
  .recon-callout {
    display: flex; align-items: center; justify-content: space-between;
    gap: 0.75rem 1rem; flex-wrap: wrap; margin-top: 1.25rem;
    padding: 0.7rem 1rem; border-radius: var(--radius-sm);
    border: 1px solid var(--color-rule); font-size: 0.9rem;
  }
  .recon-callout__amount { font-weight: 700; font-variant-numeric: tabular-nums; }
  .recon-callout--attention {
    border-color: var(--color-warning-border); background: var(--color-warning-bg);
    color: var(--color-warning-text);
  }
  .recon-callout--calm {
    border-color: var(--color-success-border); background: var(--color-success-bg);
    color: var(--color-success-text);
  }

  /* ---- State-count cards: state glyph tint ----------------------------- */
  .recon-statecard__head {
    display: flex; align-items: center; justify-content: space-between; gap: 0.5rem;
  }
  .recon-statecard__glyph {
    flex: 0 0 auto; width: 0.6rem; height: 0.6rem; border-radius: 999px;
    background: var(--color-slate);
  }
  .recon-statecard__glyph--ok { background: var(--color-success-text); }
  .recon-statecard__glyph--attention { background: var(--color-warning-text); }
  .recon-statecard__glyph--pending { background: var(--color-slate); }
  .recon-statecard__glyph--critical { background: var(--color-clay); }
  .recon-statecard__total { color: var(--color-text-muted); font-size: 1rem; font-variant-numeric: tabular-nums; }

  /* ---- Ledger toolbar (search + export) -------------------------------- */
  .recon-toolbar {
    display: flex; flex-wrap: wrap; align-items: flex-end; gap: 0.75rem;
    margin: 0.4rem 0 1rem;
  }
  .recon-toolbar__search { flex: 1 1 18rem; min-width: 12rem; }
  .recon-toolbar__spacer { flex: 1 1 auto; }
  .recon-panelhead {
    display: flex; align-items: baseline; justify-content: space-between;
    gap: 0.5rem 1rem; flex-wrap: wrap;
  }
  .recon-panelhead__actions { display: flex; gap: 0.6rem; align-items: center; flex-wrap: wrap; }

  /* ---- Ledger cell treatments ------------------------------------------ */
  .recon-channel { display: inline-flex; align-items: center; gap: 0.45rem; }
  .recon-channel__dot {
    flex: 0 0 auto; width: 0.55rem; height: 0.55rem; border-radius: 999px;
    display: inline-block;
  }
  .recon-comm { color: var(--color-clay); font-variant-numeric: tabular-nums; }
  .recon-status-delta { margin-left: 0.35rem; font-variant-numeric: tabular-nums; font-weight: 600; }
  .recon-note { color: var(--color-text-muted); font-size: 0.78rem; margin: 0.75rem 0 0; }

  @media (max-width: 900px) {
    .recon-flow__arrow { display: none; }
    .recon-flow__step { flex: 1 1 45%; }
  }
`;

/** Properties & Inventory (M1): the page-header CTA row, the property list
 *  channel chips + helper text, and the wide right-side property drawer with
 *  its two-tab strip. Mirrors the bookings-drawer slide-in but wider (40rem)
 *  for the multi-section create/edit form. No gold here — the "+ Add property"
 *  CTA uses the normal forest .console-btn; gold stays reserved elsewhere. */
const PROPERTY_DRAWER_CSS = `
  /* ---- Page header row: title block + primary CTA --------------------- */
  .prop-header-row {
    display: flex; align-items: center; justify-content: space-between;
    gap: 1rem; flex-wrap: wrap;
  }

  /* ---- List: connected-channel chips + helper text -------------------- */
  .prop-chip {
    display: inline-block; margin: 0 0.2rem 0.2rem 0;
    padding: 0.08rem 0.45rem; font-size: 0.7rem; font-weight: 600;
    background: var(--color-surface-subtle); border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm); color: var(--color-text);
    letter-spacing: 0.02em;
  }
  .prop-help {
    font-size: 0.78rem; color: var(--color-text-muted); margin-top: 0.25rem;
  }

  /* ---- The wide right-side drawer (scrim + sliding panel) -------------- */
  .prop-drawer-scrim {
    position: fixed; inset: 0; z-index: 100;
    background: rgba(16, 24, 40, 0.35);
    display: flex; justify-content: flex-end;
  }
  .prop-drawer {
    width: 100%; min-width: min(560px, 100vw); max-width: 40rem; height: 100dvh;
    background: var(--color-surface);
    box-shadow: -12px 0 40px rgba(16, 24, 40, 0.18);
    display: flex; flex-direction: column;
    animation: prop-drawer-slide 0.16s ease;
    overflow: hidden;
  }
  @keyframes prop-drawer-slide {
    from { transform: translateX(24px); opacity: 0.6; }
    to { transform: translateX(0); opacity: 1; }
  }
  /* Sticky header: stays put while the body scrolls beneath it. */
  .prop-drawer__header {
    flex: 0 0 auto;
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 1.1rem 1.9rem;
    background: var(--color-card); color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border);
  }
  .prop-drawer__eyebrow {
    margin: 0; font-size: 0.66rem; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--color-gray-500);
  }
  .prop-drawer__title {
    margin: 0.2rem 0 0; font-family: var(--font-display); font-weight: 600;
    font-size: 1.3rem; color: var(--color-text-primary);
  }
  .prop-drawer__close { color: var(--color-gray-500); width: 2rem; height: 2rem; }
  .prop-drawer__close:hover { background: var(--color-gray-100); }

  /* ---- Two-tab strip --------------------------------------------------- */
  .prop-drawer__tabs {
    display: flex; gap: 0.25rem;
    border-bottom: 1px solid var(--color-rule);
    padding: 0 1.9rem; flex: 0 0 auto;
  }
  .prop-drawer__tab {
    background: none; border: none; font: inherit; cursor: pointer;
    padding: 0.75rem 0.5rem; margin-bottom: -1px;
    color: var(--color-gray-500); font-weight: 600; font-size: 0.86rem;
    border-bottom: 2px solid transparent;
  }
  .prop-drawer__tab:hover { color: var(--color-gray-700); }
  .prop-drawer__tab--active {
    color: var(--color-text-primary);
    border-bottom-color: var(--color-primary-action);
  }
  /* Danger-zone tab + section (Feature A: Safe Property Removal). */
  .prop-drawer__tab--danger { color: var(--color-error-text); }
  .prop-drawer__tab--danger.prop-drawer__tab--active { border-bottom-color: var(--color-error-text); }
  .prop-danger__box {
    border: 1px solid var(--color-error-border); border-radius: 10px;
    background: var(--color-error-bg); padding: 1rem 1.1rem;
  }
  .prop-danger__title { color: var(--color-error-text); }
  .prop-danger__impact {
    margin: 0.5rem 0 0.75rem 1.1rem; padding: 0;
    color: var(--color-text-muted); font-size: 0.86rem;
  }
  .prop-danger__impact li { margin: 0.1rem 0; }
  .prop-danger__check {
    display: flex; align-items: flex-start; gap: 0.5rem;
    margin: 0.6rem 0; font-size: 0.86rem; color: var(--color-text-primary);
  }
  .prop-danger__confirm { margin-top: 0.75rem; }
  .console-btn--danger {
    margin-top: 0.9rem;
    background: #D92D20; border-color: #D92D20; color: #fff;
  }
  .console-btn--danger:hover:not(:disabled) { background: #B42318; border-color: #B42318; }
  .console-btn--danger:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ---- Property Retirement workflow (Danger Zone decision tree) -------- */
  .prop-danger__case { display: flex; flex-direction: column; }
  .prop-danger__retire-headline { font-weight: 600; color: var(--color-error-text); }
  .prop-danger__subtitle {
    margin: 0 0 0.4rem; color: var(--color-text-primary);
    font-family: var(--font-display); font-weight: 600; font-size: 0.98rem;
    display: flex; align-items: center; gap: 0.5rem;
  }
  .prop-danger__group-title {
    margin: 0.75rem 0 0.4rem; font-size: 0.85rem; font-weight: 700;
    color: var(--color-text-muted); text-transform: uppercase; letter-spacing: 0.03em;
  }
  .prop-danger__recommended {
    background: var(--color-primary-action); color: #fff; font-size: 0.7rem; font-weight: 700;
  }
  .prop-danger__options { display: flex; flex-direction: column; gap: 0.75rem; margin-top: 0.75rem; }
  .prop-danger__option {
    border: 1px solid var(--color-rule); border-radius: 10px;
    background: var(--color-surface); padding: 0.85rem 1rem;
  }
  .prop-danger__option--recommended { border-color: var(--color-primary-border); background: var(--color-primary-soft); }
  .prop-danger__res-list { list-style: none; margin: 0.5rem 0; padding: 0; }
  .prop-danger__res-row {
    display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
    padding: 0.5rem 0; border-bottom: 1px solid var(--color-rule);
    font-size: 0.86rem;
  }
  .prop-danger__res-row:last-child { border-bottom: none; }
  .prop-danger__res-row--ota { opacity: 0.9; }
  .prop-danger__res-text { display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; }
  .prop-danger__res-ref { color: var(--color-text-muted); }
  .prop-danger__res-status { font-size: 0.72rem; }
  .prop-danger__res-channel-note { margin: 0; white-space: nowrap; }
  .prop-danger__ota-guidance {
    background: var(--color-surface-subtle); border-radius: 8px; padding: 0.6rem 0.75rem;
    margin: 0.4rem 0;
  }
  .prop-danger__resolve-head,
  .prop-danger__resolve-group { margin-top: 0.75rem; }
  .prop-danger__resolve-head { display: flex; align-items: center; justify-content: space-between; }
  .prop-danger__confirm-bulk {
    margin-top: 0.6rem; padding: 0.6rem 0.75rem;
    background: var(--color-error-bg); border: 1px solid var(--color-error-border); border-radius: 8px;
  }
  .prop-danger__confirm-text { margin: 0 0 0.5rem; font-weight: 600; color: var(--color-error-text); }
  .prop-danger__btn-row { display: flex; flex-wrap: wrap; gap: 0.6rem; align-items: center; }
  .prop-danger__scheduled,
  .prop-danger__schedule,
  .prop-danger__support { display: flex; flex-direction: column; gap: 0.5rem; }
  .prop-lifecycle-tabs { margin: 0.75rem 0 1rem; }

  /* ---- Archived (decommissioned) properties — read-only views --------- */
  .prop-archived__badge { background: var(--color-text-muted); color: #fff; font-weight: 700; }
  .prop-archived__detail { display: flex; flex-direction: column; gap: 0.9rem; }
  .prop-archived__detail-head { display: flex; align-items: center; gap: 0.75rem; }
  .prop-archived__meta {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
    gap: 0.75rem; margin: 0;
  }
  .prop-archived__meta dt { font-size: 0.78rem; color: var(--color-text-muted); font-weight: 700; }
  .prop-archived__meta dd { margin: 0.2rem 0 0; }
  .prop-archived__section { margin-top: 0.5rem; }
  .prop-archived__units,
  .prop-archived__guests { list-style: none; margin: 0; padding: 0; }
  .prop-archived__units li,
  .prop-archived__guests li { padding: 0.3rem 0; border-bottom: 1px solid var(--color-rule); }
  .prop-archived__units li:last-child,
  .prop-archived__guests li:last-child { border-bottom: none; }

  /* ---- Internally-scrolling body + section rhythm --------------------- */
  .prop-drawer__body {
    flex: 1 1 auto; min-height: 0; overflow-y: auto;
    padding: 1.9rem;
  }
  /* Sections read as whitespace + dividers, not nested cards. */
  .prop-drawer__section { padding-bottom: 2rem; margin-bottom: 2rem; border-bottom: 1px solid var(--color-rule); }
  .prop-drawer__section:last-child { padding-bottom: 0; margin-bottom: 0; border-bottom: none; }
  .prop-drawer__section-title {
    margin: 0 0 1.1rem; color: var(--color-forest);
    font-family: var(--font-display); font-weight: 600; font-size: 1.1rem;
  }
  /* Inside the drawer, neutralise the .console-form card look (linen bg +
     border + padding) so sections are dividers + whitespace, NOT nested
     cards. Scoped to .prop-drawer so other screens keep their form card. */
  .prop-drawer .console-form {
    background: transparent; border: none; padding: 0; margin: 0;
  }
  .prop-drawer .console-form__row { gap: 1rem; margin-bottom: 1rem; }
  .prop-drawer .console-field { gap: 0.4rem; }
  .prop-drawer .console-field input:not([type="checkbox"]):not([type="radio"]),
  .prop-drawer .console-field select,
  .prop-drawer .console-field textarea { padding: 0.6rem 0.7rem; min-height: 42px; }
  .prop-drawer .console-field textarea { min-height: auto; }
  .prop-drawer .prop-help { margin-top: 0.4rem; }
  /* Long fields span the full row (single column). */
  .console-field--full { flex: 1 1 100%; width: 100%; }

  /* ---- Pass A: honest, disabled Content section ----------------------- */
  .prop-content-section .console-field input:disabled,
  .prop-content-section .console-field textarea:disabled {
    background: var(--color-surface-subtle); color: var(--color-text-muted);
    cursor: not-allowed; border-style: dashed; resize: none;
  }
  .prop-content-upload {
    display: flex; align-items: center; gap: 0.75rem;
    padding: 0.9rem 1rem; border: 1px dashed var(--color-rule);
    border-radius: var(--radius-sm); background: var(--color-surface-subtle);
    color: var(--color-text-muted);
  }
  .prop-content-upload__glyph {
    font-size: 1.3rem; line-height: 1; opacity: 0.7;
  }
  .prop-content-upload .console-btn:disabled { opacity: 0.6; cursor: not-allowed; }

  /* ---- Sticky footer: separated with a top border + slight shadow ----- */
  .prop-drawer__footer {
    flex: 0 0 auto; position: sticky; bottom: 0; z-index: 1;
    display: flex; align-items: center; justify-content: flex-end;
    gap: 0.65rem; padding: 1rem 1.9rem;
    border-top: 1px solid var(--color-border);
    background: var(--color-card);
    box-shadow: 0 -6px 16px rgba(16, 24, 40, 0.06);
  }
  .prop-drawer__footer-spacer { margin-right: auto; }

  /* Mobile: full-screen sheet; fields collapse to one column (row wraps). */
  @media (max-width: 720px) {
    .prop-drawer { min-width: 100%; max-width: 100%; }
    .prop-drawer__header, .prop-drawer__body, .prop-drawer__footer { padding-left: 1.15rem; padding-right: 1.15rem; }
  }

  /* ---- Top summary cards (Total Properties / Units / Ready / Setup) ---- */
  .prop-summary-cards {
    grid-template-columns: repeat(4, minmax(9.5rem, 1fr));
  }
  @media (max-width: 900px) {
    .prop-summary-cards { grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr)); }
  }

  /* ---- Card view: view toggle (Card | Table) -------------------------- */
  .prop-viewtoggle {
    display: inline-flex; flex: 0 0 auto; height: 36px; align-items: center;
    background: #F3F4F6; border: 1px solid var(--color-border);
    border-radius: 8px; padding: 3px;
  }
  .prop-viewtoggle__btn {
    padding: 0 14px; height: 28px; border: 1px solid transparent; background: transparent; cursor: pointer;
    font: inherit; font-weight: 500; font-size: 0.8125rem; color: var(--color-gray-500);
    border-radius: 6px; transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
  }
  .prop-viewtoggle__btn:hover { color: var(--color-gray-700); }
  .prop-viewtoggle__btn--active {
    background: var(--color-card); color: var(--color-text-primary);
    border-color: var(--color-border); box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  }

  /* ---- Responsive card grid: 3 → 2 → 1 columns ------------------------ */
  .prop-cardgrid {
    display: grid; gap: 1rem; margin-top: 0.5rem;
    grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
  }
  @media (max-width: 1100px) {
    .prop-cardgrid { grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr)); }
  }
  @media (max-width: 620px) {
    .prop-cardgrid { grid-template-columns: 1fr; }
  }

  /* ---- Property card --------------------------------------------------- */
  .prop-card { display: flex; flex-direction: column; gap: 0.85rem; }
  .prop-card--selected {
    border-color: var(--color-primary-action);
    box-shadow: 0 0 0 1px var(--color-primary-action), var(--shadow-card);
  }
  .prop-card__top { display: flex; align-items: flex-start; gap: 0.85rem; }
  /* Lightweight initial/illustration tile — UI-only, no image backend. */
  .prop-card__thumb {
    flex: 0 0 auto; width: 3rem; height: 3rem; border-radius: var(--radius-sm);
    display: flex; align-items: center; justify-content: center;
    background: var(--color-surface-subtle); border: 1px solid var(--color-rule);
  }
  .prop-card__glyph { font-size: 1.5rem; line-height: 1; }
  .prop-card__ident { min-width: 0; display: flex; flex-direction: column; gap: 0.15rem; }
  .prop-card__name {
    margin: 0; font-family: var(--font-display); font-weight: 600;
    font-size: 1.15rem; color: var(--color-forest); line-height: 1.15;
    overflow-wrap: anywhere;
  }
  .prop-card__location { margin: 0; font-size: 0.82rem; color: var(--color-text-muted); }
  .prop-card__typebadge {
    align-self: flex-start; margin-top: 0.15rem;
    padding: 0.05rem 0.5rem; border-radius: 999px;
    font-size: 0.66rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
    background: var(--color-surface-subtle); border: 1px solid var(--color-rule); color: var(--color-slate);
  }

  .prop-card__meta {
    display: flex; gap: 1.25rem; margin: 0;
    padding: 0.6rem 0; border-top: 1px solid var(--color-rule); border-bottom: 1px solid var(--color-rule);
  }
  .prop-card__meta-item { display: flex; flex-direction: column; gap: 0.1rem; margin: 0; }
  .prop-card__meta-item dt {
    margin: 0; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.1em;
    text-transform: uppercase; color: var(--color-text-muted);
  }
  .prop-card__meta-item dd {
    margin: 0; font-weight: 700; color: var(--color-text);
    font-variant-numeric: tabular-nums;
  }

  .prop-card__channels { display: flex; flex-direction: column; gap: 0.3rem; }
  .prop-card__channels-label {
    font-size: 0.62rem; font-weight: 700; letter-spacing: 0.1em;
    text-transform: uppercase; color: var(--color-text-muted);
  }
  .prop-card__chips { display: flex; flex-wrap: wrap; }
  .prop-card__channels-none { font-size: 0.82rem; color: var(--color-text-muted); }

  /* Setup-required needs: compact readable checklist. */
  .prop-card__needs {
    padding: 0.55rem 0.65rem; border-radius: var(--radius-sm);
    background: var(--color-warning-bg); border: 1px solid var(--color-warning-border);
  }
  .prop-card__needs-label {
    display: block; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--color-warning-text); margin-bottom: 0.25rem;
  }
  .prop-card__needs-list {
    list-style: none; margin: 0; padding: 0;
    display: flex; flex-wrap: wrap; gap: 0.3rem 0.6rem;
  }
  .prop-card__needs-list li {
    position: relative; padding-left: 0.85rem; font-size: 0.8rem; color: var(--color-text);
  }
  .prop-card__needs-list li::before {
    content: "○"; position: absolute; left: 0; color: var(--color-warning-text); font-size: 0.7rem; top: 0.05rem;
  }

  .prop-card__actions {
    display: flex; gap: 0.5rem; margin-top: auto; padding-top: 0.25rem;
  }
  .prop-card__actions .console-btn { flex: 1 1 auto; text-align: center; }

  /* ---- Units inventory: list header, table, expand rows, action menu --- */
  .units-head {
    display: flex; align-items: flex-start; justify-content: space-between;
    gap: 1rem; flex-wrap: wrap; margin-bottom: 0.35rem;
  }
  .units-head .console-panel__title { margin: 0; }
  .units-head__actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }

  .units-filters { display: flex; gap: 0.75rem; flex-wrap: wrap; margin: 0.75rem 0; }
  .units-filters .console-field { margin: 0; min-width: 12rem; }

  /* The Add / Manage drawer reuses .prop-drawer but sits at 520–620px wide. */
  .units-drawer { max-width: 38rem; }
  .units-drawer__form { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }

  /* Expand chevron in the first table cell. */
  .units-table__chevcol { width: 2.25rem; text-align: center; }
  .units-chevron {
    display: inline-flex; align-items: center; justify-content: center;
    background: none; border: none; cursor: pointer; font: inherit;
    color: var(--color-text-muted); padding: 0.25rem; border-radius: var(--radius-sm);
    line-height: 1;
  }
  .units-chevron svg { transition: transform 0.18s ease; }
  .units-chevron[aria-expanded="true"] svg { transform: rotate(90deg); }
  .units-chevron:hover { color: var(--color-gray-700); background: var(--color-gray-100); }

  /* Rate-plan cell: a plan name + its price, stacked compactly. */
  .units-table__rateplan { white-space: nowrap; }
  .units-rateplan__name { display: block; font-size: 0.78rem; color: var(--color-text-muted); }
  .units-rateplan__price { display: block; font-weight: 700; font-variant-numeric: tabular-nums; }

  /* Expanded detail row: compact label/value mini-lines. */
  .units-detail-row > td { background: var(--color-surface-subtle); }
  .units-detail { display: flex; flex-direction: column; gap: 0.35rem; padding: 0.35rem 0; }
  .units-detail__line { display: flex; gap: 0.75rem; font-size: 0.82rem; }
  .units-detail__label {
    flex: 0 0 6.5rem; font-weight: 700; letter-spacing: 0.04em;
    text-transform: uppercase; font-size: 0.66rem; color: var(--color-text-muted);
    padding-top: 0.1rem;
  }
  .units-detail__value { color: var(--color-text); overflow-wrap: anywhere; }

  /* Row actions: Manage button + overflow menu. */
  .units-actions { display: flex; align-items: center; gap: 0.4rem; }
  .units-fixsync { margin-left: 0.5rem; padding: 0.1rem 0.5rem; font-size: 0.72rem; }

  .units-menu { position: relative; }
  .units-menu__trigger {
    list-style: none; padding: 0.25rem 0.6rem; line-height: 1; cursor: pointer;
  }
  .units-menu__trigger::-webkit-details-marker { display: none; }
  .units-menu__list {
    position: absolute; right: 0; top: calc(100% + 0.25rem); z-index: 20;
    min-width: 11rem; display: flex; flex-direction: column;
    background: var(--color-surface); border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm); box-shadow: var(--shadow-card); padding: 0.25rem;
  }
  .units-menu__list button {
    text-align: left; background: none; border: none; font: inherit; cursor: pointer;
    padding: 0.45rem 0.6rem; border-radius: var(--radius-sm); color: var(--color-text);
  }
  .units-menu__list button:hover { background: var(--color-surface-subtle); }
  .units-menu__danger { color: #D92D20; }
  .units-menu__danger:hover { background: var(--color-error-bg); }

  /* Integration tab: technical-detail disclosure. */
  .units-tech { margin: 0.5rem 0; }
  .units-tech > summary { cursor: pointer; font-size: 0.82rem; color: var(--color-forest); }

  /* ---- Pass A: real horizontal stepper (2-step create wizard) --------- */
  .prop-stepper {
    display: flex; align-items: center; list-style: none;
    margin: 0; padding: 1.1rem 1.9rem; flex: 0 0 auto;
    border-bottom: 1px solid var(--color-rule); background: var(--color-surface);
  }
  .prop-stepper__step {
    display: flex; align-items: center; gap: 0.55rem; flex: 0 0 auto;
    font-size: 0.86rem; font-weight: 600; color: var(--color-text-muted);
  }
  /* Connector line between the two steps. */
  .prop-stepper__step + .prop-stepper__step::before {
    content: ""; flex: 1 1 2rem; min-width: 1.5rem; height: 2px;
    margin: 0 0.9rem; background: var(--color-rule); border-radius: 2px;
  }
  .prop-stepper__circle {
    display: inline-flex; align-items: center; justify-content: center;
    width: 1.7rem; height: 1.7rem; border-radius: 999px; flex: 0 0 auto;
    font-size: 0.8rem; font-weight: 700; line-height: 1;
    border: 2px solid var(--color-rule); background: var(--color-surface);
    color: var(--color-text-muted);
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  }
  .prop-stepper__label { white-space: nowrap; }
  /* Current: filled primary circle + active label. */
  .prop-stepper__step--current { color: var(--color-primary-700); }
  .prop-stepper__step--current .prop-stepper__circle {
    background: var(--color-primary-action); border-color: var(--color-primary-action); color: #fff;
  }
  /* Completed: check in a filled primary circle. */
  .prop-stepper__step--done { color: var(--color-primary-700); }
  .prop-stepper__step--done .prop-stepper__circle {
    background: var(--color-primary-600); border-color: var(--color-primary-600); color: #fff;
  }
  /* Upcoming: neutral outline circle + muted label (default). */
  .prop-stepper__step--upcoming { color: var(--color-text-muted); }
  @media (max-width: 560px) {
    .prop-stepper__step { flex-direction: column; gap: 0.3rem; text-align: center; }
    .prop-stepper__label { font-size: 0.78rem; }
  }

  /* ---- Stage 3: compact, truthful edit save-result line --------------- */
  .prop-drawer__save-result {
    display: flex; align-items: center; flex-wrap: wrap; gap: 0.5rem;
    margin: 0 0 1rem; padding: 0.5rem 0.75rem; border-radius: var(--radius-sm);
    font-size: 0.86rem; font-weight: 600;
  }
  .prop-drawer__save-result--ok {
    background: var(--color-success-bg); border: 1px solid var(--color-success-border); color: var(--color-success-text);
  }
  .prop-drawer__save-result--info {
    background: var(--color-info-bg); border: 1px solid var(--color-info-border); color: var(--color-info-text);
  }
  .prop-drawer__save-result--critical {
    background: var(--color-error-bg); border: 1px solid var(--color-error-border); color: var(--color-error-text);
  }
  .prop-drawer__save-result-retry { margin-left: 0.25rem; padding: 0.1rem 0.6rem; font-size: 0.78rem; }

  /* ---- Stage 3 / Pass A: flat hierarchical property list -------------- */
  /* ONE surface: a single bordered container; property rows separated by
     subtle horizontal dividers — no per-row card border, no card-in-card for
     the expanded rooms. */
  .prop-panel-list {
    margin-top: 0.75rem;
    border: 1px solid var(--color-rule);
    border-radius: var(--radius);
    background: var(--color-surface);
    overflow: hidden;
  }
  .prop-panel { position: relative; border-bottom: 1px solid var(--color-rule); }
  .prop-panel:last-child { border-bottom: none; }
  /* Selected/expanded: subtle tint + a 3px primary left accent bar (no ring). */
  .prop-panel--expanded { background: #F5F6FF; }
  .prop-panel--expanded::before {
    content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 2px;
    background: var(--color-primary-action);
  }

  .prop-panel__head {
    display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;
  }
  /* The full-width clickable row (expand/collapse). */
  .prop-panel__row {
    flex: 1 1 22rem; min-width: 0;
    display: flex; align-items: center; gap: 0.85rem;
    background: none; border: none; cursor: pointer; font: inherit;
    text-align: left; padding: 1rem 1.1rem; min-height: 72px;
    transition: background 0.12s ease;
  }
  .prop-panel__row:hover { background: var(--color-surface-subtle); }
  .prop-panel--expanded .prop-panel__row:hover { background: transparent; }
  .prop-panel__chevron {
    flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
    width: 1.3rem; height: 1.3rem; color: var(--color-text-muted);
    transition: transform 0.18s ease, color 0.12s ease;
  }
  .prop-panel__row:hover .prop-panel__chevron { color: var(--color-primary-700); }
  .prop-panel--expanded .prop-panel__chevron { transform: rotate(90deg); color: var(--color-primary-700); }
  .prop-panel__glyph {
    flex: 0 0 auto; width: 2.5rem; height: 2.5rem; border-radius: var(--radius-sm);
    display: flex; align-items: center; justify-content: center; font-size: 1.3rem;
    background: var(--color-surface-subtle); border: 1px solid var(--color-rule);
  }
  .prop-panel__ident-text { display: flex; flex-direction: column; gap: 0.25rem; min-width: 0; flex: 1 1 auto; }
  .prop-panel__name {
    font-family: var(--font-body); font-weight: 600; font-size: 1.1rem;
    color: var(--color-forest); line-height: 1.4; overflow-wrap: anywhere;
  }
  .prop-panel__row:hover .prop-panel__name { text-decoration: underline; text-underline-offset: 2px; }
  .prop-panel__location { font-size: 0.82rem; color: var(--color-text-muted); overflow-wrap: anywhere; }
  /* Metadata cluster: status chip · N rooms · channels — real gaps, no run-on. */
  .prop-panel__meta {
    flex: 0 0 auto; display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap;
    margin-left: 1rem;
  }
  .prop-panel__dot { color: var(--color-rule); font-weight: 700; user-select: none; }
  .prop-panel__location .prop-panel__dot { margin: 0 0.4rem; }
  .prop-panel__units { font-size: 0.82rem; font-weight: 700; color: var(--color-text); font-variant-numeric: tabular-nums; }
  .prop-panel__chips { display: flex; flex-wrap: wrap; gap: 0.25rem; }
  .prop-panel__chips--none { font-size: 0.8rem; color: var(--color-text-muted); }

  /* Right-aligned actions (not nested in the row button). */
  .prop-panel__actions {
    flex: 0 0 auto; display: flex; align-items: center; gap: 0.4rem;
    padding: 0 1.1rem 0 0;
  }
  .prop-panel__action { white-space: nowrap; }
  .prop-panel__overflow { position: relative; }
  .prop-panel__overflow-trigger {
    padding: 0.3rem 0.6rem; line-height: 1; font-size: 1.1rem;
  }
  .prop-panel__overflow-backdrop { position: fixed; inset: 0; z-index: 19; }
  .prop-panel__overflow-menu {
    position: absolute; right: 0; top: calc(100% + 0.3rem); z-index: 20;
    min-width: 12rem; display: flex; flex-direction: column;
    background: var(--color-surface); border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm); box-shadow: var(--shadow-card); padding: 0.25rem;
  }
  .prop-panel__overflow-menu button {
    text-align: left; background: none; border: none; font: inherit; cursor: pointer;
    padding: 0.5rem 0.65rem; border-radius: var(--radius-sm); color: var(--color-text);
  }
  .prop-panel__overflow-menu button:hover:not(:disabled) { background: var(--color-surface-subtle); }
  .prop-panel__overflow-disabled { color: var(--color-text-muted); cursor: not-allowed; }

  /* Expanded rooms belong to the parent via indent + subtle tint, not a box. */
  .prop-panel__body {
    background: #FCFCFD;
    border-top: 1px solid var(--color-rule);
    padding: 0.5rem 1.25rem 1.25rem 3.25rem;
  }
  /* The nested room table already has its own panel chrome; flatten it here. */
  .prop-panel__body .console-panel {
    background: transparent; box-shadow: none; border: none; padding: 0.25rem 0 0;
  }
  @media (max-width: 820px) {
    .prop-panel__head { align-items: stretch; }
    .prop-panel__meta { margin-left: 0; width: 100%; padding: 0 1.1rem 0.5rem 3.1rem; }
    .prop-panel__row { flex: 1 1 100%; padding-bottom: 0.5rem; }
    .prop-panel__actions { width: 100%; padding: 0 1.1rem 1rem 3.1rem; flex-wrap: wrap; }
    .prop-panel__body { padding-left: 1.1rem; }
  }

  /* =====================================================================
     PASS C — production visual recompose (UI/UX only).
     A single 4/8/12/16/20/24/32/40/48px spacing scale, exact type sizes,
     SVG-icon sizing, and layout polish. These rules are layered LAST so they
     take precedence over the earlier declarations without deleting them, and
     are scoped to the Properties & Inventory screen + its drawers only.
     ===================================================================== */
  [data-console-screen="properties"] {
    --sp-1: 4px; --sp-2: 8px; --sp-3: 12px; --sp-4: 16px;
    --sp-5: 20px; --sp-6: 24px; --sp-8: 32px; --sp-10: 40px; --sp-12: 48px;
  }

  /* ---- Inline SVG icons: consistent sizing, never shrink -------------- */
  [data-console-screen="properties"] svg,
  .prop-drawer svg { flex: 0 0 auto; }

  /* ---- Page header (eyebrow / title / description / CTA) -------------- */
  [data-console-screen="properties"] > .prop-header-row {
    align-items: flex-start; gap: 16px; margin-bottom: 20px;
  }
  [data-console-screen="properties"] .console-page-eyebrow {
    font-size: 12px; letter-spacing: 0.12em; font-weight: 500; margin: 0;
  }
  [data-console-screen="properties"] #properties-heading {
    font-family: var(--font-display); font-weight: 600;
    font-size: 32px; line-height: 38px; color: var(--color-forest);
    margin: 10px 0 0;
  }
  [data-console-screen="properties"] .prop-header-row .console-lede {
    font-size: 15px; line-height: 22px; margin: 10px 0 0;
    color: var(--color-text-muted);
  }
  .prop-header-row__cta {
    display: inline-flex; align-items: center; gap: 8px;
    height: 40px; padding: 0 18px; border-radius: 8px;
    font-size: 14px; font-weight: 600;
  }

  /* ---- Active / Archived segmented control ---------------------------- */
  .prop-lifecycle-tabs.prop-viewtoggle {
    height: 36px; padding: 3px; border-radius: 18px;
    background: var(--color-gray-100); border: 1px solid var(--color-border);
    margin: 0 0 20px;
  }
  .prop-lifecycle-tabs .prop-viewtoggle__btn {
    height: 28px; padding: 0 14px; border-radius: 14px;
    font-size: 13px; display: inline-flex; align-items: center;
  }
  .prop-lifecycle-tabs .prop-viewtoggle__btn--active {
    background: var(--color-primary-soft); color: var(--color-primary-700);
  }

  /* ---- KPI summary cards ---------------------------------------------- */
  [data-console-screen="properties"] .prop-summary-cards {
    gap: 16px; margin-top: 0; margin-bottom: 20px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  [data-console-screen="properties"] .prop-summary-card {
    min-height: 108px; padding: 18px 20px; border-radius: 12px;
    box-shadow: none; display: flex; flex-direction: column; justify-content: center;
  }
  [data-console-screen="properties"] .prop-summary-card.console-card--gold {
    box-shadow: var(--shadow-card);
  }
  [data-console-screen="properties"] .prop-summary-card .console-card__label {
    font-size: 11px;
  }
  [data-console-screen="properties"] .prop-summary-card .console-card__value {
    font-size: 30px; margin-top: 10px;
  }
  [data-console-screen="properties"] .prop-summary-card .console-card__hint {
    font-size: 12px; margin-top: 8px;
  }
  @media (max-width: 1100px) {
    [data-console-screen="properties"] .prop-summary-cards {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  /* ---- Properties section container ----------------------------------- */
  [data-console-screen="properties"] .console-panel[aria-label="Properties"] {
    margin-top: 0; padding: 0; border-radius: 12px; box-shadow: none;
    overflow: hidden;
  }
  [data-console-screen="properties"] .console-panel[aria-label="Properties"]
    > .accounts-panel-head {
    padding: 22px 24px 16px; margin: 0;
  }
  [data-console-screen="properties"] .console-panel[aria-label="Properties"]
    .console-panel__title {
    font-size: 23px; margin: 0;
  }
  /* The row list is flat inside the one container — no inner border/radius. */
  [data-console-screen="properties"] .prop-panel-list {
    margin-top: 0; border: none; border-top: 1px solid var(--color-rule);
    border-radius: 0; overflow: visible;
  }

  /* ---- Property row (collapsed) --------------------------------------- */
  .prop-panel__head { gap: 12px; }
  .prop-panel__row {
    min-height: 84px; padding: 18px 20px; gap: 16px; align-items: center;
  }
  /* Caret: 32x32 hit area, 6px radius, hover bg, rotation on expand. */
  .prop-panel__chevron {
    width: 32px; height: 32px; border-radius: 6px;
    transition: transform 0.18s ease, background 0.12s ease, color 0.12s ease;
  }
  .prop-panel__row:hover .prop-panel__chevron { background: var(--color-gray-100); }
  /* Neutral SVG building glyph tile — muted, no emoji. */
  .prop-panel__glyph {
    width: 40px; height: 40px; border-radius: 8px;
    color: var(--color-text-muted);
  }
  .prop-panel__glyph svg { width: 18px; height: 18px; }
  /* Identity: name 17px/24, location 13px/18 with a 4px line gap. */
  .prop-panel__ident-text { gap: 4px; }
  .prop-panel__name {
    font-family: var(--font-body); font-weight: 600;
    font-size: 17px; line-height: 24px; color: var(--color-forest);
  }
  .prop-panel__location {
    display: flex; align-items: center; flex-wrap: wrap;
    font-size: 13px; line-height: 18px;
  }
  .prop-panel__location .prop-panel__dot { margin: 0 6px; }
  /* Metadata cluster: flex row, 12px gaps, middot spaced — never a run-on. */
  .prop-panel__meta { gap: 12px; margin-left: 16px; }
  .prop-panel__meta .prop-panel__dot { margin: 0 2px; }
  .prop-panel__units { font-size: 13px; }
  .prop-panel__chips--none { font-size: 13px; }
  /* Status chip: consistent 24px pill. */
  [data-console-screen="properties"] .prop-panel__meta .avos-status {
    height: 24px; display: inline-flex; align-items: center;
    padding: 0 9px; border-radius: 12px; font-size: 11px; line-height: 1;
  }
  /* Actions: one right-aligned row, 36px controls, no native borders. */
  .prop-panel__actions { gap: 8px; padding-right: 20px; }
  .prop-panel__action {
    height: 36px; display: inline-flex; align-items: center; gap: 6px;
    padding: 0 12px; border-radius: 7px; font-size: 13px;
    border: 1px solid var(--color-rule);
  }
  .prop-panel__action svg { width: 14px; height: 14px; }
  .prop-panel__action--add { color: var(--color-forest); }
  .prop-panel__overflow-trigger {
    width: 36px; height: 36px; padding: 0; border-radius: 7px;
    display: inline-flex; align-items: center; justify-content: center;
    border: 1px solid var(--color-rule);
  }
  /* Expanded: subtle tint already set; keep the 3px primary left accent bar. */
  .prop-panel--expanded .prop-panel__chevron { background: var(--color-gray-100); }

  /* ---- Expanded rooms region (indented, no card) ---------------------- */
  .prop-panel__body { padding: 4px 20px 20px 54px; border-top: none; }

  /* ---- Drawer frame --------------------------------------------------- */
  .prop-drawer {
    width: clamp(580px, 42vw, 660px); min-width: 0; max-width: none;
  }
  .prop-drawer-scrim { background: rgba(16, 24, 40, 0.32); }
  .prop-drawer__header {
    min-height: 80px; padding: 18px 24px; align-items: center;
  }
  .prop-drawer__eyebrow { font-size: 11px; letter-spacing: 0.12em; }
  .prop-drawer__title { font-size: 24px; margin-top: 4px; }
  .prop-drawer__close {
    width: 40px; height: 40px; border-radius: 8px;
    border: 1px solid var(--color-border);
    display: inline-flex; align-items: center; justify-content: center;
  }
  .prop-drawer__close svg { width: 18px; height: 18px; }

  /* ---- Drawer tabs: underline, 48px bar ------------------------------- */
  .prop-drawer__tabs { padding: 0 24px; gap: 4px; }
  .prop-drawer__tab { height: 48px; padding: 0 4px; font-size: 13px; }

  /* ---- Drawer body + form grid ---------------------------------------- */
  .prop-drawer__body { padding: 28px 28px 36px; }
  .prop-drawer__section { padding-bottom: 24px; margin-bottom: 24px; }
  .prop-drawer__section-title { font-size: 18px; margin-bottom: 16px; }
  .prop-drawer .console-form__row { gap: 16px 16px; margin-bottom: 18px; }
  .prop-drawer .console-field label { font-size: 12px; font-weight: 600; margin-bottom: 6px; color: var(--color-text); }
  .prop-drawer .console-field input:not([type="checkbox"]):not([type="radio"]),
  .prop-drawer .console-field select,
  .prop-drawer .console-field textarea {
    min-height: 42px; border-radius: 8px; font-size: 14px;
  }
  .prop-drawer .console-field textarea { min-height: 96px; }
  .prop-drawer .console-field input:focus,
  .prop-drawer .console-field select:focus,
  .prop-drawer .console-field textarea:focus {
    border-color: var(--color-primary-action);
    box-shadow: var(--focus-ring);
  }
  .prop-drawer .prop-help { font-size: 11px; line-height: 17px; margin-top: 5px; }

  /* Read-only billing field: a distinct non-editable affordance. */
  .prop-drawer #pd-billing {
    background: var(--color-surface-subtle); color: var(--color-text-muted);
    border-style: dashed; cursor: default;
  }

  /* Disabled media-upload glyphs render SVGs, not font glyphs. */
  .prop-content-upload__glyph svg { width: 20px; height: 20px; opacity: 0.75; }
  .room-photo-slot__icon { display: inline-flex; opacity: 0.5; }
  .room-photo-slot__icon svg { width: 18px; height: 18px; }

  /* ---- Drawer footer -------------------------------------------------- */
  .prop-drawer__footer {
    min-height: 68px; padding: 12px 20px; gap: 12px;
  }
  .prop-drawer__footer .console-btn { height: 40px; }

  /* ---- Real 2-step stepper ------------------------------------------- */
  .prop-stepper {
    padding: 16px 24px 18px; gap: 0;
  }
  .prop-stepper__step { flex-direction: column; align-items: center; gap: 8px; flex: 0 0 auto; }
  .prop-stepper__circle {
    width: 28px; height: 28px; font-size: 13px; font-weight: 600;
    border: 1px solid var(--color-rule);
  }
  .prop-stepper__circle svg { width: 15px; height: 15px; }
  .prop-stepper__label { font-size: 12px; font-weight: 600; }
  /* Connector grows between the circle columns; aligns with the circle row. */
  .prop-stepper__step + .prop-stepper__step::before {
    content: ""; flex: 1 1 auto; min-width: 32px; height: 1px;
    align-self: flex-start; margin: 14px 12px 0;
    background: var(--color-rule);
  }
  .prop-stepper__step--done + .prop-stepper__step::before,
  .prop-stepper__step--current + .prop-stepper__step::before { background: var(--color-primary-action); }

  /* ---- Room table: hairline rows, comfortable heights ----------------- */
  [data-console-screen="properties"] .units-table thead th {
    font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em;
    padding-top: 10px; padding-bottom: 10px;
  }
  [data-console-screen="properties"] .units-table tbody td {
    padding-top: 14px; padding-bottom: 14px; font-size: 14px;
  }
  [data-console-screen="properties"] .units-table tbody td:first-child {
    font-weight: 600;
  }
  .units-menu__trigger {
    width: 36px; height: 36px; padding: 0;
    display: inline-flex; align-items: center; justify-content: center;
  }

  /* ---- Rate-mode / sell-mode segmented controls ----------------------- */
  .prop-drawer .seg-control {
    height: 36px; padding: 3px; border-radius: 8px;
    border: 1px solid var(--color-border); background: var(--color-gray-100);
    display: inline-flex; gap: 0;
  }
  .prop-drawer .seg-control__btn {
    flex: 1 1 50%; border-radius: 6px; border: none; background: transparent;
    font-size: 13px; font-weight: 600; color: var(--color-gray-500);
    cursor: pointer;
  }
  .prop-drawer .seg-control__btn:hover { color: var(--color-gray-700); }
  .prop-drawer .seg-control__btn--active {
    background: var(--color-primary-soft); color: var(--color-primary-700);
  }

  /* ---- Responsive: 1366 keeps actions on-row; mobile stacks ----------- */
  @media (max-width: 1366px) {
    .prop-panel__meta { flex-wrap: wrap; }
  }
  @media (max-width: 720px) {
    .prop-drawer { width: 100vw; }
    .prop-panel__action { flex: 0 0 auto; }
  }
`;

/** Channel onboarding (§5, §10–16, §21): the channel-specific PREPARATION
 *  panel, the stepped post-connection go-live flow, and the unmapped-booking
 *  attention banner. Reuses existing Channels tokens; no new gold elements —
 *  gold stays reserved for the empty-state Connect CTA and the
 *  bookings-this-month card. Step badges reuse the .booking-step__num pattern. */
const ONBOARDING_CSS = `
  /* ---- Channel-specific PREPARATION panel (§5) ------------------------- */
  .channel-prep .console-lede { margin-top: 0.5rem; }
  .channel-prep__mini-list {
    list-style: none;
    margin: 1rem 0;
    padding: 0;
    display: grid;
    gap: 0.5rem;
  }
  .channel-prep__mini-item {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0.55rem 0.75rem;
    border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm);
    background: var(--color-card);
  }
  .channel-prep__mini-item--ok { border-color: var(--color-success-border); }
  .channel-prep__note {
    margin: 0.75rem 0 0;
    padding: 0.55rem 0.75rem;
    border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm);
    background: var(--color-surface-subtle);
    color: var(--color-text-muted);
    font-size: 0.85rem;
  }

  /* ---- Stepped go-live onboarding panel (§10–16) ----------------------- */
  .channel-onboard__head-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .channel-onboard__step {
    border: 1px solid var(--color-rule);
    border-radius: var(--radius);
    padding: 1rem 1.15rem 1.15rem;
    margin: 1rem 0 0;
    background: var(--color-surface-subtle);
  }
  .channel-onboard__step-head {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin-bottom: 0.5rem;
  }
  .channel-onboard__step-title {
    margin: 0;
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.1rem;
    color: var(--color-forest);
  }
  .channel-onboard__list {
    list-style: none;
    margin: 0.85rem 0 0;
    padding: 0;
    display: grid;
    gap: 0.5rem;
  }
  .channel-onboard__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    padding: 0.65rem 0.75rem;
    border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm);
    background: var(--color-card);
  }
  .channel-onboard__row-main {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
    flex: 1 1 16rem;
  }
  .channel-onboard__row-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-top: 0.5rem;
  }
  .channel-onboard__verify { margin-top: 0.85rem; }
  .channel-onboard__attention {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--color-clay);
    border-radius: var(--radius-sm);
    background: var(--color-error-bg);
  }
  .channel-onboard__checklist {
    list-style: none;
    margin: 0.85rem 0 0;
    padding: 0;
    display: grid;
    gap: 0.5rem;
  }
  .channel-onboard__check {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0.55rem 0.75rem;
    border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm);
    background: var(--color-card);
    flex-wrap: wrap;
  }

  /* ---- Unmapped-booking attention banner (§21): clay tokens ------------ */
  .channel-unmapped {
    margin: 0 0 1rem;
    padding: 0.75rem 0.95rem;
    border: 1px solid var(--color-clay);
    border-radius: var(--radius-sm);
    background: var(--color-error-bg);
  }
  .channel-unmapped__title {
    margin: 0;
    font-weight: 700;
    color: var(--color-clay);
  }
  .channel-unmapped__note {
    margin: 0.3rem 0 0;
    color: var(--color-text-muted);
    font-size: 0.85rem;
  }
`;

/** Accounts (AVOS Books): the seven-tab finance workspace. Minimal — it mostly
 *  reuses existing console classes (panels, cards, tables, forms, the shared
 *  status pill, the channel-detail tab strip, and the property drawer). Adds
 *  only the header row, negative-amount colour, and a table totals row. No new
 *  gold — the single gold card is the Overview operating result via the shared
 *  .console-card--gold. */
const ACCOUNTS_CSS = `
  /* ---- Page header row: title block + right-aligned property selector --- */
  .accounts-header {
    display: flex; align-items: flex-start; justify-content: space-between;
    gap: 1rem; flex-wrap: wrap;
  }
  .accounts-header__selector { flex: 0 0 auto; min-width: 14rem; }

  /* ---- Negative ledger / cash amounts render in clay (§05 critical) ----- */
  .accounts-ledger-neg { color: var(--color-clay); }

  /* ---- Table totals row: emphasised with a top rule -------------------- */
  .accounts-total-row td {
    font-weight: 600;
    border-top: 1px solid var(--color-rule);
  }

  /* ---- Panel header: title on the left, "+ Add" CTA(s) on the right ---- */
  .accounts-panel-head {
    display: flex; align-items: center; justify-content: space-between;
    gap: 1rem; flex-wrap: wrap; margin-bottom: 0.75rem;
  }
  .accounts-panel-head .console-panel__title { margin: 0; }
  .accounts-panel-head__actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }

  /* ---- Overview period selector (segmented buttons, no gold) ----------- */
  .accounts-period {
    display: inline-flex; gap: 0.25rem; margin-bottom: 1rem;
    border: 1px solid var(--color-rule); border-radius: var(--radius);
    padding: 0.2rem; background: var(--color-surface);
  }
  .accounts-period__btn {
    background: none; border: none; font: inherit; cursor: pointer;
    padding: 0.4rem 0.85rem; border-radius: calc(var(--radius) - 0.2rem);
    color: var(--color-text-muted);
  }
  .accounts-period__btn:hover { color: var(--color-gray-700); }
  .accounts-period__btn--active {
    background: var(--color-primary-soft); color: var(--color-primary-700);
  }

  /* ---- Period filter dropdown (date presets + custom range) ------------- */
  .accounts-periodfilter {
    display: flex; align-items: center; gap: 0.65rem; flex-wrap: wrap;
    margin-bottom: 1rem; position: relative;
  }
  .accounts-periodfilter__caption {
    font-size: 0.75rem; font-weight: 600; letter-spacing: 0.06em;
    color: var(--color-text-muted); text-transform: uppercase;
  }
  .accounts-periodfilter__control { position: relative; display: inline-block; }
  .accounts-periodfilter__trigger {
    display: inline-flex; align-items: center; gap: 0.45rem;
    padding: 0.45rem 0.8rem; border: 1px solid var(--color-rule);
    border-radius: var(--radius-sm); background: var(--color-card);
    color: var(--color-text); font: inherit; font-size: 0.88rem;
    font-weight: 600; cursor: pointer;
    transition: border-color 0.12s ease, box-shadow 0.12s ease;
    white-space: nowrap;
  }
  .accounts-periodfilter__trigger:hover { border-color: var(--color-forest); }
  .accounts-periodfilter__trigger:focus-visible {
    outline: 2px solid var(--color-focus); outline-offset: 2px;
  }
  .accounts-periodfilter__trigger[aria-expanded="true"] {
    border-color: var(--color-primary-action); box-shadow: var(--focus-ring);
  }
  .accounts-periodfilter__icon { font-size: 1rem; line-height: 1; }
  .accounts-periodfilter__chevron { font-size: 0.7rem; color: var(--color-slate); }
  .accounts-periodfilter__value { min-width: 0; overflow-wrap: anywhere; }
  .accounts-periodfilter__menu {
    position: absolute; top: calc(100% + 0.35rem); left: 0; z-index: 60;
    list-style: none; margin: 0; padding: 0.3rem;
    min-width: 14rem; max-height: 22rem; overflow-y: auto;
    background: var(--color-card); border: 1px solid var(--color-rule);
    border-radius: var(--radius); box-shadow: 0 10px 30px rgba(16,24,40,0.14);
    animation: console-pop 0.12s ease;
  }
  .accounts-periodfilter__menu li { margin: 0; padding: 0; }
  .accounts-periodfilter__option {
    display: flex; align-items: center; justify-content: space-between; gap: 0.5rem;
    width: 100%; padding: 0.55rem 0.7rem; border: none; background: none;
    font: inherit; font-size: 0.88rem; color: var(--color-text);
    text-align: left; cursor: pointer; border-radius: var(--radius-sm);
    white-space: nowrap;
  }
  .accounts-periodfilter__option:hover { background: var(--color-surface-subtle); }
  .accounts-periodfilter__option--active { font-weight: 600; color: var(--color-primary-700); background: var(--color-primary-soft); }
  .accounts-periodfilter__option:focus-visible { outline: 2px solid var(--color-focus); outline-offset: -2px; }
  .accounts-periodfilter__tick { color: var(--color-success-text); font-weight: 700; }
  .accounts-periodfilter__panel {
    position: absolute; top: calc(100% + 0.35rem); left: 0; z-index: 60;
    width: max-content; max-width: calc(100vw - 2rem);
    background: var(--color-card); border: 1px solid var(--color-rule);
    border-radius: var(--radius); box-shadow: 0 10px 30px rgba(16,24,40,0.14);
    padding: 1rem; animation: console-pop 0.12s ease;
  }
  .accounts-periodfilter__panel-title {
    margin: 0 0 0.75rem; font-weight: 700; font-size: 0.95rem; color: var(--color-forest);
  }
  .accounts-periodfilter__panel-actions {
    display: flex; justify-content: flex-end; gap: 0.65rem; margin-top: 0.75rem;
  }
  .accounts-periodfilter__error { color: var(--color-clay); font-size: 0.82rem; margin: 0.4rem 0 0; }

  /* Range calendar (two months side-by-side). */
  .accounts-cal__nav {
    display: flex; justify-content: space-between; margin-bottom: 0.25rem;
  }
  .accounts-cal__navbtn { padding: 0.3rem 0.65rem; font-size: 1rem; }
  .accounts-cal__months {
    display: flex; gap: 1.25rem; flex-wrap: wrap;
  }
  .accounts-cal__month { flex: 1 1 12rem; min-width: 12rem; }
  .accounts-cal__monthname {
    text-align: center; font-weight: 700; font-size: 0.85rem;
    color: var(--color-forest); margin: 0 0 0.35rem;
  }
  .accounts-cal__grid {
    display: grid; grid-template-columns: repeat(7, 1fr); gap: 0;
  }
  .accounts-cal__wd {
    text-align: center; font-size: 0.65rem; font-weight: 600;
    color: var(--color-text-muted); letter-spacing: 0.04em;
    padding: 0.2rem 0;
  }
  .accounts-cal__pad { height: 2rem; }
  .accounts-cal__day {
    display: flex; align-items: center; justify-content: center;
    height: 2rem; font-size: 0.82rem; font-variant-numeric: tabular-nums;
    background: none; border: none; cursor: pointer; color: var(--color-text);
    border-radius: var(--radius-sm);
    transition: background 0.08s ease;
  }
  .accounts-cal__day:hover { background: var(--color-gray-100); }
  .accounts-cal__day:focus-visible { outline: 2px solid var(--color-focus); outline-offset: -2px; }
  .accounts-cal__day--start,
  .accounts-cal__day--end {
    background: var(--color-primary-action); color: #FFFFFF;
    border-radius: var(--radius-sm); font-weight: 700;
  }
  .accounts-cal__day--start:hover,
  .accounts-cal__day--end:hover { background: var(--color-primary-hover); }
  .accounts-cal__day--inrange {
    background: var(--color-gray-100); border-radius: 0;
  }
  .accounts-cal__fields {
    display: flex; gap: 1.25rem; margin-top: 0.65rem;
    padding-top: 0.65rem; border-top: 1px solid var(--color-rule);
  }
  .accounts-cal__field { display: flex; flex-direction: column; gap: 0.15rem; flex: 1 1 auto; }
  .accounts-cal__field-label {
    font-size: 0.68rem; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.06em; color: var(--color-text-muted);
  }
  .accounts-cal__field-value {
    font-size: 0.88rem; font-weight: 600; color: var(--color-text);
    font-variant-numeric: tabular-nums;
  }
  @media (max-width: 700px) {
    .accounts-cal__months { flex-direction: column; }
    .accounts-periodfilter__panel { width: 100%; left: 0; right: 0; }
  }

  /* ---- Owner statement + chart-of-accounts panels ---------------------- */
  .accounts-owner { margin-bottom: 1.25rem; }
  .accounts-owner:last-child { margin-bottom: 0; }
  .accounts-chart-group { margin-bottom: 1.25rem; }
  .accounts-chart-group:last-child { margin-bottom: 0; }

  /* ===================== Accounts Overview dashboard =================== */
  /* Five owner-first KPI cards: keep them one clean row where space allows,
     with the hero (Operating result) given a touch more presence. */
  .accounts-kpis {
    grid-template-columns: repeat(5, minmax(9.5rem, 1fr));
  }
  .accounts-kpi__spacer { display: none; }
  .accounts-kpi--hero { grid-row: span 1; }
  /* Consistent money-value treatment across ALL five cards: same font, size,
     weight and tabular numerals; compact text must never wrap to a second
     line. The value is focusable (keyboard) + hoverable (mouse) to reveal the
     exact amount via its title tooltip. */
  .accounts-kpi__value {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: default;
    width: fit-content;
    max-width: 100%;
    font-variant-numeric: tabular-nums;
  }
  .accounts-kpi__value:focus-visible {
    outline: 2px solid var(--color-focus);
    outline-offset: 2px;
    border-radius: 4px;
  }
  /* A loss reads clearly but calmly — clay text + hairline, never alarming. */
  .accounts-kpi--loss {
    border-color: var(--color-clay);
    box-shadow: 0 0 0 1px rgba(180, 35, 24, 0.3), var(--shadow-card);
  }
  .accounts-kpi--loss .console-card__value { color: var(--color-clay); }
  @media (max-width: 1200px) {
    .accounts-kpis { grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr)); }
  }

  /* Two-column dashboard rows (charts side by side, stacking on narrow). */
  .accounts-dash-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
    align-items: start;
  }
  .accounts-dash-row .accounts-dash-panel { margin-top: 1.5rem; }
  @media (max-width: 1000px) {
    .accounts-dash-row { grid-template-columns: 1fr; }
  }

  /* Donut centre overlay (total revenue) sits over the Recharts pie hole. */
  .accounts-donut { position: relative; }
  .accounts-donut__center {
    position: absolute; inset: 0;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    pointer-events: none; text-align: center;
  }
  .accounts-donut__label {
    font-size: 0.6rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--color-text-muted);
  }
  .accounts-donut__value {
    font-family: var(--font-body); font-weight: 700; font-size: 1.05rem;
    color: var(--color-forest); font-variant-numeric: tabular-nums; margin-top: 0.15rem;
  }

  /* GST position: three connected values with proportional bars. */
  .accounts-gst { display: flex; flex-direction: column; gap: 0.35rem; margin: 0.5rem 0 0.75rem; }
  .accounts-gst__row {
    display: grid; grid-template-columns: 9rem 1fr auto; align-items: center; gap: 0.75rem;
  }
  .accounts-gst__label { font-size: 0.85rem; color: var(--color-text); }
  .accounts-gst__bar-track {
    height: 0.6rem; background: var(--color-surface-subtle);
    border: 1px solid var(--color-rule); border-radius: 999px; overflow: hidden;
  }
  .accounts-gst__bar { display: block; height: 100%; border-radius: 999px; }
  .accounts-gst__bar--out { background: var(--color-success-text); }
  .accounts-gst__bar--itc { background: var(--color-slate); }
  .accounts-gst__bar--net { background: var(--color-primary-action); }
  .accounts-gst__amt { font-variant-numeric: tabular-nums; font-size: 0.9rem; color: var(--color-text); text-align: right; }
  .accounts-gst__amt--net { font-weight: 700; font-size: 1.05rem; color: var(--color-forest); }
  .accounts-gst__row--net .accounts-gst__label { font-weight: 700; color: var(--color-forest); }
  .accounts-gst__op {
    font-size: 0.68rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--color-text-muted); padding-left: 9.75rem;
  }

  /* Recent activity as a readable feed (not an accounting table). */
  .accounts-activity { list-style: none; margin: 0.5rem 0 0; padding: 0; display: flex; flex-direction: column; }
  .accounts-activity__item {
    display: grid;
    grid-template-columns: 6.5rem 5.5rem 1fr auto;
    align-items: center; gap: 0.75rem;
    padding: 0.7rem 0; border-bottom: 1px solid var(--color-rule);
  }
  .accounts-activity__item:last-child { border-bottom: none; }
  .accounts-activity__date { font-size: 0.8rem; color: var(--color-text-muted); font-variant-numeric: tabular-nums; }
  .accounts-activity__kind { justify-self: start; }
  .accounts-activity__desc { color: var(--color-text); min-width: 0; overflow-wrap: anywhere; }
  .accounts-activity__amt { text-align: right; font-variant-numeric: tabular-nums; font-weight: 700; }
  .accounts-activity__amt--in { color: var(--color-success-text); }
  @media (max-width: 700px) {
    .accounts-activity__item { grid-template-columns: 1fr auto; grid-row-gap: 0.15rem; }
    .accounts-activity__date { grid-column: 1; }
    .accounts-activity__kind { grid-column: 2; justify-self: end; }
    .accounts-activity__desc { grid-column: 1 / -1; }
    .accounts-activity__amt { grid-column: 1 / -1; text-align: left; }
    .accounts-gst__row { grid-template-columns: 7rem 1fr auto; }
    .accounts-gst__op { padding-left: 7.75rem; }
  }

  /* ==== Stays & Invoices workspace ===================================== */

  /* Four compact KPI cards for the invoice workspace. */
  .accounts-invkpis {
    grid-template-columns: repeat(4, minmax(9.5rem, 1fr));
    margin-bottom: 1rem;
  }
  @media (max-width: 900px) {
    .accounts-invkpis { grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr)); }
  }

  /* Control row: search grows, status + period sit to the right. */
  .accounts-invcontrols {
    display: flex; align-items: flex-end; gap: 0.75rem; flex-wrap: wrap;
    margin-bottom: 1rem;
  }
  .accounts-invcontrols__search { flex: 1 1 18rem; min-width: 14rem; margin: 0; }
  .accounts-invcontrols__status { flex: 0 0 auto; min-width: 10rem; margin: 0; }
  .accounts-invcontrols__period { flex: 0 0 auto; }

  /* Row actions cell: visible Download + overflow trigger side by side. */
  .accounts-actions { display: flex; align-items: center; gap: 0.35rem; white-space: nowrap; }

  /* The three-dot overflow menu. */
  .accounts-rowmenu { position: relative; display: inline-block; }
  .accounts-rowmenu__trigger {
    width: 2rem; min-width: 2rem; padding: 0.25rem 0; font-size: 1.1rem; line-height: 1;
  }
  .accounts-rowmenu__menu {
    position: absolute; top: calc(100% + 0.35rem); right: 0; z-index: 70;
    list-style: none; margin: 0; padding: 0.3rem; min-width: 12rem;
    background: var(--color-surface);
    border: 1px solid var(--color-rule); border-radius: var(--radius);
    box-shadow: var(--shadow-card);
    animation: console-pop 0.12s ease;
  }
  .accounts-rowmenu__menu li { margin: 0; padding: 0; }
  .accounts-rowmenu__item {
    display: block; width: 100%; text-align: left;
    background: none; border: none; font: inherit; cursor: pointer;
    padding: 0.45rem 0.6rem; border-radius: 4px; color: var(--color-text);
  }
  .accounts-rowmenu__item:hover:not(:disabled) { background: var(--color-surface-subtle); color: var(--color-forest); }
  .accounts-rowmenu__item:disabled { color: var(--color-text-muted); cursor: not-allowed; opacity: 0.6; }
  .accounts-rowmenu__item--danger { color: var(--color-clay); }
  .accounts-rowmenu__item--danger:hover:not(:disabled) { background: var(--color-error-bg); color: var(--color-clay); }

  /* Lightweight Prev / Next pagination footer. */
  .accounts-pagination {
    display: flex; align-items: center; justify-content: flex-end; gap: 0.75rem;
    margin-top: 0.9rem;
  }
  .accounts-pagination__label {
    font-size: 0.82rem; color: var(--color-text-muted); font-variant-numeric: tabular-nums;
  }

  /* Confirmation modal: a touch narrower than the default modal. */
  .console-modal--confirm { max-width: 30rem; }

  /* Invoice detail drawer: label/value rows + totals + line tables. */
  .accounts-invdetail__status { margin-bottom: 1.25rem; }
  .accounts-invdetail__rows { margin: 0; display: flex; flex-direction: column; gap: 0.4rem; }
  .accounts-invdetail__row {
    display: flex; align-items: baseline; justify-content: space-between; gap: 1rem;
  }
  .accounts-invdetail__row dt { margin: 0; color: var(--color-text-muted); font-size: 0.85rem; }
  .accounts-invdetail__row dd { margin: 0; color: var(--color-text); text-align: right; overflow-wrap: anywhere; }
  .accounts-invdetail__rows--totals { border-top: 1px solid var(--color-rule); padding-top: 0.6rem; }
  .accounts-invdetail__row--strong dt,
  .accounts-invdetail__row--strong dd { font-weight: 700; color: var(--color-forest); }
  .accounts-invdetail__lines,
  .accounts-invdetail__payments { margin-top: 0.25rem; }

  /* ---- Cash & Banks: internal sub-tab strip spacing ------------------- */
  .accounts-subtabs { margin-top: 0.25rem; margin-bottom: 1.25rem; }

  /* ---- Cash & Banks: overview summary cards --------------------------- */
  .accounts-cash-cards {
    display: grid; gap: 0.85rem; margin-bottom: 1.5rem;
    grid-template-columns: repeat(4, minmax(9.5rem, 1fr));
  }
  @media (max-width: 900px) {
    .accounts-cash-cards { grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr)); }
  }

  /* ---- Staff & Payroll: internal sub-tab strip spacing ---------------- */
  .accounts-staff-subtabs { margin-top: 0.25rem; margin-bottom: 1.25rem; }

  /* ---- Staff & Payroll: summary cards (same 4-col grid as cash cards) - */
  .accounts-staff-cards {
    display: grid; gap: 0.85rem; margin-bottom: 1.5rem;
    grid-template-columns: repeat(4, minmax(9.5rem, 1fr));
  }
  @media (max-width: 900px) {
    .accounts-staff-cards { grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr)); }
  }

  /* Muted second line under an employee name in the Team / Payslips table. */
  .accounts-staff-sub { display: block; margin-top: 0.15rem; font-size: 0.82rem; color: var(--color-text-muted); }

  /* ---- Effect-preview line (record-movement modal + count variance) --- */
  .accounts-effect-preview {
    display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem;
    margin: 0.75rem 0 0; padding: 0.6rem 0.75rem;
    background: var(--color-surface-muted, rgba(0,0,0,0.03));
    border: 1px solid var(--color-rule); border-radius: 8px;
    font-size: 0.85rem; color: var(--color-text-muted);
  }
  .accounts-effect-preview__label {
    font-weight: 700; color: var(--color-text); text-transform: uppercase;
    font-size: 0.68rem; letter-spacing: 0.04em;
  }
  .accounts-effect-preview--warn {
    border-color: var(--color-clay);
    color: var(--color-clay);
  }

  /* ---- Count-cash modal: expected figure + denomination grid ---------- */
  .accounts-cash-expected { margin: 0.2rem 0 0; font-weight: 700; }
  .accounts-cash-denomtoggle {
    display: flex; align-items: center; gap: 0.5rem; margin: 0.6rem 0;
    font-size: 0.88rem; color: var(--color-text);
  }
  .accounts-cash-variancebox {
    display: flex; flex-direction: column; gap: 0.5rem; align-items: flex-start;
  }
  .accounts-denomgrid {
    display: flex; flex-direction: column; gap: 0.4rem; margin: 0.5rem 0 0.75rem;
    padding: 0.6rem 0.75rem; border: 1px solid var(--color-rule); border-radius: 8px;
  }
  .accounts-denomgrid__row {
    display: grid; grid-template-columns: 3.5rem 1rem 1fr 6rem; align-items: center;
    gap: 0.5rem;
  }
  .accounts-denomgrid__face { font-weight: 700; }
  .accounts-denomgrid__x { color: var(--color-text-muted); text-align: center; }
  .accounts-denomgrid__sub { color: var(--color-text-muted); text-align: right; font-variant-numeric: tabular-nums; }

  /* ---- Bank-account detail drawer KPI cards --------------------------- */
  .accounts-bank-detailkpis {
    display: grid; gap: 0.75rem; margin: 0.5rem 0 0.5rem;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  /* ---- Bank-account cards grid ---------------------------------------- */
  .accounts-bankgrid {
    display: grid; gap: 1rem;
    grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  }
  .accounts-bankcard { display: flex; flex-direction: column; gap: 0.45rem; }
  .accounts-bankcard__head {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 0.5rem;
  }
  .accounts-bankcard__name { margin: 0; font-weight: 700; color: var(--color-text); }
  .accounts-bankcard__badges { display: flex; flex-wrap: wrap; gap: 0.4rem; }
  .accounts-bankcard__num {
    margin: 0.2rem 0 0; font-variant-numeric: tabular-nums; letter-spacing: 0.08em;
    color: var(--color-text);
  }
  .accounts-bankcard__balance {
    margin-top: 0.4rem; padding-top: 0.5rem; border-top: 1px solid var(--color-rule);
  }
  .accounts-bankcard__balance .console-card__label { margin: 0 0 0.15rem; }
  .accounts-bankcard__actions { margin-top: 0.5rem; }

  /* ---- Movements: clickable rows + inline row actions ----------------- */
  .accounts-clickrow { cursor: pointer; }
  .accounts-clickrow:hover { background: var(--color-surface-muted, rgba(0,0,0,0.03)); }
  .accounts-rowactions { display: flex; align-items: center; gap: 0.35rem; }

  /* ---- Offers & Vouchers: summary cards ------------------------------- */
  .accounts-offer-cards {
    display: grid; gap: 0.85rem; margin-bottom: 1.5rem;
    grid-template-columns: repeat(4, minmax(9.5rem, 1fr));
  }
  @media (max-width: 900px) {
    .accounts-offer-cards { grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr)); }
  }

  /* Muted second line under an offer name / a selected property name. */
  .accounts-offer-code { display: block; margin-top: 0.15rem; }

  /* ---- Create-offer flow: step-1 type chooser ------------------------- */
  .accounts-offer-chooser {
    display: grid; gap: 0.75rem;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  }
  .accounts-offer-chooser > .recon-caption { grid-column: 1 / -1; margin: 0 0 0.25rem; }
  .accounts-offer-chooser__card {
    display: flex; flex-direction: column; gap: 0.3rem; text-align: left;
    padding: 1rem; background: var(--color-surface-subtle);
    border: 1px solid var(--color-rule); border-radius: var(--radius, 8px);
    cursor: pointer; font: inherit; color: var(--color-text);
    transition: border-color 0.12s ease, background 0.12s ease;
  }
  .accounts-offer-chooser__card:hover { border-color: var(--color-forest); }
  .accounts-offer-chooser__card:focus-visible {
    outline: 2px solid var(--color-focus); outline-offset: 2px;
  }
  .accounts-offer-chooser__title { font-weight: 700; color: var(--color-text); }
  .accounts-offer-chooser__sub { font-size: 0.85rem; color: var(--color-text-muted); }

  /* ---- Create-offer flow: scrollable property checkbox list ----------- */
  .accounts-offer-proplist {
    display: flex; flex-direction: column; gap: 0.4rem;
    max-height: 10rem; overflow-y: auto;
    padding: 0.6rem 0.75rem; margin: 0 0 0.75rem;
    border: 1px solid var(--color-rule); border-radius: var(--radius, 8px);
  }
  .accounts-offer-proplist legend {
    font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em;
    color: var(--color-text-muted); padding: 0 0.25rem;
  }
  .accounts-offer-proplist__item {
    display: flex; align-items: center; gap: 0.5rem;
    font-size: 0.9rem; color: var(--color-text);
  }

  /* ---- Owner Statements: summary cards (4-col grid, same as cash) ----- */
  .accounts-owner-cards {
    display: grid; gap: 0.85rem; margin-bottom: 1.5rem;
    grid-template-columns: repeat(4, minmax(9.5rem, 1fr));
  }
  @media (max-width: 900px) {
    .accounts-owner-cards { grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr)); }
  }

  /* ---- Owner Statements: two-column donut row (stacks on narrow) ------ */
  .accounts-owner-charts {
    display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; align-items: start;
  }
  .accounts-owner-charts .console-panel { margin-top: 1.5rem; }
  @media (max-width: 1000px) {
    .accounts-owner-charts { grid-template-columns: 1fr; }
  }

  /* ---- Owner Statements: itemised detailed statement ------------------ */
  .accounts-stmt { display: flex; flex-direction: column; }
  .accounts-stmt__section { margin: 0 0 0.5rem; }
  .accounts-stmt__heading {
    margin: 1.25rem 0 0.5rem; font-size: 0.7rem; font-weight: 600;
    letter-spacing: 0.12em; text-transform: uppercase; color: var(--color-text-muted);
    border-bottom: 1px solid var(--color-rule); padding-bottom: 0.4rem;
  }
  .accounts-stmt__row {
    display: flex; justify-content: space-between; gap: 1rem;
    padding: 0.5rem 0; border-bottom: 1px solid var(--color-rule);
  }
  .accounts-stmt__row-amount { font-variant-numeric: tabular-nums; }
  .accounts-stmt__row-toggle {
    width: 100%; background: none; border: none; font: inherit; cursor: pointer;
    text-align: left; color: inherit;
    display: flex; justify-content: space-between; gap: 1rem;
    padding: 0.5rem 0; border-bottom: 1px solid var(--color-rule);
  }
  .accounts-stmt__row-toggle:hover { background: var(--color-surface-subtle); }
  .accounts-stmt__row-toggle:focus-visible {
    outline: 2px solid var(--color-focus); outline-offset: -2px;
  }
  .accounts-stmt__caret {
    display: inline-block; margin-right: 0.4rem; color: var(--color-text-muted);
    transition: transform 0.12s ease;
  }
  .accounts-stmt__caret--open { transform: rotate(90deg); }
  .accounts-stmt__row--subtotal { font-weight: 700; color: var(--color-forest); }
  .accounts-stmt__drill {
    padding: 0.35rem 0 0.5rem 1rem; border-bottom: 1px solid var(--color-rule);
  }
  .accounts-stmt__drill-row {
    display: flex; justify-content: space-between; gap: 1rem;
    padding: 0.25rem 0; font-size: 0.85rem;
  }
  .accounts-stmt__drill-sub { color: var(--color-text-muted); font-size: 0.8rem; }
  .accounts-stmt__neg { color: var(--color-clay); }
  .accounts-stmt__muted {
    margin: 0.5rem 0; padding: 0.5rem 0; color: var(--color-text-muted); font-size: 0.9rem;
  }
  .accounts-stmt__caption {
    margin: 0.4rem 0 0; color: var(--color-text-muted); font-size: 0.8rem;
  }

  /* ---- Owner Statements: prominent final Owner's Share block ---------- */
  .accounts-owner-share {
    margin-top: 1.25rem; padding: 1.1rem 1.25rem;
    border: 1px solid var(--color-primary-200); border-radius: var(--radius);
    box-shadow: var(--shadow-card);
    display: flex; align-items: baseline; justify-content: space-between; gap: 1rem;
  }
  .accounts-owner-share__label {
    font-weight: 700; color: var(--color-forest); letter-spacing: 0.02em;
  }
  .accounts-owner-share__value {
    font-family: var(--font-body); font-weight: 700; font-size: 1.6rem;
    color: var(--color-primary-700); font-variant-numeric: tabular-nums;
  }
  .accounts-owner-share--loss {
    border-color: var(--color-clay);
    box-shadow: 0 0 0 1px rgba(180, 35, 24, 0.3), var(--shadow-card);
  }
  .accounts-owner-share--loss .accounts-owner-share__value { color: var(--color-clay); }

  /* Owner Statements: portfolio comparison margin cell. */
  .accounts-owner-margin { font-variant-numeric: tabular-nums; }
`;

/**
 * Channel Manager v2 (real Channex OTA connectivity). Reuses the AVOS palette:
 * off-white/white cards, deep green text, restrained gold for the single
 * primary CTA. Adds the property selector, real KPI grid, connected-channel
 * cards, the OTA logo tile, the dedicated full-size embedded connect page, and
 * the mapping summary rows. No decorative colour beyond the OTA logo accent.
 */
const CHANNELS_V2_CSS = `
  /* ---- Header controls: property selector + primary CTA ---------------- */
  .chan2-header {
    display: flex; align-items: flex-end; justify-content: space-between;
    gap: 1rem; flex-wrap: wrap; margin-top: 1.25rem;
  }
  .chan2-header__controls { display: flex; align-items: flex-end; gap: 0.75rem; flex-wrap: wrap; }
  .chan2-propsel { display: flex; flex-direction: column; gap: 0.25rem; min-width: 15rem; }
  .chan2-propsel label {
    font-size: 0.7rem; font-weight: 600; letter-spacing: 0.04em;
    text-transform: uppercase; color: var(--color-text-muted);
  }
  .chan2-propsel select {
    padding: 0.5rem 0.6rem; border: 1px solid var(--color-rule); border-radius: var(--radius-sm);
    background: var(--color-card); color: var(--color-text); font: inherit; font-size: 0.9rem;
    height: 42px;
  }

  /* ---- KPI grid -------------------------------------------------------- */
  .chan2-kpis {
    display: grid; gap: 1rem; margin-top: 1.5rem;
    grid-template-columns: repeat(4, minmax(11rem, 1fr));
  }
  @media (max-width: 1000px) { .chan2-kpis { grid-template-columns: repeat(2, minmax(10rem, 1fr)); } }

  /* ---- OTA logo tile --------------------------------------------------- */
  .ota-logo {
    display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto;
    border-radius: 10px; overflow: hidden;
  }
  .ota-logo--placeholder { color: #fff; }
  .ota-logo__initials { font-weight: 700; font-size: 0.9rem; letter-spacing: 0.02em; }
  .ota-logo--img img { display: block; width: 100%; height: 100%; object-fit: contain; }

  /* ---- Connected channel cards ----------------------------------------- */
  .chan2-cards { display: grid; gap: 1rem; margin-top: 1.5rem; grid-template-columns: repeat(auto-fill, minmax(20rem, 1fr)); }
  .chan2-card { display: flex; flex-direction: column; gap: 0.75rem; }
  .chan2-card__head { display: flex; align-items: center; gap: 0.75rem; }
  .chan2-card__ident { min-width: 0; display: flex; flex-direction: column; gap: 0.15rem; }
  .chan2-card__name { margin: 0; font-weight: 700; color: var(--color-forest); }
  .chan2-card__prop { margin: 0; font-size: 0.8rem; color: var(--color-text-muted); }
  .chan2-card__grid { margin: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem 1rem;
    padding: 0.6rem 0; border-top: 1px solid var(--color-rule); border-bottom: 1px solid var(--color-rule); }
  .chan2-card__grid > div { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
  .chan2-card__grid dt { margin: 0; font-size: 0.6rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-text-muted); }
  .chan2-card__grid dd { margin: 0; color: var(--color-text); font-variant-numeric: tabular-nums; }
  .chan2-card__actions { display: flex; gap: 0.5rem; margin-top: auto; }
  .chan2-card__actions .console-btn { flex: 1 1 auto; text-align: center; }

  /* ---- Catalogue grid -------------------------------------------------- */
  .chan2-catalogue { display: grid; gap: 1rem; margin-top: 1rem; grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr)); }
  .chan2-catcard { display: flex; flex-direction: column; gap: 0.6rem; min-height: 150px; }
  .chan2-catcard__head { display: flex; align-items: center; gap: 0.6rem; }
  .chan2-catcard__name { margin: 0; font-weight: 700; color: var(--color-forest); }
  .chan2-catcard__desc { margin: 0; font-size: 0.82rem; color: var(--color-text-muted); flex: 1 1 auto; }
  .chan2-catcard__foot { margin-top: auto; }
  .chan2-catcard:hover { border-color: var(--color-forest); }
  .chan2-search { display: flex; gap: 0.75rem; flex-wrap: wrap; margin: 1rem 0 0.5rem; }
  .chan2-search input { flex: 1 1 16rem; min-width: 12rem; padding: 0.5rem 0.6rem; height: 42px;
    border: 1px solid var(--color-rule); border-radius: var(--radius-sm); background: var(--color-card); font: inherit; }

  /* ---- Dedicated connect page (full-size embedded iframe) -------------- */
  .chan2-connect__crumb { font-size: 0.8rem; color: var(--color-text-muted); margin: 0.25rem 0 0.5rem; }
  .chan2-connect__crumb button { background: none; border: none; color: var(--color-forest); font: inherit; cursor: pointer; padding: 0; }
  .chan2-connect__crumb button:hover { text-decoration: underline; }
  .chan2-connect__steps { display: flex; gap: 0.5rem; flex-wrap: wrap; margin: 0.5rem 0 1rem; }
  .chan2-step { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; color: var(--color-text-muted); }
  .chan2-step__num { display: inline-flex; align-items: center; justify-content: center; width: 1.4rem; height: 1.4rem;
    border-radius: 999px; border: 1px solid var(--color-rule); font-size: 0.72rem; font-weight: 700; }
  .chan2-step--active .chan2-step__num { background: var(--color-primary-action); color: var(--color-primary-text); border-color: var(--color-primary-action); }
  .chan2-step--active { color: var(--color-forest); font-weight: 600; }
  .chan2-connect__framewrap { position: relative; width: 100%; min-height: 720px; height: calc(100vh - 22rem);
    border: 1px solid var(--color-rule); border-radius: 12px; overflow: hidden; background: #fff; margin-top: 1rem; }
  .chan2-connect__frame { display: block; width: 100%; height: 100%; border: 0; }
  .chan2-connect__loading { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
    flex-direction: column; gap: 0.5rem; color: var(--color-text-muted); }

  /* ---- Mapping summary rows -------------------------------------------- */
  .chan2-map__section { margin-top: 1.25rem; }
  .chan2-map__row { display: grid; grid-template-columns: 1fr auto 1fr auto; align-items: center; gap: 0.75rem;
    padding: 0.6rem 0; border-bottom: 1px solid var(--color-rule); }
  .chan2-map__arrow { color: var(--color-text-muted); }
  .chan2-map__side { min-width: 0; }
  .chan2-map__side small { display: block; color: var(--color-text-muted); font-size: 0.75rem; }
`;


/** Revenue & Rates (M8): the tabbed pricing workspace — header + property
 * selector, tab bar, KPI cards, the rooms x dates rate calendar grid + cell
 * editor, bulk update, rate plans/restrictions tables, rules + simulator. All
 * tokens reuse the shared palette; NO undefined tokens. */
const REVENUE_CSS = `
  .rev-screen { display: block; }
  .rev-header {
    display: flex; align-items: flex-end; justify-content: space-between;
    gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem;
  }
  .rev-subtitle { margin: 0.35rem 0 0; color: var(--color-text-muted); font-size: 0.9rem; }
  .rev-propsel { min-width: 15rem; margin: 0; }

  .rev-tabs {
    display: flex; gap: 0.25rem; flex-wrap: wrap;
    border-bottom: 1px solid var(--color-rule); margin-bottom: 1.25rem;
  }
  .rev-tab {
    appearance: none; border: none; background: transparent; cursor: pointer;
    font: inherit; font-weight: 600; font-size: 0.9rem; color: var(--color-text-muted);
    padding: 0.6rem 0.9rem; border-bottom: 3px solid transparent; margin-bottom: -1px;
  }
  .rev-tab:hover { color: var(--color-forest); }
  .rev-tab:focus-visible { outline: none; box-shadow: var(--focus-ring); border-radius: var(--radius-sm); }
  .rev-tab--active { color: var(--color-forest); border-bottom-color: var(--color-primary-action); }

  .rev-tabpanel { display: block; }
  .rev-error { margin-bottom: 1rem; }
  .rev-muted { color: var(--color-text-muted); font-size: 0.9rem; }
  .rev-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }

  .rev-kpis {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.9rem; margin-bottom: 1.5rem;
  }

  .rev-panel {
    margin-top: 1.25rem; border: 1px solid var(--color-rule); border-radius: var(--radius);
    padding: 1.2rem 1.35rem 1.35rem; background: var(--color-card); box-shadow: var(--shadow-card);
  }
  .rev-panel__title { margin: 0 0 0.9rem; font-family: var(--font-display); font-weight: 600; font-size: 1.2rem; color: var(--color-forest); }

  .rev-toolbar { display: flex; align-items: flex-end; gap: 0.9rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .rev-inline { margin: 0; min-width: 8rem; }
  .rev-flash {
    display: inline-block; font-size: 0.8rem; font-weight: 600; color: var(--color-success-text);
    background: var(--color-success-bg); border: 1px solid var(--color-success-border);
    border-radius: 999px; padding: 0.2rem 0.7rem;
  }

  .rev-tablewrap { overflow-x: auto; }
  .rev-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
  .rev-table th, .rev-table td { text-align: left; padding: 0.55rem 0.7rem; border-bottom: 1px solid var(--color-rule); }
  .rev-table th { color: var(--color-text-muted); font-weight: 600; font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.04em; }
  .rev-table td { font-variant-numeric: tabular-nums; }
  .rev-mini { width: 4.5rem; padding: 0.3rem 0.4rem; border: 1px solid var(--color-rule); border-radius: var(--radius-sm); background: var(--color-card); color: var(--color-text); font: inherit; }

  /* Rate calendar grid */
  .rev-calendar { border-collapse: collapse; min-width: 100%; }
  .rev-calendar th, .rev-calendar td { border: 1px solid var(--color-rule); padding: 0; }
  .rev-calendar__unitcol {
    position: sticky; left: 0; z-index: 1; background: var(--color-surface-subtle);
    min-width: 11rem; padding: 0.5rem 0.7rem; text-align: left; vertical-align: top;
  }
  .rev-calendar__unitname { display: block; font-weight: 600; color: var(--color-forest); font-size: 0.85rem; }
  .rev-calendar__restr { display: inline-flex; gap: 0.25rem; margin-top: 0.3rem; flex-wrap: wrap; }
  .rev-calendar__day { text-align: center; padding: 0.35rem 0.2rem; background: var(--color-surface-subtle); min-width: 4.2rem; }
  .rev-calendar__day--weekend { background: var(--color-warning-bg); }
  .rev-calendar__dow { display: block; font-size: 0.68rem; color: var(--color-text-muted); text-transform: uppercase; }
  .rev-calendar__dom { display: block; font-size: 0.95rem; font-weight: 600; color: var(--color-forest); }
  .rev-calendar__cellwrap { padding: 0; }

  .rev-cell {
    display: block; width: 100%; border: none; background: var(--color-card); cursor: pointer;
    font: inherit; padding: 0.55rem 0.3rem; color: var(--color-text);
    font-variant-numeric: tabular-nums; font-size: 0.82rem; text-align: center;
  }
  .rev-cell:hover { background: var(--color-gray-100); }
  .rev-cell:focus-visible { outline: none; box-shadow: var(--focus-ring); }
  .rev-cell--override { background: var(--color-warning-bg); font-weight: 700; color: var(--color-forest); }
  .rev-cell--stop { background: var(--color-error-bg); color: var(--color-clay); }
  .rev-cell__ss { font-weight: 700; letter-spacing: 0.04em; }

  .rev-chip {
    display: inline-block; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.03em;
    color: var(--color-forest); background: var(--color-card); border: 1px solid var(--color-rule);
    border-radius: 999px; padding: 0.05rem 0.35rem;
  }

  /* Cell edit modal */
  .rev-modal { position: fixed; inset: 0; z-index: 80; display: flex; align-items: center; justify-content: center; }
  .rev-modal__backdrop { position: absolute; inset: 0; background: rgba(16, 24, 40, 0.35); }
  .rev-modal__panel {
    position: relative; z-index: 1; background: var(--color-card); border: 1px solid var(--color-rule);
    border-radius: var(--radius); box-shadow: 0 20px 50px rgba(16, 24, 40, 0.28);
    padding: 1.3rem 1.4rem; width: min(24rem, calc(100vw - 2rem));
  }
  .rev-modal__title { margin: 0 0 1rem; font-family: var(--font-display); font-weight: 600; font-size: 1.15rem; color: var(--color-forest); }
  .rev-modal__actions { display: flex; gap: 0.6rem; justify-content: flex-end; margin-top: 1.2rem; align-items: center; }
  .rev-modal__actions.rev-left { justify-content: flex-start; }
  .rev-check { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.8rem; font-size: 0.88rem; color: var(--color-text); }

  /* Bulk update */
  .rev-dows { display: flex; gap: 0.35rem; flex-wrap: wrap; }
  .rev-dow {
    appearance: none; cursor: pointer; font: inherit; font-size: 0.8rem; font-weight: 600;
    border: 1px solid var(--color-rule); background: var(--color-card); color: var(--color-text-muted);
    border-radius: var(--radius-sm); padding: 0.35rem 0.6rem;
  }
  .rev-dow--on { background: var(--color-primary-action); color: var(--color-primary-text); border-color: var(--color-primary-action); }
  .rev-preview { margin-top: 1rem; }
  .rev-preview__new { font-weight: 700; color: var(--color-forest); }

  /* Rate-plan-aware calendar: Room header row + indented rate-plan rows. */
  .rev-calendar__roomrow th.rev-calendar__roomhdr { background: var(--color-surface-subtle); border-bottom: 2px solid var(--color-forest); }
  .rev-calendar__roomspacer { background: var(--color-surface-subtle); border-bottom: 2px solid var(--color-forest); }
  .rev-calendar__rooms { display: block; font-size: 0.68rem; color: var(--color-text-muted); margin-top: 0.15rem; }
  .rev-calendar__planhdr { padding-left: 1.2rem; }
  .rev-calendar__planname { display: block; font-weight: 600; color: var(--color-text); font-size: 0.82rem; }
  .rev-cell--restr { box-shadow: inset 0 -3px 0 var(--color-primary-action); }
  .rev-cell--dim { opacity: 0.35; }

  /* Batch composer: stacked update lines. */
  .rev-batchline { border: 1px solid var(--color-rule); border-radius: var(--radius-sm); padding: 0.8rem 0.9rem; margin-bottom: 0.8rem; background: var(--color-surface-subtle); }
  .rev-batchline__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.6rem; }
  .rev-batchline__head strong { color: var(--color-forest); font-size: 0.92rem; }
  .rev-batchline__lineactions { display: flex; gap: 0.75rem; }
  .rev-batch__add { margin: 0.2rem 0 1rem; }


  /* Rules toggle + simulator */
  .rev-toggle {
    appearance: none; cursor: pointer; font: inherit; font-size: 0.78rem; font-weight: 700;
    border: 1px solid var(--color-rule); background: var(--color-card); color: var(--color-text-muted);
    border-radius: 999px; padding: 0.2rem 0.7rem;
  }
  .rev-toggle--on { background: var(--color-success-bg); color: var(--color-success-text); border-color: var(--color-success-border); }
  .rev-link-danger { appearance: none; border: none; background: none; cursor: pointer; font: inherit; color: var(--color-clay); font-weight: 600; font-size: 0.85rem; }
  .rev-sim-result {
    display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; margin-top: 1rem;
    padding: 0.9rem 1.1rem; border: 1px solid var(--color-rule); border-radius: var(--radius); background: var(--color-surface-subtle);
  }
  .rev-sim-result div { display: flex; flex-direction: column; }
  .rev-sim-result strong { font-size: 1.3rem; font-variant-numeric: tabular-nums; color: var(--color-forest); }
  .rev-sim-quoted { color: var(--color-primary-700) !important; }
  .rev-sim-arrow { font-size: 1.4rem; color: var(--color-text-muted); }

  @media (max-width: 900px) {
    .rev-kpis { grid-template-columns: repeat(2, 1fr); }
  }

  /* Certification additions: help text, sync-health grid, technical details. */
  .rev-help { font-size: 0.78rem; color: var(--color-text-muted); margin: 0 0 0.6rem; }
  .rev-small { font-size: 0.75rem; font-family: var(--font-mono, monospace); word-break: break-all; }
  .rev-synchealth { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.75rem 1.2rem; }
  .rev-synchealth__item { display: flex; flex-direction: column; gap: 0.2rem; font-size: 0.85rem; }
  .rev-synchealth__item strong { font-variant-numeric: tabular-nums; color: var(--color-forest); }
  .rev-tech { margin-top: 0.75rem; padding-top: 0.75rem; border-top: 1px solid var(--color-rule); }
  .rev-restr-details { margin: 0.5rem 0 0.75rem; border: 1px solid var(--color-rule); border-radius: var(--radius-sm); padding: 0.4rem 0.7rem; }
  .rev-restr-details > summary { cursor: pointer; font-size: 0.85rem; font-weight: 600; color: var(--color-forest); }
  .rev-recon-summary { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem 1rem; margin: 0.5rem 0 0; }
  .rev-recon-summary dt { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-text-muted); }
  .rev-recon-summary dd { margin: 0.15rem 0 0; font-weight: 600; }
  @media (max-width: 900px) { .rev-synchealth { grid-template-columns: repeat(2, 1fr); } }
`;


/** Platform Administration portal (internal AVOS staff). AVOS-branded but
 * visually distinct from the customer product: emerald sidebar with a gold
 * wordmark accent, restrained tables + cards. Reuses palette tokens. */
const PLATFORM_ADMIN_CSS = `
  .pa-shell { display: flex; height: 100vh; height: 100dvh; overflow: hidden; background: var(--color-surface); color: var(--color-text); }
  .pa-sidebar { width: 15rem; flex: 0 0 15rem; background: #F9FAFB; color: var(--color-gray-600); display: flex; flex-direction: column; padding: 1.25rem 1rem; min-height: 0; overflow-y: auto; border-right: 1px solid var(--color-border); }
  .pa-brand { display: flex; align-items: center; gap: 0.5rem; font-family: var(--font-display); font-size: 1.4rem; color: var(--color-text-primary); }
  .pa-brand--center { justify-content: center; }
  .pa-brand__sub { margin: 0.15rem 0 1.5rem; font-size: 0.72rem; letter-spacing: 0.14em; text-transform: uppercase; color: var(--color-gray-500); }
  .pa-brand__sub--center { text-align: center; margin-bottom: 1.5rem; }
  .pa-nav { display: flex; flex-direction: column; gap: 0.15rem; }
  .pa-nav__link { appearance: none; border: none; background: transparent; color: var(--color-gray-600); text-align: left; font: inherit; font-weight: 500; padding: 0.55rem 0.7rem; border-radius: var(--radius-xs); cursor: pointer; }
  .pa-nav__link:hover { background: var(--color-gray-100); color: var(--color-gray-700); }
  .pa-nav__link--active { background: var(--color-primary-soft); color: var(--color-primary-700); box-shadow: inset 3px 0 0 var(--color-primary-action); }
  .pa-sidebar__foot { margin-top: auto; display: flex; flex-direction: column; gap: 0.4rem; padding-top: 1rem; border-top: 1px solid var(--color-border); }
  .pa-small { font-size: 0.78rem; }
  .pa-main { flex: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; overflow-y: auto; }
  .pa-topbar { padding: 0.9rem 1.5rem; border-bottom: 1px solid var(--color-rule); background: var(--color-card); }
  .pa-topbar__title { font-family: var(--font-display); font-size: 1.1rem; color: var(--color-forest); }
  .pa-content { padding: 1.75rem; max-width: 72rem; }
  .pa-h1 { font-family: var(--font-display); font-weight: 600; font-size: 1.8rem; color: var(--color-forest); margin: 0 0 1.2rem; }
  .pa-h2 { font-family: var(--font-display); font-weight: 600; font-size: 1.25rem; color: var(--color-forest); margin: 0; }
  .pa-muted { color: var(--color-text-muted); }
  .pa-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.9rem; margin-bottom: 1.5rem; }
  .pa-card { border: 1px solid var(--color-rule); border-radius: var(--radius); background: var(--color-card); padding: 1.1rem 1.2rem; box-shadow: var(--shadow-card); }
  .pa-card__label { display: block; font-size: 0.78rem; color: var(--color-text-muted); text-transform: uppercase; letter-spacing: 0.04em; }
  .pa-card__value { display: block; font-size: 2rem; font-weight: 700; color: var(--color-forest); font-variant-numeric: tabular-nums; margin-top: 0.3rem; }
  .pa-panel { border: 1px solid var(--color-rule); border-radius: var(--radius); background: var(--color-card); padding: 1.2rem 1.35rem; box-shadow: var(--shadow-card); margin-top: 1.25rem; }
  .pa-panel__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.9rem; }
  .pa-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
  .pa-table th, .pa-table td { text-align: left; padding: 0.55rem 0.7rem; border-bottom: 1px solid var(--color-rule); }
  .pa-table th { color: var(--color-text-muted); font-weight: 600; font-size: 0.76rem; text-transform: uppercase; letter-spacing: 0.04em; }
  .pa-table a { color: var(--color-primary-700); font-weight: 600; }
  .pa-table a:hover { color: var(--color-primary-800); }
  .pa-actions { text-align: right; }
  .pa-badge { display: inline-block; padding: 0.12rem 0.55rem; border-radius: 999px; font-size: 0.72rem; font-weight: 600; border: 1px solid var(--color-rule); color: var(--color-text-muted); }
  .pa-badge--ok { border-color: var(--color-success-border); color: var(--color-success-text); background: var(--color-success-bg); }
  .pa-badge--warn { border-color: var(--color-warning-border); color: var(--color-warning-text); background: var(--color-warning-bg); }
  .pa-btn { background: var(--color-primary-action); color: var(--color-primary-text); border: 1px solid var(--color-primary-action); border-radius: var(--radius-sm); padding: 0.55rem 1.1rem; font: inherit; font-weight: 600; cursor: pointer; }
  .pa-btn:hover:not(:disabled) { background: var(--color-primary-hover); border-color: var(--color-primary-hover); }
  .pa-btn:disabled { opacity: 0.6; cursor: default; }
  .pa-btn--ghost { background: transparent; color: var(--color-forest); border: 1px solid var(--color-rule); border-radius: var(--radius-sm); padding: 0.5rem 0.9rem; font: inherit; font-weight: 600; cursor: pointer; }
  .pa-link { color: var(--color-forest); font-weight: 600; background: none; border: none; cursor: pointer; font: inherit; padding: 0; }
  .pa-link-danger { color: var(--color-clay); font-weight: 600; background: none; border: none; cursor: pointer; font: inherit; padding: 0; }
  .pa-error { color: var(--color-danger); background: var(--color-card); border: 1px solid var(--color-danger); border-radius: var(--radius-sm); padding: 0.55rem 0.75rem; font-size: 0.85rem; }
  .pa-field { display: flex; flex-direction: column; gap: 0.3rem; margin-bottom: 0.9rem; }
  .pa-field > span { font-size: 0.82rem; font-weight: 600; color: var(--color-text); }
  .pa-field input, .pa-field select { padding: 0.5rem 0.6rem; border: 1px solid var(--color-rule); border-radius: var(--radius-sm); background: var(--color-card); color: var(--color-text); font: inherit; }
  .pa-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.9rem; }
  .pa-authwrap { min-height: 100vh; min-height: 100dvh; display: flex; align-items: center; justify-content: center; background: #F7F8FC; padding: 2rem; }
  .pa-authcard { width: min(24rem, 100%); background: var(--color-card); border: 1px solid var(--color-border); border-radius: var(--radius); padding: 2rem 1.75rem; box-shadow: var(--shadow-lg); }
  .pa-dl { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin: 0; }
  .pa-dl dt { font-size: 0.76rem; color: var(--color-text-muted); text-transform: uppercase; letter-spacing: 0.04em; }
  .pa-dl dd { margin: 0.2rem 0 0; font-weight: 500; }
  .pa-code { font-family: var(--font-mono, monospace); font-size: 0.8rem; background: var(--color-surface-subtle); padding: 0.15rem 0.4rem; border-radius: 4px; word-break: break-all; }
  .pa-modal { position: fixed; inset: 0; z-index: 80; display: flex; align-items: center; justify-content: center; }
  .pa-modal__backdrop { position: absolute; inset: 0; background: rgba(16,24,40,0.4); }
  .pa-modal__panel { position: relative; z-index: 1; background: var(--color-card); border: 1px solid var(--color-border); border-radius: var(--radius); padding: 1.5rem 1.6rem; width: min(30rem, calc(100vw - 2rem)); box-shadow: var(--shadow-lg); max-height: 90vh; overflow-y: auto; }
  .pa-modal__actions { display: flex; gap: 0.6rem; justify-content: flex-end; margin-top: 1rem; }
  /* Toolbar (search + filters) above tables. */
  .pa-toolbar { display: flex; gap: 0.6rem; flex-wrap: wrap; margin-bottom: 0.9rem; }
  .pa-search { flex: 1 1 16rem; min-width: 0; padding: 0.5rem 0.7rem; border: 1px solid var(--color-rule); border-radius: var(--radius-sm); background: var(--color-card); color: var(--color-text); font: inherit; }
  .pa-toolbar select { padding: 0.5rem 0.7rem; border: 1px solid var(--color-rule); border-radius: var(--radius-sm); background: var(--color-card); color: var(--color-text); font: inherit; }
  /* Tables wrap horizontally on narrow viewports without breaking the page. */
  .pa-tablewrap { width: 100%; overflow-x: auto; }
  /* Tabs on the customer-detail page. */
  .pa-tabs { display: flex; gap: 0.25rem; flex-wrap: wrap; border-bottom: 1px solid var(--color-rule); margin-bottom: 1rem; }
  .pa-tab { appearance: none; border: none; background: transparent; cursor: pointer; font: inherit; font-weight: 600; font-size: 0.9rem; color: var(--color-text-muted); padding: 0.55rem 0.9rem; border-bottom: 3px solid transparent; margin-bottom: -1px; }
  .pa-tab:hover { color: var(--color-gray-700); }
  .pa-tab--active { color: var(--color-text-primary); border-bottom-color: var(--color-primary-action); }
  .pa-divider { height: 1px; background: var(--color-rule); margin: 0.9rem 0; }
  /* Health status dots (non-colour-only: always paired with a text label). */
  .pa-dot { display: inline-block; width: 0.6rem; height: 0.6rem; border-radius: 999px; margin-right: 0.4rem; vertical-align: middle; }
  .pa-dot--ok { background: var(--color-success-text); }
  .pa-dot--warn { background: var(--color-warning-text); }
  .pa-dot--crit { background: var(--color-error-text); }
  .pa-health-status { display: block; margin-top: 0.35rem; font-weight: 600; text-transform: capitalize; }
  .pa-health-overall { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem; font-size: 1rem; text-transform: capitalize; }
  @media (max-width: 800px) { .pa-kpis { grid-template-columns: repeat(2,1fr); } .pa-sidebar { display: none; } .pa-content { padding: 1.1rem; } }
`;


/** Public access-auth screens (forgot/reset password, invitation accept). */
const AUTH_ACCESS_CSS = `
  .auth-card { width: min(26rem, 100%); margin: 3rem auto; background: var(--color-card); border: 1px solid var(--color-rule); border-radius: var(--radius); padding: 2rem 1.75rem; box-shadow: var(--shadow-card); }
  .auth-card__title { font-family: var(--font-display); font-weight: 600; font-size: 1.6rem; color: var(--color-forest); margin: 0 0 1rem; }
  .auth-card__note { color: var(--color-text-muted); font-size: 0.9rem; margin: 0 0 1rem; }
  .auth-card__foot { margin-top: 1.2rem; font-size: 0.85rem; }
  .auth-card__foot a { color: var(--color-primary-700); font-weight: 600; }
  .auth-card__foot a:hover { color: var(--color-primary-800); }
  .auth-field { display: flex; flex-direction: column; gap: 0.3rem; margin-bottom: 0.9rem; }
  .auth-field > span { font-size: 0.82rem; font-weight: 600; }
  .auth-field input { padding: 0.55rem 0.65rem; border: 1px solid var(--color-rule); border-radius: var(--radius-sm); background: var(--color-card); color: var(--color-text); font: inherit; }
  .auth-btn { width: 100%; background: var(--color-primary-action); color: var(--color-primary-text); border: none; border-radius: var(--radius-sm); padding: 0.6rem 1rem; font: inherit; font-weight: 600; cursor: pointer; margin-top: 0.3rem; }
  .auth-btn:hover:not(:disabled) { background: var(--color-primary-hover); }
  .auth-btn:disabled { opacity: 0.6; }
  .auth-error { color: var(--color-danger); border: 1px solid var(--color-danger); border-radius: var(--radius-sm); padding: 0.5rem 0.7rem; font-size: 0.85rem; margin-bottom: 0.8rem; }
`;


/** Users & Access (Admin & Settings) + Access Denied state. */
const USERS_ACCESS_CSS = `
  .ua-screen { display: block; }
  .ua-head { margin-bottom: 1rem; }
  .ua-sub { margin: 0.35rem 0 0; color: var(--color-text-muted); font-size: 0.9rem; }
  .ua-tabs { display: flex; gap: 0.25rem; flex-wrap: wrap; border-bottom: 1px solid var(--color-rule); margin-bottom: 1.25rem; }
  .ua-tab { appearance: none; border: none; background: transparent; cursor: pointer; font: inherit; font-weight: 600; font-size: 0.9rem; color: var(--color-text-muted); padding: 0.6rem 0.9rem; border-bottom: 3px solid transparent; margin-bottom: -1px; }
  .ua-tab:hover { color: var(--color-gray-700); }
  .ua-tab--active { color: var(--color-text-primary); border-bottom-color: var(--color-primary-action); }
  .ua-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; flex-wrap: wrap; }
  .ua-toolbar__actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  .ua-kpis { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  .ua-kpi { font-size: 0.8rem; font-weight: 600; color: var(--color-primary-700); background: var(--color-primary-soft); border: 1px solid var(--color-primary-border); border-radius: 999px; padding: 0.25rem 0.7rem; }
  .ua-tablewrap { overflow-x: auto; border: 1px solid var(--color-rule); border-radius: var(--radius); background: var(--color-card); }
  .ua-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
  .ua-table th, .ua-table td { text-align: left; padding: 0.6rem 0.8rem; border-bottom: 1px solid var(--color-rule); }
  .ua-table th { color: var(--color-text-muted); font-weight: 600; font-size: 0.76rem; text-transform: uppercase; letter-spacing: 0.04em; }
  .ua-table tr:last-child td { border-bottom: none; }
  .ua-actions { text-align: right; white-space: nowrap; }
  .ua-link { color: var(--color-forest); font-weight: 600; background: none; border: none; cursor: pointer; font: inherit; padding: 0 0.35rem; }
  .ua-link-danger { color: var(--color-clay); font-weight: 600; background: none; border: none; cursor: pointer; font: inherit; padding: 0 0.35rem; }
  .ua-muted { color: var(--color-text-muted); }
  .ua-h2 { font-family: var(--font-display); font-weight: 600; font-size: 1.3rem; color: var(--color-forest); margin: 0 0 1rem; }
  .ua-badge { display: inline-block; padding: 0.1rem 0.5rem; border-radius: 999px; font-size: 0.7rem; font-weight: 600; border: 1px solid var(--color-rule); color: var(--color-text-muted); }
  .ua-badge--ok { border-color: var(--color-success-border); color: var(--color-success-text); background: var(--color-success-bg); }
  .ua-badge--info { border-color: var(--color-info-border); color: var(--color-info-text); background: var(--color-info-bg); }
  .ua-badge--warn { border-color: var(--color-warning-border); color: var(--color-warning-text); background: var(--color-warning-bg); }
  .ua-error { color: var(--color-danger); border: 1px solid var(--color-danger); border-radius: var(--radius-sm); padding: 0.5rem 0.7rem; font-size: 0.85rem; margin-bottom: 0.8rem; }
  .ua-note { color: var(--color-success-text); background: var(--color-success-bg); border: 1px solid var(--color-success-border); border-radius: var(--radius-sm); padding: 0.5rem 0.7rem; font-size: 0.85rem; margin-bottom: 0.8rem; }
  .ua-code { display: block; font-family: monospace; font-size: 0.8rem; background: var(--color-surface-subtle); padding: 0.5rem 0.6rem; border-radius: var(--radius-sm); word-break: break-all; margin: 0.5rem 0; }
  .ua-drawer { position: fixed; inset: 0; z-index: 80; display: flex; justify-content: flex-end; }
  .ua-drawer__backdrop { position: absolute; inset: 0; background: rgba(16,24,40,0.35); }
  .ua-drawer__panel { position: relative; z-index: 1; width: min(30rem, 100%); height: 100%; background: var(--color-card); box-shadow: -10px 0 40px rgba(16,24,40,0.18); padding: 1.5rem 1.6rem; overflow-y: auto; }
  .ua-drawer__actions { display: flex; gap: 0.6rem; justify-content: flex-end; margin-top: 1.2rem; }
  .ua-field { display: flex; flex-direction: column; gap: 0.3rem; margin-bottom: 0.9rem; }
  .ua-field > span { font-size: 0.82rem; font-weight: 600; }
  .ua-field input, .ua-field select { padding: 0.5rem 0.6rem; border: 1px solid var(--color-rule); border-radius: var(--radius-sm); background: var(--color-card); color: var(--color-text); font: inherit; }
  .ua-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.9rem; }
  .ua-fieldset { border: 1px solid var(--color-rule); border-radius: var(--radius-sm); padding: 0.8rem; margin: 0 0 0.9rem; }
  .ua-fieldset legend { font-size: 0.82rem; font-weight: 600; padding: 0 0.4rem; }
  .ua-radio { display: block; margin-bottom: 0.4rem; font-size: 0.88rem; }
  .ua-proplist { margin-top: 0.5rem; padding-top: 0.5rem; border-top: 1px solid var(--color-rule); display: flex; flex-direction: column; gap: 0.35rem; }
  .ua-check { display: flex; align-items: center; gap: 0.5rem; font-size: 0.88rem; }

  .access-denied { max-width: 30rem; margin: 4rem auto; text-align: center; padding: 2rem; border: 1px solid var(--color-rule); border-radius: var(--radius); background: var(--color-card); box-shadow: var(--shadow-card); }
  .access-denied__title { font-family: var(--font-display); font-weight: 600; font-size: 1.7rem; color: var(--color-forest); margin: 0 0 0.75rem; }
  .access-denied__body { font-size: 1rem; color: var(--color-text); margin: 0 0 0.4rem; }
  .access-denied__hint { color: var(--color-text-muted); font-size: 0.9rem; margin: 0 0 1.5rem; }
  .access-denied__cta { display: inline-block; background: var(--color-primary-action); color: var(--color-primary-text); text-decoration: none; border-radius: var(--radius-sm); padding: 0.55rem 1.2rem; font-weight: 600; }
`;

/** Premium two-column sign-in page (LoginScreen). Emerald marketing panel +
 *  warm-ivory form column; collapses to a single spacious column on mobile.
 *  All colours come from AVOS design tokens; no browser-default controls. */
const AUTH_PAGE_CSS = `
  /* On the login route the shell's redundant top banner is hidden: the auth
     page is its own full-bleed public experience. The banner stays in the DOM
     (landmark) but is visually removed so branding is not duplicated. The
     authenticated console header is a different shell and is untouched. */
  [data-active-route="/login"] > header[role="banner"] { display: none; }
  [data-active-route="/login"] > main {
    max-width: none;
    margin: 0;
    padding: 0;
  }

  /* ---- Page grid ------------------------------------------------------- */
  .auth-page {
    display: grid;
    grid-template-columns: 45fr 30fr;
    min-height: 100vh;
    min-height: 100dvh;
    background: var(--color-card);
    color: var(--color-text-primary);
  }

  /* ---- Left: light-blue marketing panel -------------------------------- */
  .auth-aside {
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 2rem;
    padding: clamp(3.5rem, 4.5vw, 4.5rem);
    background: #F7F8FC;
    color: var(--color-text-primary);
    border-right: 1px solid var(--color-border);
    isolation: isolate;
  }
  /* Subtle hospitality "arch / doorway" motif — pure CSS, no image. Two large,
     very low-contrast arcs echoing the AVOS mark, plus a faint gold hairline. */
  .auth-aside__motif {
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background:
      radial-gradient(120% 90% at 50% 118%,
        rgba(29, 53, 169, 0.10) 0%,
        rgba(29, 53, 169, 0.04) 38%,
        rgba(29, 53, 169, 0) 62%),
      radial-gradient(80% 70% at 82% 8%,
        rgba(29, 53, 169, 0.05) 0%,
        rgba(29, 53, 169, 0) 60%);
  }
  .auth-aside__motif::before,
  .auth-aside__motif::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -46%;
    transform: translateX(-50%);
    border-radius: 50%;
    border: 1px solid rgba(29, 53, 169, 0.14);
    border-bottom-color: transparent;
  }
  .auth-aside__motif::before { width: 150%; aspect-ratio: 1; }
  .auth-aside__motif::after {
    width: 108%;
    aspect-ratio: 1;
    bottom: -34%;
    border-color: rgba(29, 53, 169, 0.08);
    border-bottom-color: transparent;
  }

  .auth-aside__top { display: flex; flex-direction: column; gap: 0.9rem; }
  .auth-aside__brand { display: inline-flex; align-items: center; gap: 0.6rem; }
  .auth-aside__brand-name {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.4rem;
    letter-spacing: 0.22em;
    color: var(--color-text-primary);
  }
  .auth-aside__endorse {
    font-family: var(--font-body);
    font-weight: 500;
    font-size: 0.68rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--color-gray-500);
  }
  .auth-aside__pitch { max-width: 29rem; }
  .auth-aside__headline {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: clamp(2rem, 2.9vw, 2.6rem);
    line-height: 1.1;
    letter-spacing: -0.005em;
    color: #171B26;
    margin: 0 0 1.375rem;
    max-width: 29rem;
  }
  .auth-aside__body {
    font-size: 0.9375rem;
    line-height: 1.6;
    color: var(--color-gray-500);
    margin: 0;
    max-width: 27rem;
  }
  .auth-aside__foot {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.10em;
    text-transform: uppercase;
    color: var(--color-gray-400);
    margin: 0;
  }

  /* ---- Right: form column ---------------------------------------------- */
  .auth-main {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: clamp(2rem, 4vw, 3.5rem) clamp(1.4rem, 3vw, 3rem);
    background: var(--color-card);
  }
  .auth-panel {
    width: 100%;
    max-width: 26.25rem;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    animation: auth-rise 0.26s ease both;
  }
  @keyframes auth-rise {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .auth-panel__brand {
    display: none; /* shown only on mobile where the aside is hidden */
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1.75rem;
  }
  .auth-panel__brand-name {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.2rem;
    letter-spacing: 0.2em;
    color: var(--color-forest);
  }

  .auth-panel__title {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: clamp(1.625rem, 2.2vw, 1.875rem);
    line-height: 1.2;
    letter-spacing: -0.01em;
    color: #171B26;
    margin: 0 0 0.4375rem;
  }
  .auth-panel__prompt {
    color: var(--color-gray-500);
    font-size: 0.875rem;
    line-height: 1.43;
    margin: 0 0 1.875rem;
  }

  /* Generic login error alert, consistent with the AVOS design system — soft,
     not an aggressive bright-red fill. */
  .auth-panel__alert { margin: 0 0 1.25rem; }
  .auth-panel__alert .user-message {
    border: 1px solid var(--color-error-border);
    background: var(--color-error-bg);
    color: var(--color-error-text);
    border-radius: var(--radius-sm);
    padding: 0.7rem 0.85rem;
  }
  .auth-panel__alert .user-message__signal {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 700;
    font-size: 0.78rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin: 0 0 0.2rem;
  }
  .auth-panel__alert .user-message__what { font-weight: 600; margin: 0.1rem 0; }
  .auth-panel__alert .user-message__why,
  .auth-panel__alert .user-message__next {
    font-size: 0.86rem;
    color: var(--color-text);
    margin: 0.1rem 0 0;
  }
  .auth-panel__alert .user-message__technical { display: none; }

  /* ---- Fields ---------------------------------------------------------- */
  .auth-form { display: flex; flex-direction: column; gap: 1.25rem; }
  .auth-page .auth-field {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    margin: 0;
  }
  .auth-page .auth-field > label {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: var(--color-text-secondary);
  }
  .auth-page .auth-field input {
    width: 100%;
    height: 44px;
    padding: 0 0.875rem;
    border: 1px solid var(--color-border-strong);
    border-radius: 8px;
    background: var(--color-card);
    color: var(--color-text-primary);
    font: inherit;
    font-size: 0.95rem;
    transition: border-color 0.16s ease, box-shadow 0.16s ease;
  }
  .auth-page .auth-field input::placeholder { color: var(--placeholder); }
  .auth-page .auth-field input:hover:not(:disabled) { border-color: var(--color-gray-400); }
  .auth-page .auth-field input:focus {
    outline: none;
    border-color: var(--color-primary-action);
    box-shadow: var(--focus-ring);
  }
  .auth-page .auth-field input:disabled {
    background: var(--color-gray-100);
    color: var(--placeholder);
    cursor: not-allowed;
  }
  /* Validation error state: soft red border + ring (no loud background). */
  .auth-page .auth-field input[aria-invalid="true"] {
    border-color: #F04438;
  }
  .auth-page .auth-field input[aria-invalid="true"]:focus {
    box-shadow: 0 0 0 3px rgba(240, 68, 56, 0.12);
  }

  /* Password field with the integrated reveal control inside the input. */
  .auth-field__control { position: relative; }
  .auth-field__control input { padding-right: 3rem; }
  .auth-field__reveal {
    position: absolute;
    top: 50%;
    right: 0.55rem;
    transform: translateY(-50%);
    width: 2.1rem;
    height: 2.1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--color-gray-500);
    cursor: pointer;
    transition: color 0.14s ease, background 0.14s ease;
  }
  .auth-field__reveal:hover:not(:disabled) {
    color: var(--color-gray-700);
    background: var(--color-gray-100);
  }
  .auth-field__reveal:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
  .auth-field__reveal:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ---- Forgot password link (subtle green text link, not browser blue) -- */
  .auth-form__meta { display: flex; justify-content: flex-end; margin-top: -0.3rem; }
  .auth-form__meta a {
    color: var(--color-primary-action);
    font-size: 0.75rem;
    font-weight: 600;
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.14s ease, color 0.14s ease;
  }
  .auth-form__meta a:hover { border-bottom-color: var(--color-primary-action); color: var(--color-primary-800); }

  /* ---- Primary sign-in button ------------------------------------------ */
  .auth-form__submit {
    width: 100%;
    height: 44px;
    margin-top: 0.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--color-primary-action);
    border-radius: 8px;
    background: var(--color-primary-action);
    color: #FFFFFF;
    font: inherit;
    font-weight: 600;
    font-size: 0.875rem;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background 0.16s ease, box-shadow 0.16s ease, transform 0.08s ease;
  }
  .auth-form__submit:hover:not(:disabled) {
    background: var(--color-primary-hover);
    border-color: var(--color-primary-hover);
    box-shadow: 0 6px 18px rgba(16, 24, 40, 0.12);
  }
  .auth-form__submit:active:not(:disabled) { transform: translateY(1px); background: var(--color-primary-pressed); border-color: var(--color-primary-pressed); }
  .auth-form__submit:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
  .auth-form__submit:disabled { opacity: 0.7; cursor: default; }
  .auth-form__submit-busy { display: inline-flex; align-items: center; gap: 0.5rem; }
  .auth-spinner { animation: auth-spin 0.7s linear infinite; }
  @keyframes auth-spin { to { transform: rotate(360deg); } }

  /* ---- Responsive: single column below the tablet breakpoint ----------- */
  @media (min-width: 861px) and (max-width: 1100px) {
    .auth-page { grid-template-columns: 44fr 56fr; }
    .auth-aside { padding: clamp(2.5rem, 4vw, 3.25rem); }
    .auth-panel { max-width: 24rem; }
  }
  /* 70/30 split can make the right column tight on ~1280-1440px; keep the form
     comfortably inside its 30% panel. */
  @media (min-width: 1101px) and (max-width: 1440px) {
    .auth-main { padding-left: clamp(1.25rem, 2.2vw, 2rem); padding-right: clamp(1.25rem, 2.2vw, 2rem); }
    .auth-panel { max-width: 23rem; padding: 1.5rem; }
  }
  @media (max-width: 860px) {
    .auth-page { grid-template-columns: 1fr; }
    .auth-aside { display: none; }
    .auth-main {
      align-items: flex-start;
      padding: clamp(2.5rem, 9vw, 4rem) 22px 2.5rem;
    }
    .auth-panel { max-width: 420px; margin: 0 auto; padding: 1.5rem; }
    .auth-panel__brand { display: inline-flex; }
  }

  @media (max-width: 400px) {
    .auth-main { padding-left: 20px; padding-right: 20px; }
  }

  /* Respect reduced-motion: the base rule already disables transitions, but be
     explicit for the entrance + spinner. */
  @media (prefers-reduced-motion: reduce) {
    .auth-panel { animation: none; }
    .auth-spinner { animation: none; }
  }
`;

/* ========================================================================= *
 *  Pass B: room/rate drawer — the tabbed Add-room / Edit-room drawer and the
 *  full Channex-parity rate-plan field set. Reuses existing tokens + the
 *  .prop-drawer / .console-field rhythm from Pass A; no Channex blue, no
 *  heavy card-per-section. Responsive: one column on mobile.
 * ========================================================================= */

export const SHELL_STYLESHEET =
  BASE_CSS +
  CONSOLE_SHELL_CSS +
  MODULES_CSS +
  AGENTS_CSS +
  DASHBOARD_CSS +
  CHANNELS_CSS +
  CHANNELS_V2_CSS +
  RECON2_CSS +
  PROPERTY_DRAWER_CSS +
  ONBOARDING_CSS +
  ACCOUNTS_CSS +
  REVENUE_CSS +
  PLATFORM_ADMIN_CSS +
  AUTH_ACCESS_CSS +
  AUTH_PAGE_CSS +
  USERS_ACCESS_CSS;

/**
 * Wrap {@link SHELL_STYLESHEET} in a `<style data-avos-styles>` element for SSR
 * inlining in the document `<head>`. Kept as a tiny pure helper so the renderer
 * and tests share one source of truth for the tag shape.
 */
export function shellStyleTag(): string {
  return `<style data-avos-styles>${SHELL_STYLESHEET}</style>`;
}
